import { useState, useEffect, useRef } from "react";

// Load native TTS only when running in Capacitor
let TextToSpeech = null;
try { TextToSpeech = require("@capacitor-community/text-to-speech").TextToSpeech; } catch(e) {}
// Fallback: Capacitor exposes natively-registered plugins on window.Capacitor.Plugins,
// which works even when the bundler (e.g. Vite) doesn't support require()
function getTTS() {
  if (TextToSpeech) return TextToSpeech;
  try {
    if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()
        && window.Capacitor.Plugins && window.Capacitor.Plugins.TextToSpeech) {
      return window.Capacitor.Plugins.TextToSpeech;
    }
  } catch(e) {}
  return null;
}

// ─── DATA ───────────────────────────────────────────────────────────────────

const PROGRAMS = [
  {
    id: "beginner", name: "Foundation", tag: "Start here",
    description: "Build awareness and basic strength",
    color: "#c084fc", glow: "#7c3aed",
    exercises: [
      { id: "slow", name: "Slow Holds", sets: 1, reps: 5, squeezeSecs: 3, holdSecs: 5, restSecs: 5, setRestSecs: 0,
        instruction: "Slowly lift and squeeze your pelvic floor as if stopping the flow of urine. Hold, then slowly release fully.",
        tip: "Focus on a full release — letting go completely is just as important as squeezing." },
      { id: "quick", name: "Quick Flicks", sets: 1, reps: 10, squeezeSecs: 3, holdSecs: 1, restSecs: 2, setRestSecs: 0,
        instruction: "Quickly squeeze and release your pelvic floor. These fast-twitch fibres help prevent leaks when you cough or sneeze.",
        tip: "Keep your buttocks and thighs relaxed — only your pelvic floor should be working." }
    ]
  },
  {
    id: "prolapse", name: "Core & Pelvic Tone", tag: "Strength",
    description: "Gentle strength for your core and pelvic floor",
    dailyDose: "Aim for 3 sessions/day · ~10 slow holds + fast contractions each session · ~40–48 contractions total per day",
    color: "#f472b6", glow: "#db2777",
    exercises: [
      { id: "slow-deep", name: "Deep Slow Holds", sets: 1, reps: 10, squeezeSecs: 3, holdSecs: 8, restSecs: 8, setRestSecs: 0,
        instruction: "Imagine a lift rising through 3 floors. Gently lift to floor 1, then 2, then 3. Hold at the top, then slowly descend floor by floor.",
        tip: "Never bear down or push out at the end. Always finish with a gentle lift." },
      { id: "endurance", name: "Quick Contractions", sets: 1, reps: 10, squeezeSecs: 3, holdSecs: 1, restSecs: 1, setRestSecs: 0,
        instruction: "After your slow holds, finish each session with a set of quick contractions. Squeeze firmly and release rapidly — these target the fast-twitch fibres.",
        tip: "Quality over quantity — 30 good contractions beat 100 rushed ones. This is the minimum effective dose per POGP guidance." }
    ]
  },
  {
    id: "postnatal", name: "Postpartum Wellness", tag: "After birth",
    description: "Gentle movement from 6 weeks after birth",
    color: "#c084fc", glow: "#7c3aed",
    exercises: [
      { id: "awareness", name: "Awareness Squeeze", sets: 1, reps: 5, squeezeSecs: 3, holdSecs: 3, restSecs: 8, setRestSecs: 0,
        instruction: "Gently reconnect with your pelvic floor. Even a small squeeze counts — nerves may still be recovering after birth.",
        tip: "It may feel like nothing is happening at first. That's normal. Keep trying gently — sensation returns gradually." },
      { id: "progressive", name: "Progressive Hold", sets: 1, reps: 6, squeezeSecs: 3, holdSecs: 6, restSecs: 6, setRestSecs: 0,
        instruction: "Build gradually. Squeeze, hold, and release slowly. Always do these before returning to running, jumping, or heavy lifting.",
        tip: "Build up gradually — take your time before returning to high-impact exercise, and listen to your body." }
    ]
  },
  {
    id: "incontinence", name: "Confidence & Control", tag: "Strength",
    description: "Build strength and endurance for everyday confidence",
    dailyDose: "3 sessions/day · 8–10 slow holds + 8–10 quick flicks each · ~30–40 contractions total per day",
    exercises: [
      { id: "slow-holds", name: "Slow Holds", sets: 1, reps: 10, squeezeSecs: 3, holdSecs: 10, restSecs: 10, setRestSecs: 0,
        instruction: "Slowly lift and squeeze your pelvic floor and hold for 10 seconds. Release fully between each rep. Do 8–10 reps per session, 3 times a day.",
        tip: "Full release between contractions is essential — a muscle that never relaxes can't contract effectively." },
      { id: "quick-flicks", name: "Quick Flicks", sets: 1, reps: 10, squeezeSecs: 3, holdSecs: 1, restSecs: 1, setRestSecs: 0,
        instruction: "Squeeze and release rapidly. These target the fast-twitch fibres that fire automatically during a cough, sneeze, laugh or lift. A key part of everyday pelvic floor fitness.",
        tip: "Think of these as training your reflex — the goal is speed, not strength. 8–10 quick flicks per session." },
      { id: "knack", name: "The Knack", sets: 1, reps: 10, squeezeSecs: 3, holdSecs: 2, restSecs: 3, setRestSecs: 0,
        instruction: "Squeeze firmly just before and during a cough, sneeze, laugh or lift. This is a great everyday skill — contract before the pressure hits.",
        tip: "Eventually this becomes automatic. Practice deliberately every time you cough or sneeze until it's a reflex." }
    ],
    color: "#60a5fa", glow: "#2563eb", sessions: 12,
  }
];

// ─── PROGRESSION LEVELS ──────────────────────────────────────────────────────
// Levels progress by increasing hold time and matching rest, following widely
// recognised pelvic floor training principles (gradual increase in hold
// duration as strength builds; rest at least as long as the hold; breathing
// maintained throughout). Basic is free; Intermediate and Advanced are Pro.
const LEVELS = [
  {
    id: "basic", name: "Basic", emoji: "🌱", free: true,
    blurb: "Shorter holds to build awareness and a foundation of strength.",
    holdSecs: 5, restSecs: 5, slowReps: 6, quickReps: 6,
    note: "Hold ~5 seconds, rest the same. A gentle, comfortable starting point.",
  },
  {
    id: "intermediate", name: "Intermediate", emoji: "💪", free: false,
    blurb: "Longer holds and more repetitions as your strength grows.",
    holdSecs: 8, restSecs: 8, slowReps: 8, quickReps: 10,
    note: "Hold ~8 seconds, rest the same. A natural step up once Basic feels easy.",
  },
  {
    id: "advanced", name: "Advanced", emoji: "🔥", free: false,
    blurb: "Full-length holds and extra quick contractions for endurance.",
    holdSecs: 10, restSecs: 10, slowReps: 10, quickReps: 14,
    note: "Hold ~10 seconds, rest the same, with more quick contractions. The endurance endpoint.",
  },
];

// Build a level-adjusted copy of a program. Slow/hold exercises take the level's
// hold/rest/reps; quick-flick exercises take the level's quick rep count.
function applyLevel(program, levelId) {
  const level = LEVELS.find(l => l.id === levelId) || LEVELS[0];
  const isQuick = (ex) => ex.holdSecs <= 2; // quick flicks / knack have very short holds
  return {
    ...program,
    levelId: level.id,
    levelName: level.name,
    displayName: `${program.name} · ${level.name}`,
    exercises: program.exercises.map(ex => {
      if (isQuick(ex)) {
        return { ...ex, reps: level.quickReps };
      }
      return { ...ex, holdSecs: level.holdSecs, restSecs: level.restSecs, reps: level.slowReps };
    }),
  };
}

// ─── THEME & MUSIC DATA ──────────────────────────────────────────────────────

const THEME_KEY = "coredoc-theme-v1";
const MUSIC_KEY = "coredoc-music-v1";
const CUSTOM_BG_KEY = "coredoc-custom-bg-v1";

const FREE_THEMES = [
  { id: "purple", name: "Midnight", bg: "#150d28", orb1: "#7c3aed", orb2: "#db2777", orb3: "#2563eb", accent: "#c084fc", free: true },
  { id: "blue", name: "Deep Ocean", bg: "#04122a", orb1: "#2563eb", orb2: "#06b6d4", orb3: "#0ea5e9", accent: "#38bdf8", free: true },
  { id: "green", name: "Forest", bg: "#06180f", orb1: "#059669", orb2: "#84cc16", orb3: "#10b981", accent: "#4ade80", free: true },
];

const PRO_THEMES = [
  { id: "daylight", name: "Daylight", bg: "#f6f3ff", orb1: "#c4b5fd", orb2: "#f0abfc", orb3: "#a5b4fc", accent: "#7c3aed", free: false, light: true },
  { id: "peach", name: "Peach Cream", bg: "#fff6ef", orb1: "#fdba74", orb2: "#fda4af", orb3: "#fde68a", accent: "#ea580c", free: false, light: true },
  { id: "sky", name: "Sky", bg: "#eef7ff", orb1: "#7dd3fc", orb2: "#a5b4fc", orb3: "#99f6e4", accent: "#0284c7", free: false, light: true },
  { id: "sakura", name: "Sakura", bg: "#fff1f6", orb1: "#f9a8d4", orb2: "#fbcfe8", orb3: "#e9d5ff", accent: "#db2777", free: false, light: true },
  { id: "rose", name: "Rose Gold", bg: "#1f0a12", orb1: "#e11d48", orb2: "#f59e0b", orb3: "#fbbf24", accent: "#fda4af", free: false },
  { id: "sunrise", name: "Sunrise", bg: "#1c0e05", orb1: "#ea580c", orb2: "#fbbf24", orb3: "#ef4444", accent: "#fb923c", free: false },
  { id: "galaxy", name: "Galaxy", bg: "#030014", orb1: "#6366f1", orb2: "#06b6d4", orb3: "#312e81", accent: "#67e8f9", free: false },
  { id: "mint", name: "Mint", bg: "#041712", orb1: "#10b981", orb2: "#34d399", orb3: "#06b6d4", accent: "#6ee7b7", free: false },
];

const ALL_THEMES = [...FREE_THEMES, ...PRO_THEMES];

const MUSIC_TRACKS = [
  { id: "none", name: "No music", icon: "🔇", free: true },
  { id: "calm", name: "Calm Piano", icon: "🎹", free: true, freqs: [261, 329, 392, 440, 523] },
  { id: "nature", name: "Nature", icon: "🌿", free: false, freqs: [110, 165, 220, 277, 330] },
  { id: "lofi", name: "Lo-fi Beats", icon: "🎵", free: false, freqs: [196, 247, 294, 370, 440] },
  { id: "ocean", name: "Ocean Waves", icon: "🌊", free: false, freqs: [80, 120, 160, 200, 240] },
  { id: "rain", name: "Gentle Rain", icon: "🌧️", free: false, freqs: [300, 400, 500, 600, 700] },
];

function loadTheme() {
  try { const d = localStorage.getItem(THEME_KEY); return d ? JSON.parse(d) : FREE_THEMES[0]; }
  catch { return FREE_THEMES[0]; }
}
function saveTheme(t) { try { localStorage.setItem(THEME_KEY, JSON.stringify(t)); } catch {} }
function loadMusic() {
  try { const d = localStorage.getItem(MUSIC_KEY); return d || "none"; }
  catch { return "none"; }
}
function saveMusic(m) { try { localStorage.setItem(MUSIC_KEY, m); } catch {} }
function loadCustomBg() {
  try { return localStorage.getItem(CUSTOM_BG_KEY) || null; }
  catch { return null; }
}
function saveCustomBg(url) { try { if (url) localStorage.setItem(CUSTOM_BG_KEY, url); else localStorage.removeItem(CUSTOM_BG_KEY); } catch {} }

// ─── BACKGROUND MUSIC ENGINE ──────────────────────────────────────────────────

function createMusicEngine() {
  let ctx = null; let nodes = []; let playing = false;
  function getCtx() {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  }
  function stop() {
    nodes.forEach(n => { try { n.stop(); } catch {} });
    nodes = []; playing = false;
  }
  function playAmbient(trackId) {
    stop();
    if (trackId === "none") return;
    const track = MUSIC_TRACKS.find(t => t.id === trackId);
    if (!track?.freqs) return;
    try {
      const c = getCtx();
      playing = true;
      track.freqs.forEach((freq, i) => {
        const osc = c.createOscillator();
        const gain = c.createGain();
        const filter = c.createBiquadFilter();
        osc.connect(filter); filter.connect(gain); gain.connect(c.destination);
        osc.type = trackId === "lofi" ? "sawtooth" : trackId === "nature" ? "triangle" : "sine";
        osc.frequency.value = freq;
        filter.type = "lowpass"; filter.frequency.value = 800;
        gain.gain.value = 0;
        gain.gain.linearRampToValueAtTime(0.025 / track.freqs.length, c.currentTime + 2);
        // Gentle LFO for movement
        const lfo = c.createOscillator();
        const lfoGain = c.createGain();
        lfo.connect(lfoGain); lfoGain.connect(gain.gain);
        lfo.frequency.value = 0.1 + i * 0.03;
        lfoGain.gain.value = 0.01;
        lfo.start(); osc.start();
        nodes.push(osc, lfo);
      });
    } catch {}
  }
  return { playAmbient, stop, isPlaying: () => playing };
}
const musicEngine = createMusicEngine();

// ─── PERSONALISE SCREEN ───────────────────────────────────────────────────────

function PersonaliseScreen({ onBack, pro, onUpgrade, currentTheme, onThemeChange, currentMusic, onMusicChange, customBg, onCustomBg, voiceEnabled, onVoiceToggle, voiceStyle, onVoiceStyleChange }) {
  const [activeTab, setActiveTab] = useState("themes");
  const fileInputRef = useRef(null);

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { saveCustomBg(ev.target.result); onCustomBg(ev.target.result); };
    reader.readAsDataURL(file);
  }

  function removeCustomBg() { saveCustomBg(null); onCustomBg(null); }

  return (
    <div className={currentTheme.light ? "light-ui" : ""} style={{ "--bg": currentTheme.bg, "--accent": currentTheme.accent, "--orb1": currentTheme.orb1, "--orb2": currentTheme.orb2, position: "relative", minHeight: "100vh", color: "#f5f0ff", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", maxWidth: 420, margin: "0 auto", padding: "32px 24px 120px", overflowY: "auto",
      background: customBg ? `linear-gradient(#000000bb, #000000bb), url(${customBg}) center/cover` : currentTheme.bg
    }}>
      <OrbBackground color1={currentTheme.orb1} color2={currentTheme.orb2} color3={currentTheme.orb3} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 24, display: "flex", alignItems: "center", gap: 6 }}>← Back</button>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: currentTheme.accent, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>Make it yours</div>
          <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>Personalise</h2>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", background: "#ffffff11", borderRadius: 12, padding: 4, marginBottom: 24, gap: 4 }}>
          {[{ id: "themes", label: "🎨" }, { id: "music", label: "🎵" }, { id: "voice", label: "🗣️" }, { id: "photo", label: "🖼️" }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, background: activeTab === tab.id ? `linear-gradient(135deg, ${currentTheme.orb1}88, ${currentTheme.orb2}66)` : "transparent",
              border: "none", borderRadius: 8, padding: "8px 4px", color: activeTab === tab.id ? "#fff" : "#6b7280",
              fontSize: 16, fontWeight: 600, cursor: "pointer", transition: "all 0.2s ease"
            }}>{tab.label}</button>
          ))}
        </div>

        {/* THEMES TAB */}
        {activeTab === "themes" && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Free themes</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
              {FREE_THEMES.map(theme => (
                <button key={theme.id} onClick={() => { onThemeChange(theme); saveTheme(theme); }} style={{
                  background: `linear-gradient(135deg, ${theme.orb1}, ${theme.orb2})`,
                  border: currentTheme.id === theme.id ? `2px solid ${theme.accent}` : "2px solid transparent",
                  borderRadius: 14, padding: "16px 8px", cursor: "pointer", textAlign: "center",
                  boxShadow: currentTheme.id === theme.id ? `0 0 16px ${theme.accent}66` : "none"
                }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: theme.accent, margin: "0 auto 6px", boxShadow: `0 0 8px ${theme.accent}` }} />
                  <div style={{ fontSize: 11, color: "#f5f0ff", fontWeight: 600 }}>{theme.name}</div>
                  {currentTheme.id === theme.id && <div style={{ fontSize: 10, color: theme.accent, marginTop: 2 }}>✓ Active</div>}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>Pro themes</div>
              {!pro && <div style={{ background: "#c084fc22", border: "1px solid #c084fc44", borderRadius: 8, padding: "2px 8px", fontSize: 10, color: "#c084fc", fontWeight: 700 }}>🔒 Pro</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
              {PRO_THEMES.map(theme => (
                <button key={theme.id} onClick={() => { if (!pro) { onUpgrade(); return; } onThemeChange(theme); saveTheme(theme); }} style={{
                  background: `linear-gradient(135deg, ${theme.orb1}, ${theme.orb2})`,
                  border: currentTheme.id === theme.id ? `2px solid ${theme.accent}` : "2px solid transparent",
                  borderRadius: 12, padding: "12px 6px", cursor: "pointer", textAlign: "center", position: "relative",
                  opacity: pro ? 1 : 0.7
                }}>
                  {!pro && <div style={{ position: "absolute", top: 4, right: 4, fontSize: 10 }}>🔒</div>}
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: theme.accent, margin: "0 auto 4px", boxShadow: `0 0 6px ${theme.accent}` }} />
                  <div style={{ fontSize: 9, color: "#f5f0ff", fontWeight: 600, lineHeight: 1.2 }}>{theme.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MUSIC TAB */}
        {activeTab === "music" && (
          <div>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20, lineHeight: 1.5 }}>Play gentle background music during your exercises to help you relax and focus.</p>
            {MUSIC_TRACKS.map(track => (
              <button key={track.id} onClick={() => {
                if (!track.free && !pro) { onUpgrade(); return; }
                onMusicChange(track.id); saveMusic(track.id);
                if (track.id === "none") musicEngine.stop();
                else musicEngine.playAmbient(track.id);
              }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                background: currentMusic === track.id ? `linear-gradient(135deg, ${currentTheme.orb1}44, ${currentTheme.orb2}22)` : "linear-gradient(135deg, #1a1030ee, #110820ee)",
                border: `1px solid ${currentMusic === track.id ? currentTheme.accent + "88" : "#3d2a5e44"}`,
                borderRadius: 14, padding: "14px 18px", marginBottom: 10, cursor: "pointer", textAlign: "left",
                opacity: !track.free && !pro ? 0.7 : 1
              }}>
                <div style={{ fontSize: 24 }}>{track.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#f5f0ff" }}>{track.name}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{track.free ? "Free" : "Pro"}</div>
                </div>
                {!track.free && !pro && <span style={{ fontSize: 14 }}>🔒</span>}
                {currentMusic === track.id && <div style={{ width: 10, height: 10, borderRadius: "50%", background: currentTheme.accent, boxShadow: `0 0 8px ${currentTheme.accent}` }} />}
              </button>
            ))}
          </div>
        )}

        {/* VOICE TAB */}
        {activeTab === "voice" && (
          <div>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20, lineHeight: 1.5 }}>
              Voice guidance talks you through every squeeze, hold, and release — so you don't need to look at the screen.
            </p>

            {/* Voice on/off toggle */}
            <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 16, padding: "16px 18px", marginBottom: 14, border: "1px solid #3d2a5e55", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f5f0ff" }}>Voice guidance</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Free · Contract, Hold, Release, countdown</div>
              </div>
              <div onClick={onVoiceToggle} style={{
                width: 48, height: 26, borderRadius: 13, cursor: "pointer", position: "relative",
                background: voiceEnabled ? `linear-gradient(135deg, ${currentTheme.orb1}, ${currentTheme.accent})` : "#2a1a3e",
                border: `1px solid ${voiceEnabled ? currentTheme.accent : "#3d2a5e"}`,
                transition: "background 0.2s ease"
              }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: voiceEnabled ? 24 : 2, transition: "left 0.2s ease", boxShadow: "0 1px 4px #00000044" }} />
              </div>
            </div>

            {/* Test voice button */}
            <button onClick={() => speak("Squeeze. Hold. And release. Well done!", voiceStyle)} style={{
              width: "100%", background: `${currentTheme.accent}18`, border: `1px solid ${currentTheme.accent}44`,
              borderRadius: 12, padding: "12px", color: currentTheme.accent,
              fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 20
            }}>🗣️ Test voice now</button>

            {/* Voice style selector */}
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
              Voice style
            </div>
            {VOICE_STYLES.map(v => (
              <button key={v.id} onClick={() => {
                if (!v.free && !pro) { onUpgrade(); return; }
                onVoiceStyleChange(v.id);
                saveVoiceStyle(v.id);
                speak(`This is the ${v.name} voice. How does this sound?`, v.id);
              }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                background: voiceStyle === v.id ? `linear-gradient(135deg, ${currentTheme.orb1}44, ${currentTheme.orb2}22)` : "linear-gradient(135deg, #1a1030ee, #110820ee)",
                border: `1px solid ${voiceStyle === v.id ? currentTheme.accent + "88" : "#3d2a5e44"}`,
                borderRadius: 14, padding: "14px 18px", marginBottom: 10,
                cursor: "pointer", textAlign: "left", opacity: !v.free && !pro ? 0.7 : 1
              }}>
                <div style={{ fontSize: 24 }}>🗣️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#f5f0ff" }}>{v.name}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{v.free ? "Free" : "Pro"} · {v.lang} · Pitch {v.pitch} · Rate {v.rate}x</div>
                </div>
                {!v.free && !pro && <span style={{ fontSize: 14 }}>🔒</span>}
                {voiceStyle === v.id && <div style={{ width: 10, height: 10, borderRadius: "50%", background: currentTheme.accent, boxShadow: `0 0 8px ${currentTheme.accent}` }} />}
              </button>
            ))}
          </div>
        )}

        {/* PHOTO TAB */}
        {activeTab === "photo" && (
          <div>
            {!pro ? (
              <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: 20, padding: "28px 20px", textAlign: "center", border: "1px solid #c084fc33" }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>🖼️</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#f5f0ff", marginBottom: 8 }}>Custom Background Photo</div>
                <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20, lineHeight: 1.6 }}>Use your own photo as your app background. A calming nature scene, your favourite place, or anything that helps you relax during exercises.</p>
                <button onClick={onUpgrade} style={{
                  width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)", color: "#fff",
                  border: "none", borderRadius: 12, padding: "14px", fontSize: 14, fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 6px 20px #7c3aed44"
                }}>🔓 Unlock — £1.99/month</button>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20, lineHeight: 1.5 }}>Choose a photo from your camera roll to use as your background. It will appear subtly behind the app.</p>
                {customBg && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ borderRadius: 16, overflow: "hidden", height: 160, background: `url(${customBg}) center/cover`, marginBottom: 10, border: "1px solid #3d2a5e" }} />
                    <button onClick={removeCustomBg} style={{ background: "#ef444422", border: "1px solid #ef444444", borderRadius: 10, padding: "8px 16px", color: "#ef4444", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Remove photo</button>
                  </div>
                )}
                <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileUpload} style={{ display: "none" }} />
                <button onClick={() => fileInputRef.current?.click()} style={{
                  width: "100%", background: `linear-gradient(135deg, ${currentTheme.orb1}, ${currentTheme.orb2})`,
                  color: "#fff", border: "none", borderRadius: 14, padding: "16px",
                  fontSize: 15, fontWeight: 700, cursor: "pointer",
                  boxShadow: `0 6px 20px ${currentTheme.orb1}44`
                }}>📷 Choose photo from camera roll</button>
                <p style={{ fontSize: 11, color: "#4b5563", marginTop: 10, textAlign: "center" }}>Photo is stored on your device only and never uploaded.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}



const BLADDER_KEY = "coredoc-bladder-v1";
const VOIDING_ALARM_KEY = "coredoc-voiding-alarm";

function loadBladderDiary() {
  try { const d = localStorage.getItem(BLADDER_KEY); return d ? JSON.parse(d) : { entries: [] }; }
  catch { return { entries: [] }; }
}
function saveBladderDiary(d) { try { localStorage.setItem(BLADDER_KEY, JSON.stringify(d)); } catch {} }

const DRINK_TYPES = [
  { id: "water", label: "Water", icon: "💧", color: "#60a5fa" },
  { id: "tea", label: "Tea/Coffee", icon: "☕", color: "#c084fc" },
  { id: "juice", label: "Juice", icon: "🧃", color: "#f59e0b" },
  { id: "fizzy", label: "Fizzy drink", icon: "🥤", color: "#f472b6" },
  { id: "alcohol", label: "Alcohol", icon: "🍷", color: "#ef4444" },
  { id: "milk", label: "Milk", icon: "🥛", color: "#e2e8f0" },
  { id: "other", label: "Other", icon: "🫗", color: "#6b7280" },
];

const URGENCY_LEVELS = [
  { value: 1, label: "Normal", color: "#c084fc" },
  { value: 2, label: "Mild urge", color: "#f59e0b" },
  { value: 3, label: "Strong urge", color: "#f97316" },
  { value: 4, label: "Urgent/leak", color: "#ef4444" },
];

// ─── TIMED VOIDING SCREEN (FREE) ─────────────────────────────────────────────

function TimedVoidingScreen({ onBack, pro, onUpgrade }) {
  const [intervalHours, setIntervalHours] = useState(() => {
    try { return parseInt(localStorage.getItem(VOIDING_ALARM_KEY) || "2"); } catch { return 2; }
  });
  // The target time is PERSISTED so the countdown survives the app being
  // closed: on relaunch we restore it and keep counting toward the same
  // moment. A native notification fires at the target even if the app stays
  // closed (id 2001).
  const VOIDING_NEXT_KEY = "coredoc-voiding-next";
  // Alarm ringtones — each maps to an Android notification channel (channel
  // sound is fixed at creation, so one channel per tone). WAV files live in
  // android/app/src/main/res/raw/ (native alarm) and public/sounds/ (preview).
  const ALARM_SOUNDS = [
    { id: "chime", label: "🎐 Chime", file: "coredoc_chime" },
    { id: "bell",  label: "🔔 Bell",  file: "coredoc_bell" },
    { id: "beeps", label: "⏰ Beeps", file: "coredoc_beeps" },
    { id: "silent", label: "🔕 Silent", file: null },
  ];
  const VOIDING_SOUND_KEY = "coredoc-voiding-sound";
  const [alarmSound, setAlarmSound] = useState(() => {
    try { return localStorage.getItem(VOIDING_SOUND_KEY) || "chime"; } catch { return "chime"; }
  });

  async function ensureAlarmChannels() {
    const LN = getLocalNotifications();
    if (!LN || !LN.createChannel) return;
    for (const s of ALARM_SOUNDS) {
      try {
        if (s.file) {
          await LN.createChannel({
            id: "voiding-" + s.id, name: "Timed voiding — " + s.label.replace(/^\S+\s/, ""),
            description: "Timed voiding alarm", importance: 5, visibility: 1,
            sound: s.file + ".wav", vibration: true
          });
        } else {
          // Silent: notification appears in the tray, no sound, no vibration
          await LN.createChannel({
            id: "voiding-" + s.id, name: "Timed voiding — Silent",
            description: "Timed voiding silent notification", importance: 3, visibility: 1,
            vibration: false
          });
        }
      } catch(e) {}
    }
  }
  useEffect(() => { ensureAlarmChannels(); }, []);

  function pickSound(id) {
    setAlarmSound(id);
    try { localStorage.setItem(VOIDING_SOUND_KEY, id); } catch {}
    const s = ALARM_SOUNDS.find(x => x.id === id);
    if (s && s.file) { try { new Audio("sounds/" + s.file + ".wav").play().catch(() => {}); } catch(e) {} }
    // If a timer is running, reschedule its alarm on the new sound's channel
    if (active && nextVoid) scheduleVoidNotification(nextVoid, id);
  }

  const [nextVoid, setNextVoid] = useState(() => {
    try {
      const t = parseInt(localStorage.getItem(VOIDING_NEXT_KEY) || "0");
      return t > Date.now() ? new Date(t) : null;
    } catch { return null; }
  });
  const [active, setActive] = useState(() => {
    try { return parseInt(localStorage.getItem(VOIDING_NEXT_KEY) || "0") > Date.now(); } catch { return false; }
  });
  const [timeLeft, setTimeLeft] = useState(null);
  const intervalRef = useRef(null);

  async function scheduleVoidNotification(when, soundId) {
    const LN = getLocalNotifications();
    if (!LN) return;
    const s = ALARM_SOUNDS.find(x => x.id === (soundId || alarmSound)) || ALARM_SOUNDS[0];
    try {
      await LN.cancel({ notifications: [{ id: 2001 }] });
      const notif = {
        id: 2001, title: s.id === "silent" ? "CoreDoc" : "CoreDoc ⏰",
        body: "Time for your scheduled bathroom visit.",
        schedule: { at: new Date(when), allowWhileIdle: true },
        channelId: "voiding-" + s.id, smallIcon: "ic_stat_icon"
      };
      if (s.file) notif.sound = s.file + ".wav";
      await LN.schedule({ notifications: [notif] });
    } catch(e) {}
  }
  async function cancelVoidNotification() {
    const LN = getLocalNotifications();
    if (!LN) return;
    try { await LN.cancel({ notifications: [{ id: 2001 }] }); } catch(e) {}
  }

  function startTimer() {
    const next = new Date(Date.now() + intervalHours * 60 * 60 * 1000);
    try { localStorage.setItem(VOIDING_NEXT_KEY, String(next.getTime())); } catch {}
    setNextVoid(next);
    setActive(true);
    scheduleVoidNotification(next);
  }
  function stopTimer() {
    try { localStorage.removeItem(VOIDING_NEXT_KEY); } catch {}
    setNextVoid(null);
    setActive(false);
    cancelVoidNotification();
  }

  useEffect(() => {
    if (!active || !nextVoid) { clearInterval(intervalRef.current); setTimeLeft(null); return; }
    const tick = () => {
      const remaining = Math.max(0, nextVoid - Date.now());
      setTimeLeft(remaining);
      if (remaining === 0) {
        clearInterval(intervalRef.current);
        try { localStorage.removeItem(VOIDING_NEXT_KEY); } catch {}
        setActive(false); setNextVoid(null);
      }
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [active, nextVoid]);

  function formatTime(ms) {
    if (!ms) return "--:--:--";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  }

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#15102a", color: "#f5f0ff", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", maxWidth: 420, margin: "0 auto", padding: "32px 24px 120px", overflowY: "auto" }}>
      <OrbBackground />
      <div style={{ position: "relative", zIndex: 1 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 24, display: "flex", alignItems: "center", gap: 6 }}>← Back</button>

        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>Bladder Training</div>
          <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>Timed Voiding</h2>
          <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 6, lineHeight: 1.5 }}>Train your bladder by voiding at set intervals rather than responding to every urge. Start at 1–2 hours and gradually increase.</p>
        </div>

        {/* Interval selector */}
        <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "20px", marginBottom: 16, border: "1px solid #60a5fa33" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>Voiding interval</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[1, 1.5, 2, 2.5, 3, 3.5, 4].map(h => (
              <button key={h} onClick={() => { setIntervalHours(h); localStorage.setItem(VOIDING_ALARM_KEY, String(h)); stopTimer(); }} style={{
                background: intervalHours === h ? "linear-gradient(135deg, #2563eb, #60a5fa)" : "#221545",
                border: `1px solid ${intervalHours === h ? "#60a5fa" : "#3d2a5e"}`,
                borderRadius: 10, padding: "8px 14px", color: intervalHours === h ? "#fff" : "#9ca3af",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                boxShadow: intervalHours === h ? "0 0 12px #60a5fa44" : "none"
              }}>{h}h</button>
            ))}
          </div>
        </div>

        {/* Timer display */}
        <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "28px 20px", marginBottom: 16, border: "1px solid #60a5fa33", textAlign: "center" }}>
          <div style={{ fontSize: 48, fontWeight: 900, color: "#60a5fa", fontVariantNumeric: "tabular-nums", filter: active ? "drop-shadow(0 0 12px #60a5fa88)" : "none", marginBottom: 8 }}>
            {active ? formatTime(timeLeft) : `${intervalHours}:00:00`}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
            {active ? `Next void at ${nextVoid?.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}` : "Timer not running"}
          </div>
          {/* Alarm sound picker */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, textAlign: "left" }}>Alarm sound</div>
            <div style={{ display: "flex", gap: 8 }}>
              {ALARM_SOUNDS.map(s => (
                <button key={s.id} onClick={() => pickSound(s.id)} style={{
                  flex: 1, background: alarmSound === s.id ? "#60a5fa22" : "#221545",
                  border: `1px solid ${alarmSound === s.id ? "#60a5fa" : "#3d2a5e"}`,
                  borderRadius: 12, padding: "10px 0", color: alarmSound === s.id ? "#60a5fa" : "#9ca3af",
                  fontSize: 12.5, fontWeight: 700, cursor: "pointer"
                }}>{s.label}</button>
              ))}
            </div>
            <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 6, textAlign: "left" }}>Tap to preview. Rings even when the app is closed — or choose Silent for a quiet notification.</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => active ? stopTimer() : startTimer()} style={{
              flex: 2, background: active ? "linear-gradient(135deg, #ef444488, #ef4444)" : "linear-gradient(135deg, #2563eb, #60a5fa)",
              color: "#fff", border: "none", borderRadius: 14, padding: "14px",
              fontSize: 15, fontWeight: 700, cursor: "pointer",
              boxShadow: active ? "0 6px 20px #ef444444" : "0 6px 20px #60a5fa44"
            }}>{active ? "⏹ Stop timer" : "▶ Start timer"}</button>
            {active && (
              <button onClick={() => { stopTimer(); setTimeout(startTimer, 100); }} style={{
                flex: 1, background: "#221545", border: "1px solid #60a5fa44",
                borderRadius: 14, padding: "14px", color: "#60a5fa",
                fontSize: 13, fontWeight: 600, cursor: "pointer"
              }}>↺ Reset</button>
            )}
          </div>
        </div>

        {/* Clinical tip */}
        <div style={{ background: "#60a5fa11", border: "1px solid #60a5fa33", borderRadius: 14, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>💡 How bladder training works</div>
          <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.6 }}>
            Start at a comfortable interval and void at the timer — even if you don't feel urge. Increase by 15–30 minutes every 1–2 weeks. Most people reach a 3–4 hour interval within 6–8 weeks.
          </p>
        </div>

        {/* Upgrade prompt for diary */}
        {!pro && (
          <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: 20, padding: "20px", border: "1px solid #c084fc33", textAlign: "center" }}>
            <div style={{ fontSize: 22, marginBottom: 10 }}>📔</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#f5f0ff", marginBottom: 6 }}>Bladder Diary</div>
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 10, lineHeight: 1.5 }}>Track what you drink, how much, and how often you void. Export to Excel to review or share with your clinician.</p>
            <p style={{ fontSize: 12, color: "#c084fc", marginBottom: 16, lineHeight: 1.5 }}>Pro also unlocks 🔥 Intermediate & Advanced levels for every focus.</p>
            <button onClick={onUpgrade} style={{
              width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)",
              color: "#fff", border: "none", borderRadius: 12, padding: "13px",
              fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 20px #7c3aed44"
            }}>🔓 Unlock CoreDoc Pro — £1.99</button>
          </div>
        )}

        {pro && <BladderDiarySection />}
      </div>
    </div>
  );
}

// ─── BLADDER DIARY (PRO) ──────────────────────────────────────────────────────

function BladderDiarySection() {
  const [diary, setDiary] = useState(() => loadBladderDiary());
  const [showAddVoid, setShowAddVoid] = useState(false);
  const [showAddDrink, setShowAddDrink] = useState(false);
  const [urgency, setUrgency] = useState(1);
  const [leak, setLeak] = useState(false);
  const [drinkType, setDrinkType] = useState("water");
  const [drinkAmount, setDrinkAmount] = useState("250");
  const todayStr = getTodayStr();
  const todayEntries = diary.entries.filter(e => e.date === todayStr).sort((a,b) => b.ts - a.ts);
  const todayVoids = todayEntries.filter(e => e.type === "void");
  const todayDrinks = todayEntries.filter(e => e.type === "drink");
  const totalFluid = todayDrinks.reduce((a, e) => a + (e.amount || 0), 0);

  function addVoid() {
    const entry = { id: Date.now(), type: "void", date: todayStr, ts: Date.now(), time: new Date().toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}), urgency, leak };
    const updated = { ...diary, entries: [...diary.entries, entry] };
    setDiary(updated); saveBladderDiary(updated); setShowAddVoid(false); setUrgency(1); setLeak(false);
  }

  function addDrink() {
    const drink = DRINK_TYPES.find(d => d.id === drinkType);
    const entry = { id: Date.now(), type: "drink", date: todayStr, ts: Date.now(), time: new Date().toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}), drinkType, drinkLabel: drink?.label, drinkIcon: drink?.icon, amount: parseInt(drinkAmount) || 250 };
    const updated = { ...diary, entries: [...diary.entries, entry] };
    setDiary(updated); saveBladderDiary(updated); setShowAddDrink(false);
  }

  function deleteEntry(id) {
    const updated = { ...diary, entries: diary.entries.filter(e => e.id !== id) };
    setDiary(updated); saveBladderDiary(updated);
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#c084fc", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>✨ Pro</div>
          <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Bladder Diary</h3>
        </div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>{new Date().toLocaleDateString('en-GB', {day:'numeric',month:'short'})}</div>
      </div>

      {/* Evidence-based instructions */}
      <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", border: "1px solid #3d2a5e", borderRadius: 16, padding: "16px 18px", marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#c084fc", marginBottom: 8 }}>📔 How to keep your diary</div>
        <p style={{ fontSize: 13, color: "#c4b5d4", lineHeight: 1.65, margin: "0 0 10px" }}>
          A bladder diary is a simple record of what you drink and how often you pass urine. Keeping one for <strong style={{ color: "#f5f0ff" }}>at least 3 days</strong> (including work and rest days) gives the most useful picture — this is the period commonly recommended in continence guidance.
        </p>
        <div style={{ fontSize: 12.5, color: "#c4b5d4", lineHeight: 1.7 }}>
          <div style={{ marginBottom: 4 }}>💧 <strong style={{ color: "#f5f0ff" }}>Log each drink</strong> — the type and roughly how much (ml). Aim for around 1.5–2 litres of fluid a day unless advised otherwise.</div>
          <div style={{ marginBottom: 4 }}>🚻 <strong style={{ color: "#f5f0ff" }}>Log each time you pass urine</strong> — and note how strong the urge was.</div>
          <div style={{ marginBottom: 4 }}>⚠️ <strong style={{ color: "#f5f0ff" }}>Note any leaks</strong> — and what you were doing (coughing, rushing to the toilet).</div>
          <div>📤 <strong style={{ color: "#f5f0ff" }}>Export your diary</strong> as a spreadsheet to keep, review your patterns, or share with a healthcare professional if you choose.</div>
        </div>
        <p style={{ fontSize: 11, color: "#8b7ba8", lineHeight: 1.6, margin: "10px 0 0" }}>
          This is a personal tracking tool for general awareness. It does not diagnose or assess any condition — if you have concerns, please see your GP or a pelvic health physiotherapist.
        </p>
      </div>

      {/* Today summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Voids today", value: todayVoids.length, icon: "🚽", color: "#60a5fa" },
          { label: "Fluid in (ml)", value: totalFluid, icon: "💧", color: "#c084fc" },
          { label: "Leaks", value: todayVoids.filter(v => v.leak).length, icon: "⚠️", color: "#f59e0b" },
        ].map((s,i) => (
          <div key={i} style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 14, padding: "14px 10px", textAlign: "center", border: `1px solid ${s.color}33` }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Add buttons */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button onClick={() => setShowAddVoid(true)} style={{
          flex: 1, background: "linear-gradient(135deg, #2563eb, #60a5fa)", color: "#fff",
          border: "none", borderRadius: 12, padding: "12px", fontSize: 13, fontWeight: 700, cursor: "pointer"
        }}>+ Log void</button>
        <button onClick={() => setShowAddDrink(true)} style={{
          flex: 1, background: "linear-gradient(135deg, #7c3aed, #c084fc)", color: "#fff",
          border: "none", borderRadius: 12, padding: "12px", fontSize: 13, fontWeight: 700, cursor: "pointer"
        }}>+ Log drink</button>
      </div>

      {/* Add void modal */}
      {showAddVoid && (
        <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: 20, padding: "20px", marginBottom: 14, border: "1px solid #60a5fa44" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Log void</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Urgency level</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {URGENCY_LEVELS.map(u => (
              <button key={u.value} onClick={() => setUrgency(u.value)} style={{
                flex: 1, background: urgency === u.value ? u.color+"33" : "#221545",
                border: `1px solid ${urgency === u.value ? u.color : "#3d2a5e"}`,
                borderRadius: 8, padding: "8px 4px", color: urgency === u.value ? u.color : "#6b7280",
                fontSize: 10, fontWeight: 600, cursor: "pointer", textAlign: "center"
              }}>{u.label}</button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <button onClick={() => setLeak(l => !l)} style={{
              background: leak ? "#ef444422" : "#221545", border: `1px solid ${leak ? "#ef4444" : "#3d2a5e"}`,
              borderRadius: 8, padding: "8px 16px", color: leak ? "#ef4444" : "#6b7280",
              fontSize: 13, fontWeight: 600, cursor: "pointer"
            }}>{leak ? "✓ Leak occurred" : "No leak"}</button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={addVoid} style={{ flex: 2, background: "linear-gradient(135deg, #2563eb, #60a5fa)", color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Save</button>
            <button onClick={() => setShowAddVoid(false)} style={{ flex: 1, background: "#221545", border: "1px solid #3d2a5e", borderRadius: 10, padding: "12px", color: "#6b7280", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Add drink modal */}
      {showAddDrink && (
        <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: 20, padding: "20px", marginBottom: 14, border: "1px solid #c084fc44" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Log drink</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Drink type</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {DRINK_TYPES.map(d => (
              <button key={d.id} onClick={() => setDrinkType(d.id)} style={{
                background: drinkType === d.id ? d.color+"22" : "#221545",
                border: `1px solid ${drinkType === d.id ? d.color : "#3d2a5e"}`,
                borderRadius: 8, padding: "6px 12px", color: drinkType === d.id ? d.color : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: "pointer"
              }}>{d.icon} {d.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Amount (ml)</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {["100","150","200","250","330","500"].map(a => (
              <button key={a} onClick={() => setDrinkAmount(a)} style={{
                background: drinkAmount === a ? "#c084fc22" : "#221545",
                border: `1px solid ${drinkAmount === a ? "#c084fc" : "#3d2a5e"}`,
                borderRadius: 8, padding: "6px 12px", color: drinkAmount === a ? "#c084fc" : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: "pointer"
              }}>{a}ml</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={addDrink} style={{ flex: 2, background: "linear-gradient(135deg, #7c3aed, #c084fc)", color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Save</button>
            <button onClick={() => setShowAddDrink(false)} style={{ flex: 1, background: "#221545", border: "1px solid #3d2a5e", borderRadius: 10, padding: "12px", color: "#6b7280", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Today's log */}
      {todayEntries.length > 0 && (
        <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "18px 20px", border: "1px solid #3d2a5e55" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Today's log</div>
          {todayEntries.map(entry => (
            <div key={entry.id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, padding: "10px 12px", background: "#15102a66", borderRadius: 10 }}>
              <div style={{ fontSize: 20 }}>{entry.type === "void" ? "🚽" : (DRINK_TYPES.find(d=>d.id===entry.drinkType)?.icon || "💧")}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#f5f0ff" }}>
                  {entry.type === "void"
                    ? `Void — ${URGENCY_LEVELS.find(u=>u.value===entry.urgency)?.label || "Normal"}${entry.leak ? " · ⚠️ Leak" : ""}`
                    : `${entry.drinkLabel} — ${entry.amount}ml`
                  }
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{entry.time}</div>
              </div>
              <button onClick={() => deleteEntry(entry.id)} style={{ background: "none", border: "none", color: "#4b5563", fontSize: 16, cursor: "pointer", padding: 4 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {todayEntries.length === 0 && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#4b5563" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📔</div>
          <div style={{ fontSize: 13 }}>No entries today. Start logging above.</div>
        </div>
      )}

      {/* Export / Download button */}
      {diary.entries.length > 0 && (
        <button onClick={() => exportBladderDiary(diary)} style={{
          width: "100%", marginTop: 16,
          background: "linear-gradient(135deg, #271a4a, #1e1238)",
          border: "1px solid #c084fc44", borderRadius: 14, padding: "14px",
          color: "#c084fc", fontSize: 13, fontWeight: 700, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8
        }}>
          📥 Download as Excel spreadsheet
        </button>
      )}
    </div>
  );
}


async function exportBladderDiary(diary) {
  try {
    const entries = diary.entries.sort((a, b) => a.ts - b.ts);
    const dates = [...new Set(entries.map(e => e.date))].sort();
    const urgencyLabels = { 1: "Normal", 2: "Mild urge", 3: "Strong urge", 4: "Urgent/leak" };
    const esc = (s) => String(s == null ? "" : s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

    // Build an Excel-compatible spreadsheet as an HTML table (.xls). Excel and
    // Google Sheets open this as a real formatted spreadsheet with cells.
    let rows = "";
    rows += `<tr><th>Date</th><th>Time</th><th>Type</th><th>Detail</th><th>Amount (ml)</th><th>Urgency</th><th>Leak</th></tr>`;
    entries.forEach(e => {
      if (e.type === "void") {
        rows += `<tr><td>${esc(e.date)}</td><td>${esc(e.time)}</td><td>Void</td><td></td><td></td><td>${esc(urgencyLabels[e.urgency] || "Normal")}</td><td>${e.leak ? "Yes" : "No"}</td></tr>`;
      } else {
        rows += `<tr><td>${esc(e.date)}</td><td>${esc(e.time)}</td><td>Drink</td><td>${esc(e.drinkLabel || e.drinkType)}</td><td>${esc(e.amount)}</td><td></td><td></td></tr>`;
      }
    });

    let summary = `<tr><th colspan="7" style="background:#7c3aed;color:#fff;">Daily Summary</th></tr>`;
    summary += `<tr><th>Date</th><th>Voids</th><th>Fluid in (ml)</th><th>Leaks</th><th></th><th></th><th></th></tr>`;
    dates.forEach(date => {
      const dayEntries = entries.filter(e => e.date === date);
      const voids = dayEntries.filter(e => e.type === "void");
      const drinks = dayEntries.filter(e => e.type === "drink");
      const totalFluid = drinks.reduce((a, e) => a + (e.amount || 0), 0);
      const leaks = voids.filter(v => v.leak).length;
      summary += `<tr><td>${esc(date)}</td><td>${voids.length}</td><td>${totalFluid}</td><td>${leaks}</td><td></td><td></td><td></td></tr>`;
    });

    const footer = `<tr><td colspan="7" style="font-size:10px;color:#666;">Generated by CoreDoc: Pelvic Floor Exercises on ${new Date().toLocaleDateString('en-GB')}. For personal reference only — this does not replace professional medical advice.</td></tr>`;

    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body>` +
      `<table border="1" style="border-collapse:collapse;font-family:Calibri,Arial,sans-serif;">` +
      `<tr><th colspan="7" style="background:#7c3aed;color:#fff;font-size:14px;">CoreDoc Bladder Diary</th></tr>` +
      rows +
      `<tr><td colspan="7"></td></tr>` +
      summary +
      footer +
      `</table></body></html>`;

    const fname = `coredoc-bladder-diary-${new Date().toISOString().slice(0,10)}.xls`;

    // On-device (Capacitor): write the file natively and open the Android
    // share sheet — the browser-style download link does not work inside the
    // WebView. Requires @capacitor/filesystem and @capacitor/share.
    const cap = window.Capacitor && window.Capacitor.Plugins;
    if (cap && cap.Filesystem && cap.Share) {
      const res = await cap.Filesystem.writeFile({
        path: fname, data: "\ufeff" + html, directory: "CACHE", encoding: "utf8"
      });
      await cap.Share.share({
        title: "CoreDoc Bladder Diary",
        text: "Your bladder diary spreadsheet",
        url: res.uri, dialogTitle: "Save or share your diary"
      });
      return;
    }

    // Browser fallback (web app / preview)
    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch(e) {
    if (e && /canceled|cancelled/i.test(String(e.message||e))) return; // user closed share sheet
    alert("Download failed. Please try again.");
  }
}

const STORAGE_KEY = "coredoc-progress-v2";
const INCOMPLETE_SESSION_KEY = "coredoc-incomplete-session-v1";
const CUSTOM_TIMINGS_KEY = "coredoc-custom-timings-v1";

function loadCustomTimings() {
  try { const d = localStorage.getItem(CUSTOM_TIMINGS_KEY); return d ? JSON.parse(d) : {}; }
  catch { return {}; }
}
function saveCustomTimings(t) { try { localStorage.setItem(CUSTOM_TIMINGS_KEY, JSON.stringify(t)); } catch {} }

function saveIncompleteSession(programId, exerciseIndex, currentRep = 0) {
  try {
    localStorage.setItem(INCOMPLETE_SESSION_KEY, JSON.stringify({
      programId, exerciseIndex, currentRep, date: getTodayStr(), ts: Date.now()
    }));
  } catch {}
}

function loadIncompleteSession() {
  try {
    const d = localStorage.getItem(INCOMPLETE_SESSION_KEY);
    if (!d) return null;
    const session = JSON.parse(d);
    // Only resume if it was today
    if (session.date !== getTodayStr()) {
      localStorage.removeItem(INCOMPLETE_SESSION_KEY);
      return null;
    }
    return session;
  } catch { return null; }
}

function clearIncompleteSession() {
  try { localStorage.removeItem(INCOMPLETE_SESSION_KEY); } catch {}
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function loadProgress() {
  try { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : { sessions: [] }; }
  catch { return { sessions: [] }; }
}
function saveProgress(d) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch {} }
function getTodayStr() { return new Date().toISOString().slice(0, 10); }
function getStreakDays(sessions) {
  if (!sessions.length) return 0;
  const dates = [...new Set(sessions.map(s => s.date))].sort().reverse();
  let streak = 0, cursor = getTodayStr();
  for (const d of dates) {
    if (d === cursor) { streak++; const p = new Date(cursor); p.setDate(p.getDate() - 1); cursor = p.toISOString().slice(0, 10); }
    else break;
  }
  return streak;
}
function getLast7Days() {
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - (6 - i)); return d.toISOString().slice(0, 10); });
}

// ─── AVATAR DATA ─────────────────────────────────────────────────────────────

const SKIN_TONES = [
  { id: "s1", color: "#FDDBB4", label: "Fair" },
  { id: "s2", color: "#F5C89A", label: "Light" },
  { id: "s3", color: "#E8A87C", label: "Medium" },
  { id: "s4", color: "#C68642", label: "Tan" },
  { id: "s5", color: "#8D5524", label: "Brown" },
  { id: "s6", color: "#4A2912", label: "Deep" },
];

const HAIR_STYLES = [
  { id: "h1", label: "Short", path: "M 50,28 Q 30,20 28,38 Q 26,50 30,55 Q 35,45 50,42 Q 65,45 70,55 Q 74,50 72,38 Q 70,20 50,28 Z" },
  { id: "h2", label: "Long", path: "M 50,28 Q 28,18 24,40 Q 20,60 22,80 Q 30,78 32,60 Q 35,45 50,42 Q 65,45 68,60 Q 70,78 78,80 Q 80,60 76,40 Q 72,18 50,28 Z" },
  { id: "h3", label: "Curly", path: "M 50,28 Q 30,20 27,36 Q 22,30 24,42 Q 20,38 22,50 Q 26,46 30,54 Q 35,44 50,42 Q 65,44 70,54 Q 74,46 78,50 Q 80,38 76,42 Q 78,30 73,36 Q 70,20 50,28 Z" },
  { id: "h4", label: "Bun", path: "M 50,22 Q 42,18 40,24 Q 36,22 38,28 Q 36,30 40,32 Q 44,28 50,28 Q 56,28 60,32 Q 64,30 62,28 Q 64,22 60,24 Q 58,18 50,22 Z M 43,19 Q 50,14 57,19 Q 54,16 50,15 Q 46,16 43,19 Z" },
];

const HAIR_COLORS = [
  { id: "hc1", color: "#1a1008", label: "Black" },
  { id: "hc2", color: "#3B1F0A", label: "Dark Brown" },
  { id: "hc3", color: "#7B4A1E", label: "Brown" },
  { id: "hc4", color: "#C49A3C", label: "Blonde" },
  { id: "hc5", color: "#B5312C", label: "Red" },
  { id: "hc6", color: "#888888", label: "Grey" },
  { id: "hc7", color: "#c084fc", label: "Purple" },
  { id: "hc8", color: "#f472b6", label: "Pink" },
];

const OUTFIT_COLORS = [
  { id: "o1", color: "#7c3aed", label: "Purple" },
  { id: "o2", color: "#db2777", label: "Pink" },
  { id: "o3", color: "#7c3aed", label: "Green" },
  { id: "o4", color: "#2563eb", label: "Blue" },
  { id: "o5", color: "#d97706", label: "Amber" },
  { id: "o6", color: "#dc2626", label: "Red" },
  { id: "o7", color: "#0891b2", label: "Teal" },
  { id: "o8", color: "#4b5563", label: "Slate" },
];

const DEFAULT_AVATAR = { skinId: "s2", hairId: "h1", hairColorId: "hc1", outfitId: "o1", name: "My Avatar" };
const AVATAR_KEY = "pelvic-floor-avatar";

function loadAvatar() {
  try { const d = localStorage.getItem(AVATAR_KEY); return d ? JSON.parse(d) : DEFAULT_AVATAR; }
  catch { return DEFAULT_AVATAR; }
}
function saveAvatar(a) { try { localStorage.setItem(AVATAR_KEY, JSON.stringify(a)); } catch {} }

// ─── SVG AVATAR ───────────────────────────────────────────────────────────────

function AvatarSVG({ avatar, size = 80, animated = false, phase = null }) {
  // If user has set a photo, show that instead of SVG avatar
  if (avatar?.photo) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
        <img src={avatar.photo} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    );
  }

  const skin = SKIN_TONES.find(s => s.id === avatar.skinId) || SKIN_TONES[1];
  const hair = HAIR_STYLES.find(h => h.id === avatar.hairId) || HAIR_STYLES[0];
  const hairColor = HAIR_COLORS.find(h => h.id === avatar.hairColorId) || HAIR_COLORS[0];
  const outfit = OUTFIT_COLORS.find(o => o.id === avatar.outfitId) || OUTFIT_COLORS[0];

  const isSqueezing = phase === "squeeze" || phase === "hold";
  const bodyAnim = animated && isSqueezing ? "avatarSqueeze 0.6s ease-in-out infinite alternate" : animated && phase === "release" ? "avatarRelax 0.4s ease-out both" : "none";

  return (
    <svg width={size} height={size} viewBox="0 0 100 120" style={{ overflow: "visible" }}>
      <style>{`
        @keyframes avatarSqueeze { from{transform:scaleY(1)} to{transform:scaleY(0.94) scaleX(1.03)} }
        @keyframes avatarRelax { from{transform:scaleY(0.94) scaleX(1.03)} to{transform:scaleY(1)} }
        @keyframes avatarBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        .avatar-body { transform-origin: 50px 80px; animation: ${bodyAnim}; }
        ${animated && phase === "rest" ? ".avatar-body { animation: avatarBounce 2s ease-in-out infinite; }" : ""}
      `}</style>

      <g className="avatar-body">
        {/* Body / outfit */}
        <ellipse cx="50" cy="95" rx="22" ry="16" fill={outfit.color} />
        <rect x="28" y="80" width="44" height="22" rx="8" fill={outfit.color} />

        {/* Neck */}
        <rect x="44" y="68" width="12" height="14" rx="4" fill={skin.color} />

        {/* Head */}
        <ellipse cx="50" cy="52" rx="22" ry="24" fill={skin.color} />

        {/* Hair */}
        <path d={hair.path} fill={hairColor.color} />

        {/* Eyes */}
        <ellipse cx="42" cy="50" rx="3.5" ry={isSqueezing ? "2" : "3.5"} fill="#1a1008" style={{ transition: "ry 0.2s ease" }} />
        <ellipse cx="58" cy="50" rx="3.5" ry={isSqueezing ? "2" : "3.5"} fill="#1a1008" style={{ transition: "ry 0.2s ease" }} />
        {/* Eye shine */}
        <circle cx="43.5" cy="48.5" r="1" fill="white" opacity="0.8" />
        <circle cx="59.5" cy="48.5" r="1" fill="white" opacity="0.8" />

        {/* Mouth */}
        {isSqueezing
          ? <path d="M 44,60 Q 50,57 56,60" stroke="#8D5524" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          : <path d="M 44,60 Q 50,64 56,60" stroke="#8D5524" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        }

        {/* Cheeks */}
        <ellipse cx="37" cy="57" rx="5" ry="3" fill={outfit.color} opacity="0.3" />
        <ellipse cx="63" cy="57" rx="5" ry="3" fill={outfit.color} opacity="0.3" />

        {/* Arms */}
        <ellipse cx="22" cy="88" rx="8" ry="5" fill={outfit.color} transform="rotate(-20, 22, 88)" />
        <ellipse cx="78" cy="88" rx="8" ry="5" fill={outfit.color} transform="rotate(20, 78, 88)" />
      </g>
    </svg>
  );
}

// ─── AVATAR CUSTOMISER ────────────────────────────────────────────────────────

function AvatarCustomiser({ avatar, onSave, onClose, pro, onUpgrade }) {
  const [draft, setDraft] = useState({ ...avatar });
  const [editingName, setEditingName] = useState(false);
  const [activeTab, setActiveTab] = useState("style");
  const photoInputRef = useRef(null);

  function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setDraft(d => ({ ...d, photo: ev.target.result }));
    };
    reader.readAsDataURL(file);
  }

  function Section({ label, children }) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>{label}</div>
        {children}
      </div>
    );
  }

  function SwatchRow({ items, selectedId, onSelect, type = "color" }) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {items.map(item => (
          <button key={item.id} onClick={() => onSelect(item.id)} style={{
            width: type === "color" ? 36 : "auto",
            height: type === "color" ? 36 : "auto",
            padding: type === "color" ? 0 : "6px 12px",
            borderRadius: type === "color" ? "50%" : 20,
            background: type === "color" ? item.color : selectedId === item.id ? "#c084fc22" : "#221545",
            border: selectedId === item.id ? "3px solid #c084fc" : "2px solid #3d2a5e44",
            cursor: "pointer", transition: "all 0.15s ease",
            fontSize: 12, color: selectedId === item.id ? "#c084fc" : "#9ca3af", fontWeight: 600,
            transform: selectedId === item.id ? "scale(1.12)" : "scale(1)",
            boxShadow: selectedId === item.id ? "0 0 12px #c084fc66" : "none"
          }}>{type !== "color" ? item.label : ""}</button>
        ))}
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 300, padding: 0 }}>
      <div className="slide-up" style={{
        background: "linear-gradient(180deg, #110820, #0e0a1a)",
        borderRadius: "24px 24px 0 0", padding: "0 0 40px", maxWidth: 420,
        width: "100%", maxHeight: "90vh", overflow: "auto",
        border: "1px solid #3d2a5e"
      }}>
        {/* Handle + header */}
        <div style={{ position: "sticky", top: 0, background: "linear-gradient(180deg, #110820, #110820dd)", padding: "16px 24px 12px", borderBottom: "1px solid #3d2a5e33", zIndex: 1 }}>
          <div style={{ width: 40, height: 4, background: "#3d2a5e", borderRadius: 2, margin: "0 auto 16px" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#f5f0ff", margin: 0 }}>Customise Avatar</h3>
            <button onClick={onClose} style={{ background: "#ffffff11", border: "none", borderRadius: 8, padding: "6px 12px", color: "#9ca3af", cursor: "pointer", fontSize: 13 }}>✕ Cancel</button>
          </div>
          <div style={{ display: "flex", background: "#ffffff11", borderRadius: 10, padding: 3, gap: 3 }}>
            {[{ id: "style", label: "🎨 Style" }, { id: "photo", label: "📷 Photo" }].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                flex: 1, background: activeTab === tab.id ? "#c084fc33" : "transparent",
                border: "none", borderRadius: 8, padding: "7px", color: activeTab === tab.id ? "#c084fc" : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: "pointer"
              }}>{tab.label}</button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 24px 0" }}>
          <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: "50%", padding: draft.photo ? 0 : 16, border: "2px solid #3d2a5e", marginBottom: 14, boxShadow: "0 0 32px #c084fc22", overflow: "hidden", width: 122, height: 122, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {draft.photo
              ? <img src={draft.photo} alt="avatar" style={{ width: 122, height: 122, objectFit: "cover", borderRadius: "50%" }} />
              : <AvatarSVG avatar={draft} size={90} />
            }
          </div>
          {editingName ? (
            <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              onBlur={() => setEditingName(false)} autoFocus
              style={{ background: "#221545", border: "1px solid #c084fc55", borderRadius: 10, padding: "8px 16px", color: "#f5f0ff", fontSize: 16, fontWeight: 700, textAlign: "center", outline: "none", width: 180 }}
            />
          ) : (
            <button onClick={() => setEditingName(true)} style={{ background: "none", border: "none", cursor: "pointer", color: "#f5f0ff", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
              {draft.name} <span style={{ fontSize: 12, color: "#6b7280" }}>✏️</span>
            </button>
          )}
        </div>

        {/* Style tab */}
        {activeTab === "style" && (
          <div style={{ padding: "20px 24px 0" }}>
            <Section label="Skin Tone">
              <SwatchRow items={SKIN_TONES} selectedId={draft.skinId} onSelect={id => setDraft(d => ({ ...d, skinId: id }))} />
            </Section>
            <Section label="Hair Style">
              <SwatchRow items={HAIR_STYLES} selectedId={draft.hairId} onSelect={id => setDraft(d => ({ ...d, hairId: id }))} type="label" />
            </Section>
            <Section label="Hair Colour">
              <SwatchRow items={HAIR_COLORS} selectedId={draft.hairColorId} onSelect={id => setDraft(d => ({ ...d, hairColorId: id }))} />
            </Section>
            <Section label="Outfit Colour">
              <SwatchRow items={OUTFIT_COLORS} selectedId={draft.outfitId} onSelect={id => setDraft(d => ({ ...d, outfitId: id }))} />
            </Section>
          </div>
        )}

        {/* Photo tab — Pro only */}
        {activeTab === "photo" && (
          <div style={{ padding: "20px 24px 0" }}>
            {!pro ? (
              <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: 20, padding: 24, textAlign: "center", border: "1px solid #c084fc33" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📷</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#f5f0ff", marginBottom: 8 }}>Use your own photo</div>
                <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20, lineHeight: 1.6 }}>Upload a selfie or any photo to use as your avatar instead of the illustrated character.</p>
                <button onClick={onUpgrade} style={{
                  width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)",
                  color: "#fff", border: "none", borderRadius: 12, padding: "13px",
                  fontSize: 14, fontWeight: 700, cursor: "pointer"
                }}>🔓 Unlock Pro — £1.99/month</button>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20, lineHeight: 1.5 }}>Upload a photo to use as your avatar. It will appear as a circle in your exercise screen.</p>
                {draft.photo && (
                  <div style={{ marginBottom: 16 }}>
                    <img src={draft.photo} alt="preview" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", display: "block", margin: "0 auto 12px", border: "2px solid #c084fc" }} />
                    <button onClick={() => setDraft(d => ({ ...d, photo: null }))} style={{
                      display: "block", margin: "0 auto", background: "#ef444422", border: "1px solid #ef444444",
                      borderRadius: 10, padding: "8px 20px", color: "#ef4444", fontSize: 13, cursor: "pointer"
                    }}>Remove photo</button>
                  </div>
                )}
                <input type="file" accept="image/*" ref={photoInputRef} onChange={handlePhotoUpload} style={{ display: "none" }} />
                <button onClick={() => photoInputRef.current?.click()} style={{
                  width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)",
                  color: "#fff", border: "none", borderRadius: 14, padding: "16px",
                  fontSize: 15, fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 6px 20px #7c3aed44"
                }}>📷 Choose photo from camera roll</button>
                <p style={{ fontSize: 11, color: "#4b5563", marginTop: 10, textAlign: "center" }}>Photo stored on your device only.</p>
              </div>
            )}
          </div>
        )}

        <div style={{ padding: "8px 24px 0" }}>
          <button onClick={() => { saveAvatar(draft); onSave(draft); }} className="btn-primary" style={{
            width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)",
            color: "#fff", border: "none", borderRadius: 14, padding: "16px",
            fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 8px 24px #7c3aed44"
          }}>Save Avatar</button>
        </div>
      </div>
    </div>
  );
}

const SCREENER_KEY = "coredoc-screener-v1";
function loadScreener() {
  try { const d = localStorage.getItem(SCREENER_KEY); return d ? JSON.parse(d) : null; }
  catch { return null; }
}
function saveScreener(d) { try { localStorage.setItem(SCREENER_KEY, JSON.stringify(d)); } catch {} }

const SCREENER_STEPS = [
  {
    id: "postnatal",
    question: "Did you have a baby recently?",
    subtitle: "This helps us keep you safe",
    emoji: "🤱",
    options: [
      { label: "Yes, less than 6 weeks ago", value: "under6", warning: true },
      { label: "Yes, 6 weeks or more ago", value: "over6" },
      { label: "No", value: "no" },
    ]
  },
  {
    id: "pelvicpain",
    question: "Do you experience pelvic pain?",
    subtitle: "Pain in your lower tummy, pelvis, or between your legs",
    emoji: "🩺",
    options: [
      { label: "Yes", value: "yes", hypertonicWarning: true },
      { label: "Sometimes", value: "sometimes", hypertonicWarning: true },
      { label: "No", value: "no" },
    ]
  },
  {
    id: "painintercourse",
    question: "Do you experience pain during intercourse?",
    subtitle: "This is more common than you think and important to flag",
    emoji: "💜",
    options: [
      { label: "Yes", value: "yes", hypertonicWarning: true },
      { label: "Sometimes", value: "sometimes", hypertonicWarning: true },
      { label: "No", value: "no" },
      { label: "Not applicable", value: "na" },
    ]
  },
  {
    id: "focus",
    question: "Which area would you like to focus on?",
    subtitle: "Pick any focus — you can explore all the programmes and switch anytime",
    emoji: "🎯",
    options: [
      { label: "General pelvic floor fitness", value: "beginner" },
      { label: "Postpartum Wellness — after birth", value: "postnatal" },
      { label: "Confidence & Control — strength & endurance", value: "incontinence" },
      { label: "Core & Pelvic Tone — core & pelvic floor", value: "prolapse" },
    ]
  }
];

// The user chooses their focus area directly — the app does not infer a
// condition from symptoms. This is content navigation by preference, not
// assessment. The safety questions above only signpost to a professional.
function getSelectedProgram(answers) {
  return answers.focus || "beginner";
}

// ─── SYMPTOM SCREENER ─────────────────────────────────────────────────────────

function SymptomScreener({ onComplete }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);
  const [showHypertonicWarning, setShowHypertonicWarning] = useState(false);
  const [hypertonicAcknowledged, setHypertonicAcknowledged] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  const current = SCREENER_STEPS[step];
  const totalSteps = SCREENER_STEPS.length;
  const progress = (step / totalSteps) * 100;

  function handleAnswer(value) {
    const option = current.options.find(o => o.value === value);
    
    // Postnatal warning (existing)
    if (option?.warning && !warningAcknowledged) {
      setShowWarning(true);
      return;
    }
    
    // Hypertonic/pelvic pain warning — always show for each question independently
    if (option?.hypertonicWarning) {
      setPendingValue(value);
      setShowHypertonicWarning(true);
      return;
    }

    advanceWithAnswer(value);
  }

  function advanceWithAnswer(value) {
    const newAnswers = { ...answers, [current.id]: value };
    setAnswers(newAnswers);
    if (step + 1 < totalSteps) {
      setHypertonicAcknowledged(false); // Reset for next question
      setStep(s => s + 1);
    } else {
      const selected = getSelectedProgram(newAnswers);
      saveScreener({ answers: newAnswers, recommended: selected, completedAt: Date.now() });
      onComplete(newAnswers, selected);
    }
  }

  function handleWarningContinue() {
    setShowWarning(false);
    setWarningAcknowledged(true);
  }

  function handleWarningStop() {
    setShowWarning(false);
  }

  function handleHypertonicContinue() {
    setShowHypertonicWarning(false);
    setHypertonicAcknowledged(true);
    const val = pendingValue;
    setPendingValue(null);
    advanceWithAnswer(val);
  }

  function handleHypertonicStop() {
    setShowHypertonicWarning(false);
    setPendingValue(null);
  }

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#15102a", color: "#f5f0ff", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", maxWidth: 420, margin: "0 auto" }}>
      <OrbBackground />

      {/* Hypertonic / pelvic pain warning modal */}
      {showHypertonicWarning && (
        <div style={{ position: "fixed", inset: 0, background: "#000000dd", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: 16 }}>
          <div className="slide-up" style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: 24, padding: 28, maxWidth: 420, width: "100%", border: "1px solid #f59e0b77", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ fontSize: 44, marginBottom: 12, textAlign: "center" }}>⚠️</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#f59e0b", marginBottom: 16, textAlign: "center", lineHeight: 1.4 }}>
              A quick word first
            </div>
            <p style={{ fontSize: 14.5, color: "#f5f0ff", lineHeight: 1.7, marginBottom: 20 }}>
              With these symptoms, it's worth having a check-up with your <strong style={{ color: "#c084fc" }}>GP or a pelvic health physiotherapist</strong> before starting. We don't recommend doing these exercises until then — sometimes tight muscles, not weak ones, are the cause, and squeezing can make that worse.
            </p>
            <p style={{ fontSize: 14, color: "#c4b5d4", lineHeight: 1.6, marginBottom: 20, textAlign: "center", fontWeight: 700 }}>
              Would you like to stop, or continue anyway?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleHypertonicStop} style={{
                flex: 1, background: "#221545", border: "1px solid #3d2a5e",
                borderRadius: 14, padding: "14px", color: "#f5f0ff",
                fontSize: 13, fontWeight: 700, cursor: "pointer"
              }}>Stop</button>
              <button onClick={handleHypertonicContinue} style={{
                flex: 2, background: "linear-gradient(135deg, #d97706, #f59e0b)",
                border: "none", borderRadius: 14, padding: "14px",
                color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer"
              }}>Continue anyway</button>
            </div>
          </div>
        </div>
      )}

      {/* Postnatal Warning modal */}
      {showWarning && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 400, padding: 16 }}>
          <div className="slide-up" style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: 24, padding: 28, maxWidth: 420, width: "100%", border: "1px solid #f59e0b55" }}>
            <div style={{ fontSize: 40, marginBottom: 16, textAlign: "center" }}>⚠️</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#f59e0b", marginBottom: 12, textAlign: "center" }}>Important safety notice</div>
            <p style={{ fontSize: 14, color: "#c4b5d4", lineHeight: 1.7, marginBottom: 20, textAlign: "center" }}>
              Pelvic floor exercises should only begin <strong style={{ color: "#f5f0ff" }}>6 weeks after birth</strong> — or when cleared by your midwife, GP, or health visitor.
            </p>
            <p style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.6, marginBottom: 24, textAlign: "center" }}>
              Starting too early can cause more harm than good. Please wait until your 6-week postnatal check before using this app.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleWarningStop} style={{ flex: 1, background: "#221545", border: "1px solid #3d2a5e", borderRadius: 14, padding: "14px", color: "#9ca3af", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Go back
              </button>
              <button onClick={handleWarningContinue} style={{ flex: 2, background: "linear-gradient(135deg, #d97706, #f59e0b)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                I understand, continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ position: "relative", zIndex: 1, padding: "56px 24px 40px" }}>

        {/* Progress bar */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Step {step + 1} of {totalSteps}
            </span>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 13, cursor: "pointer" }}>← Back</button>
            )}
          </div>
          <div style={{ height: 4, background: "#221545", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 2, background: "linear-gradient(90deg, #7c3aed, #c084fc)", width: `${progress + (100 / totalSteps)}%`, transition: "width 0.4s cubic-bezier(0.16,1,0.3,1)", boxShadow: "0 0 8px #c084fc88" }} />
          </div>
        </div>

        {/* Question */}
        <div className="fade-up" key={step} style={{ marginBottom: 36 }}>
          <div style={{ fontSize: 52, marginBottom: 20, textAlign: "center" }}>{current.emoji}</div>
          <h2 style={{ fontSize: 24, fontWeight: 900, lineHeight: 1.3, marginBottom: 8, textAlign: "center" }}>{current.question}</h2>
          <p style={{ fontSize: 14, color: "#6b7280", textAlign: "center", margin: 0 }}>{current.subtitle}</p>
        </div>

        {/* Options */}
        <div className="fade-up-delay-1" key={`opts-${step}`} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {current.options.map((opt, i) => (
            <button key={i} onClick={() => handleAnswer(opt.value)} className="card-hover" style={{
              background: "linear-gradient(135deg, #1a1030ee, #110820ee)",
              backdropFilter: "blur(12px)",
              border: "1px solid #3d2a5e55",
              borderRadius: 16, padding: "18px 20px",
              color: "#f5f0ff", fontSize: 15, fontWeight: 600,
              cursor: "pointer", textAlign: "left",
              display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 4px 16px #00000033",
              transition: "all 0.2s ease"
            }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid #c084fc44", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {opt.warning && <span style={{ fontSize: 10 }}>⚠️</span>}
              </div>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── RECOMMENDATION SCREEN ────────────────────────────────────────────────────

function RecommendationScreen({ answers, recommendedId, onStart, onBrowse }) {
  const prog = PROGRAMS.find(p => p.id === recommendedId) || PROGRAMS[0];

  const reasonMap = {
    postnatal: "You chose Postpartum Wellness. This programme starts gently and builds up.",
    prolapse: "You chose Core & Pelvic Tone. You can switch focus anytime.",
    incontinence: "You chose Confidence & Control. You can switch focus anytime.",
    beginner: "You chose general pelvic floor fitness — a great place to start.",
  };

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#15102a", color: "#f5f0ff", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", maxWidth: 420, margin: "0 auto", padding: "60px 24px 40px" }}>
      <OrbBackground color1={prog.glow} color2={prog.color} color3="#2563eb" />
      <div style={{ position: "relative", zIndex: 1 }}>

        <div className="pop-in" style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>✨</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: prog.color, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>Your selection</div>
          <h2 style={{ fontSize: 26, fontWeight: 900, marginBottom: 8 }}>You've chosen</h2>
        </div>

        {/* Recommended card */}
        <div className="fade-up" style={{ background: `linear-gradient(135deg, ${prog.glow}22, ${prog.color}15)`, borderRadius: 20, padding: "24px", marginBottom: 16, border: `2px solid ${prog.color}44`, boxShadow: `0 8px 32px ${prog.color}22` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: "#f5f0ff" }}>{prog.name}</span>
            <span style={{ background: prog.color + "22", color: prog.color, borderRadius: 20, padding: "4px 14px", fontSize: 11, fontWeight: 700 }}>{prog.tag}</span>
          </div>
          <p style={{ fontSize: 13, color: "#c4b5d4", lineHeight: 1.6, marginBottom: 14 }}>{reasonMap[recommendedId]}</p>
          <div style={{ display: "flex", gap: 14 }}>
            <span style={{ fontSize: 12, color: prog.color }}>✦ {prog.exercises.length} exercises</span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>3× daily</span>
          </div>
        </div>

        {/* Beginner tip — shown for all non-beginner programmes */}
        {recommendedId !== "beginner" && (
          <div className="fade-up-delay-1" style={{ background: "#c084fc11", border: "1px solid #c084fc44", borderRadius: 14, padding: "16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#c084fc", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>💜 Tip for beginners</div>
            <p style={{ fontSize: 13, color: "#c4b5d4", marginBottom: 14, lineHeight: 1.6 }}>
              If you are not familiar with pelvic floor exercises, you might like to start with our <strong style={{ color: "#f5f0ff" }}>Foundation programme</strong> — even if you picked a different focus area.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => onStart(PROGRAMS.find(p => p.id === "beginner"))} style={{
                flex: 1, background: "linear-gradient(135deg, #7c3aed, #c084fc)",
                color: "#fff", border: "none", borderRadius: 10, padding: "10px 8px",
                fontSize: 12, fontWeight: 700, cursor: "pointer"
              }}>Start Foundation</button>
              <button onClick={() => onStart(prog)} style={{
                flex: 1, background: "transparent", border: "1px solid #c084fc55",
                color: "#c084fc", borderRadius: 10, padding: "10px 8px",
                fontSize: 12, fontWeight: 600, cursor: "pointer"
              }}>My focus</button>
            </div>
          </div>
        )}

        {/* Warning if postnatal under 6 weeks was selected */}
        {answers.postnatal === "under6" && (
          <div className="fade-up-delay-1" style={{ background: "#f59e0b11", border: "1px solid #f59e0b44", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>⚠️ Reminder</div>
            <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.5 }}>Please wait until 6 weeks after birth before starting these exercises, and get clearance from your midwife or GP first.</p>
          </div>
        )}

        {/* General signposting note */}
        {(recommendedId === "prolapse" || recommendedId === "incontinence") && (
          <div className="fade-up-delay-1" style={{ background: "#f472b611", border: "1px solid #f472b644", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#f472b6", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>💜 Good to know</div>
            <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.5 }}>These are general exercises for pelvic floor fitness. If you have symptoms you're concerned about, it's always worth seeing your GP or a pelvic health physiotherapist for a personal assessment.</p>
          </div>
        )}

        {/* Consistency note for leaking focus */}
        {recommendedId === "incontinence" && (
          <div className="fade-up-delay-1" style={{ background: "#60a5fa11", border: "1px solid #60a5fa44", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>⏱ Consistency helps</div>
            <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.6 }}>
              Published guidance suggests <strong style={{ color: "#f5f0ff" }}>consistent pelvic floor exercises over at least 3 months</strong> to build strength. Stick with it — many people notice a difference by week 8–12.
            </p>
          </div>
        )}

        {/* Consistency note for prolapse focus */}
        {recommendedId === "prolapse" && (
          <div className="fade-up-delay-2" style={{ background: "#f472b611", border: "1px solid #f472b633", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#f472b6", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>⏱ Consistency helps</div>
            <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.6 }}>
              Published guidance suggests <strong style={{ color: "#f5f0ff" }}>regular pelvic floor exercises over several months</strong> to build strength. Consistency is key — aim for 3 short sessions daily.
            </p>
          </div>
        )}

        <div className="fade-up-delay-2" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          <button onClick={() => onStart(prog)} className="btn-primary" style={{
            width: "100%", background: `linear-gradient(135deg, ${prog.glow}, ${prog.color})`,
            color: "#fff", border: "none", borderRadius: 14, padding: "16px",
            fontSize: 16, fontWeight: 700, cursor: "pointer",
            boxShadow: `0 8px 24px ${prog.color}44`
          }}>Start {prog.name} →</button>

          <button onClick={onBrowse} style={{
            width: "100%", background: "transparent", border: "1px solid #3d2a5e",
            borderRadius: 14, padding: "14px", color: "#9ca3af",
            fontSize: 14, fontWeight: 600, cursor: "pointer"
          }}>Browse all programmes instead</button>
        </div>
      </div>
    </div>
  );
}



// ─── EDUCATION MODULE ────────────────────────────────────────────────────────

const EDU_SLIDES = [
  { id: "intro", illustration: "woman-standing", title: "Know your pelvic floor", body: "Before we exercise, let's understand what we're working with. Your pelvic floor is a group of muscles — and like any muscle, knowing how it works helps you train it better.", highlight: null },
  { id: "where", illustration: "where-is-it", title: "Where is it?", body: "Your pelvic floor sits at the base of your pelvis — like the floor of a room. It stretches from your pubic bone at the front to your tailbone at the back, and from side to side between your sit bones.", highlight: "You cannot see it from the outside. It sits deep inside your body, hidden beneath your abdomen." },
  { id: "what-does", illustration: "what-it-does", title: "What does it do?", body: "Your pelvic floor has 4 main jobs — it supports your pelvic organs, controls your bladder and bowel, helps with sexual function, and works with your core during movement.", highlight: "Every time you cough, sneeze, laugh or lift — your pelvic floor fires automatically to prevent leaking." },
  { id: "what-is", illustration: "bowl", title: "Shaped like a hammock", body: "Your pelvic floor muscles sit inside your pelvis, stretching from your pubic bone at the front to your tailbone at the back.", highlight: "Think of it like a hammock holding up your bladder, uterus, and bowel." },
  { id: "organs", illustration: "organs", title: "What it supports", body: "Three organs sit on your pelvic floor — your bladder at the front, your uterus (womb) in the middle, and your rectum (bowel) at the back.", highlight: "A strong pelvic floor helps hold these organs comfortably in place — which is why keeping it strong matters." },
  { id: "two-layers", illustration: "two-layers", title: "Two layers working together", body: "There are two main layers. The superficial layer acts like a drawstring — it closes and tightens around the vaginal opening and anus.", highlight: "The deeper layer acts like an elevator — it lifts upward toward your belly button." },
  { id: "squeeze", illustration: "drawstring", title: "Engage your core & squeeze", body: "When you engage your core and squeeze your pelvic floor, you're doing two things at once: closing the superficial layer and lifting the deeper layer.", highlight: "Engage your core, squeeze inward and upward — like stopping the flow of urine and lifting at the same time." },
  { id: "relax", illustration: "flower", title: "The release is just as important", body: "A muscle that can't fully relax can't fully contract. After every squeeze, let everything go completely.", highlight: "Imagine a flower blooming open — or an elevator reaching the ground floor and the doors opening." },
  { id: "when-weak", illustration: "when-weak", title: "Signs your pelvic floor could be stronger", body: "When the pelvic floor could use some strengthening, you might notice less control when you cough, sneeze or exercise, a feeling of heaviness, needing the toilet more urgently, or reduced sensation.", highlight: "Very common after having a baby — and something you can work on. You are not alone." },
  { id: "technique", illustration: "sitting", title: "Getting the technique right", body: "Sit comfortably and feel your sit bones on the seat. Gently squeeze and lift — without holding your breath, squeezing your thighs, or tensing your tummy.", highlight: "If you're not sure you've found the right muscles, that's normal. It takes practice." },
  { id: "ready", illustration: "ready", title: "You're ready to squeeze 💜", body: "You now understand what your pelvic floor is, how it works, and how to contract it correctly. That's more than most women ever learn.", highlight: "Consistency is everything — a few minutes daily beats one long session a week." },
];

function EduIllustration({ type, color = "#c084fc" }) {
  const s = { "woman-standing": (
    <svg viewBox="0 0 120 180" width="130" height="195">
      <ellipse cx="60" cy="155" rx="20" ry="12" fill={color+"22"} />
      <rect x="42" y="108" width="36" height="48" rx="10" fill={color+"44"} />
      <rect x="46" y="148" width="10" height="28" rx="5" fill={color+"33"} />
      <rect x="64" y="148" width="10" height="28" rx="5" fill={color+"33"} />
      <rect x="44" y="80" width="32" height="32" rx="8" fill={color+"55"} />
      <rect x="30" y="82" width="10" height="26" rx="5" fill={color+"44"} transform="rotate(8,35,95)" />
      <rect x="80" y="82" width="10" height="26" rx="5" fill={color+"44"} transform="rotate(-8,85,95)" />
      <rect x="55" y="68" width="10" height="14" rx="4" fill={color+"66"} />
      <circle cx="60" cy="56" r="18" fill={color+"77"} />
      <path d="M42,50 Q44,36 60,34 Q76,36 78,50 Q72,42 60,42 Q48,42 42,50Z" fill={color} />
      <circle cx="60" cy="125" r="8" fill={color} opacity="0.3"><animate attributeName="r" values="8;13;8" dur="2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite"/></circle>
      <circle cx="60" cy="125" r="4" fill={color} opacity="0.9"/>
    </svg>
  ), "bowl": (
    <svg viewBox="0 0 180 150" width="200" height="167">
      <ellipse cx="90" cy="60" rx="75" ry="55" fill="none" stroke="#ffffff12" strokeWidth="3"/>
      <path d="M 20,78 Q 25,128 90,138 Q 155,128 160,78" fill={color+"22"} stroke={color} strokeWidth="3" strokeLinecap="round"/>
      <circle cx="58" cy="98" r="16" fill="#60a5fa77"/>
      <circle cx="90" cy="90" r="18" fill="#f472b677"/>
      <circle cx="122" cy="98" r="16" fill="#34d39977"/>
      <text x="58" y="102" textAnchor="middle" fontSize="9" fill="white" opacity="0.9">bladder</text>
      <text x="90" y="94" textAnchor="middle" fontSize="9" fill="white" opacity="0.9">uterus</text>
      <text x="122" y="102" textAnchor="middle" fontSize="9" fill="white" opacity="0.9">bowel</text>
    </svg>
  ), "two-layers": (
    <svg viewBox="0 0 180 170" width="200" height="189">
      <path d="M 20,72 Q 25,128 90,140 Q 155,128 160,72" fill={color+"18"} stroke={color} strokeWidth="3" strokeLinecap="round" strokeDasharray="7,4"/>
      <text x="90" y="154" textAnchor="middle" fontSize="10" fill={color} opacity="0.9">deep layer — lifts ↑</text>
      <ellipse cx="90" cy="78" rx="44" ry="18" fill="none" stroke="#f472b6" strokeWidth="3"/>
      <ellipse cx="90" cy="78" rx="30" ry="11" fill="#f472b611" stroke="#f472b6" strokeWidth="2" strokeDasharray="5,3"/>
      <text x="90" y="56" textAnchor="middle" fontSize="10" fill="#f472b6" opacity="0.9">superficial — closes ●</text>
      <ellipse cx="90" cy="78" rx="11" ry="6" fill="#f472b633"/>
    </svg>
  ), "drawstring": (
    <svg viewBox="0 0 150 170" width="170" height="193">
      <path d="M 35,65 Q 22,138 75,155 Q 128,138 115,65 Z" fill={color+"22"} stroke={color} strokeWidth="2.5"/>
      <path d="M 40,60 Q 75,50 110,60" fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
      <path d="M 50,60 Q 42,34 60,28" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"/>
      <path d="M 100,60 Q 108,34 90,28" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"/>
      <path d="M 50,88 Q 75,100 100,88" fill="none" stroke={color+"55"} strokeWidth="1.5"/>
      <path d="M 44,110 Q 75,122 106,110" fill="none" stroke={color+"44"} strokeWidth="1.5"/>
      <path d="M 42,130 Q 75,142 108,130" fill="none" stroke={color+"33"} strokeWidth="1.5"/>
    </svg>
  ), "flower": (
    <svg viewBox="0 0 150 180" width="170" height="204">
      <line x1="75" y1="178" x2="75" y2="112" stroke="#c084fc" strokeWidth="5" strokeLinecap="round"/>
      <path d="M75,145 Q48,134 44,118 Q58,122 75,145Z" fill="#34d39977"/>
      <path d="M75,145 Q102,134 106,118 Q92,122 75,145Z" fill="#34d39977"/>
      {[0,60,120,180,240,300].map((a,i)=>(
        <ellipse key={i} cx={75+Math.cos(a*Math.PI/180)*34} cy={82+Math.sin(a*Math.PI/180)*34} rx="15" ry="22"
          fill={color+"77"} transform={`rotate(${a+90},${75+Math.cos(a*Math.PI/180)*34},${82+Math.sin(a*Math.PI/180)*34})`}>
          <animate attributeName="rx" values="15;18;15" dur="2.5s" repeatCount="indefinite"/>
        </ellipse>
      ))}
      <circle cx="75" cy="82" r="20" fill={color}/>
      <circle cx="75" cy="82" r="11" fill={color+"cc"}/>
    </svg>
  ), "sitting": (
    <svg viewBox="0 0 170 210" width="180" height="222">
      <rect x="32" y="106" width="94" height="8" rx="3" fill="#4b5563"/>
      <rect x="32" y="114" width="8" height="62" rx="3" fill="#4b5563"/>
      <rect x="118" y="114" width="8" height="62" rx="3" fill="#4b5563"/>
      <rect x="27" y="62" width="8" height="56" rx="3" fill="#4b5563"/>
      <rect x="65" y="106" width="12" height="42" rx="5" fill={color+"55"}/>
      <rect x="85" y="106" width="12" height="42" rx="5" fill={color+"55"}/>
      <ellipse cx="71" cy="150" rx="10" ry="5" fill={color+"44"}/>
      <ellipse cx="91" cy="150" rx="10" ry="5" fill={color+"44"}/>
      <rect x="58" y="66" width="46" height="44" rx="10" fill={color+"55"}/>
      <rect x="38" y="84" width="20" height="10" rx="5" fill={color+"44"}/>
      <rect x="104" y="84" width="20" height="10" rx="5" fill={color+"44"}/>
      <rect x="74" y="52" width="12" height="16" rx="5" fill={color+"77"}/>
      <circle cx="80" cy="38" r="21" fill={color+"88"}/>
      <path d="M59,30 Q62,14 80,12 Q96,14 100,26 Q92,20 80,20 Q64,20 59,30Z" fill={color}/>
      <path d="M98,24 Q108,30 101,40 Q103,32 98,24Z" fill={color}/>
      <circle cx="80" cy="100" r="7" fill={color} opacity="0.5"><animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite"/></circle>
    </svg>
  ), "ready": (
    <svg viewBox="0 0 170 170" width="190" height="190">
      <circle cx="85" cy="85" r="75" fill="none" stroke={color+"22"} strokeWidth="2"/>
      <circle cx="85" cy="85" r="60" fill="none" stroke={color+"33"} strokeWidth="2"/>
      <circle cx="85" cy="85" r="70" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
        strokeDasharray="440" strokeDashoffset="0" transform="rotate(-90 85 85)"
        style={{filter:`drop-shadow(0 0 8px ${color})`}}/>
      <circle cx="85" cy="85" r="48" fill={color+"22"}/>
      <path d="M 58,85 L 76,103 L 115,65" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"
        style={{filter:`drop-shadow(0 0 8px ${color})`}}/>
      {[[22,22],[148,28],[152,140],[18,145]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r="3.5" fill={color} opacity="0.7">
          <animate attributeName="opacity" values="0.7;1;0.7" dur={`${1.2+i*0.3}s`} repeatCount="indefinite"/>
        </circle>
      ))}
    </svg>
  ), "where-is-it": (
    <svg viewBox="0 0 180 200" width="200" height="222">
      {/* Body outline */}
      <ellipse cx="90" cy="60" rx="35" ry="45" fill={color+"22"} stroke={color+"55"} strokeWidth="2"/>
      <rect x="55" y="100" width="70" height="80" rx="15" fill={color+"18"} stroke={color+"44"} strokeWidth="2"/>
      {/* Pelvis highlight */}
      <ellipse cx="90" cy="145" rx="32" ry="25" fill={color+"44"} stroke={color} strokeWidth="3"/>
      {/* Arrow pointing to pelvis */}
      <path d="M 140,100 L 118,140" stroke={color} strokeWidth="3" strokeLinecap="round" markerEnd="url(#arr)"/>
      <defs><marker id="arr" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto"><circle cx="4" cy="4" r="3" fill={color}/></marker></defs>
      <text x="145" y="98" fontSize="12" fill={color} fontWeight="700" fontFamily="Arial">Here</text>
      {/* Spine dots */}
      {[0,1,2,3,4,5].map(i => <circle key={i} cx="90" cy={105+i*14} r="3" fill={color+"66"}/>)}
      {/* Pubic bone */}
      <ellipse cx="90" cy="168" rx="20" ry="8" fill={color+"33"} stroke={color+"66"} strokeWidth="1.5"/>
      <text x="90" y="190" textAnchor="middle" fontSize="10" fill={color} fontFamily="Arial">Pelvic Floor</text>
    </svg>
  ),
  "what-it-does": (
    <svg viewBox="0 0 200 180" width="220" height="198">
      {/* Centre muscle */}
      <ellipse cx="100" cy="90" rx="30" ry="20" fill={color+"44"} stroke={color} strokeWidth="3"/>
      <text x="100" y="94" textAnchor="middle" fontSize="9" fill="#f5f0ff" fontWeight="700" fontFamily="Arial">Pelvic</text>
      <text x="100" y="104" textAnchor="middle" fontSize="9" fill="#f5f0ff" fontWeight="700" fontFamily="Arial">Floor</text>
      {/* 4 functions radiating out */}
      {[
        {x:30, y:30, icon:"💧", line1:"Bladder", line2:"control"},
        {x:170, y:30, icon:"🤸", line1:"Core", line2:"support"},
        {x:30, y:150, icon:"🫀", line1:"Organ", line2:"support"},
        {x:170, y:150, icon:"✨", line1:"Sexual", line2:"function"},
      ].map((item,i) => (
        <g key={i}>
          <line x1={item.x} y1={item.y} x2={100} y2={90} stroke={color+"44"} strokeWidth="1.5" strokeDasharray="4,3"/>
          <circle cx={item.x} cy={item.y} r="22" fill={color+"22"} stroke={color+"55"} strokeWidth="1.5"/>
          <text x={item.x} y={item.y-4} textAnchor="middle" fontSize="14">{item.icon}</text>
          <text x={item.x} y={item.y+10} textAnchor="middle" fontSize="8" fill={color} fontFamily="Arial">{item.line1}</text>
          <text x={item.x} y={item.y+20} textAnchor="middle" fontSize="8" fill={color} fontFamily="Arial">{item.line2}</text>
        </g>
      ))}
    </svg>
  ),
  "organs": (
    <svg viewBox="0 0 180 160" width="200" height="178">
      {/* Hammock */}
      <path d="M 20,90 Q 25,138 90,148 Q 155,138 160,90" fill={color+"18"} stroke={color} strokeWidth="3" strokeLinecap="round"/>
      {/* Bladder */}
      <ellipse cx="58" cy="78" rx="22" ry="18" fill="#60a5fa88" stroke="#60a5fa" strokeWidth="2"/>
      <text x="58" y="75" textAnchor="middle" fontSize="9" fill="white" fontFamily="Arial" fontWeight="700">Bladder</text>
      <text x="58" y="86" textAnchor="middle" fontSize="8" fill="white" fontFamily="Arial">💧</text>
      {/* Uterus */}
      <ellipse cx="90" cy="72" rx="20" ry="18" fill={color+"88"} stroke={color} strokeWidth="2"/>
      <text x="90" y="69" textAnchor="middle" fontSize="9" fill="white" fontFamily="Arial" fontWeight="700">Uterus</text>
      <text x="90" y="80" textAnchor="middle" fontSize="8" fill="white" fontFamily="Arial">🌸</text>
      {/* Bowel */}
      <ellipse cx="122" cy="78" rx="22" ry="18" fill="#a78bfa88" stroke="#c084fc" strokeWidth="2"/>
      <text x="122" y="75" textAnchor="middle" fontSize="9" fill="white" fontFamily="Arial" fontWeight="700">Bowel</text>
      <text x="122" y="86" textAnchor="middle" fontSize="8" fill="white" fontFamily="Arial">🫁</text>
    </svg>
  ),
  "when-weak": (
    <svg viewBox="0 0 200 180" width="220" height="198">
      {/* Warning circle */}
      <circle cx="100" cy="70" r="50" fill="#f59e0b18" stroke="#f59e0b" strokeWidth="3"/>
      <text x="100" y="60" textAnchor="middle" fontSize="28">⚠️</text>
      <text x="100" y="85" textAnchor="middle" fontSize="11" fill="#f59e0b" fontFamily="Arial" fontWeight="700">Signs of weakness</text>
      {/* Symptoms */}
      {["💧 Leaking on cough/sneeze", "🏃 Rushing to the toilet", "⬇️ Heaviness 'down below'", "😔 Reduced sensation"].map((s,i) => (
        <g key={i}>
          <rect x="10" y={108+i*18} width="180" height="15" rx="7" fill={color+"18"} stroke={color+"33"} strokeWidth="1"/>
          <text x="20" y={119+i*18} fontSize="9" fill="#f5f0ff" fontFamily="Arial">{s}</text>
        </g>
      ))}
    </svg>
  )
  };
  return s[type] || s["ready"];
}

// ─── ANIMATED TECHNIQUE VIDEOS (Learn → Videos) ──────────────────────────────
const VIDEO_LESSONS = [
  {
    id: "squeeze", emoji: "🎬", title: "The Squeeze & Lift",
    desc: "Simple animated guide to finding and working your pelvic floor",
    breathGuide: false,
    phases: [
      { key: "ready",     dur: 10, title: "Get ready",               caption: "Sit in a comfy position. Check three things: soft legs, soft tummy, soft bottom. Keep your body still — it should not move up and down.", say: "Sit in a comfy position. Check three things. Soft legs. Soft tummy. Soft bottom. Keep your body still.", t: { a: 0, f: 0, l: 0 } },
      { key: "back",      dur: 10, title: "1 · Start at the back",   caption: "Keep everything else relaxed. Now gently tighten the muscle around your bottom — like when you hold in gas.", say: "Keep everything else relaxed. Now gently tighten the muscle around your bottom. Like when you hold in gas.", t: { a: 1, f: 0, l: 0.15 } },
      { key: "escalator", dur: 12, title: "2 · Move it up and forward", caption: "Now move that tight feeling up and forward, toward your bladder — like slowly closing a zip from the back to the front.", say: "Now move that tight feeling up and forward, toward your bladder. Like slowly closing a zip, from the back to the front.", t: { a: 1, f: 0.7, l: 0.8 } },
      { key: "front",     dur: 15, title: "3 · It's all one muscle", caption: "Starting at the back wakes up the whole muscle. The front joins in by itself. You can add a tiny extra squeeze at the front if you want.", say: "Starting at the back wakes up the whole muscle. The front joins in by itself. You can add a tiny extra squeeze at the front if you want.", t: { a: 1, f: 1, l: 1 } },
      { key: "check",     dur: 15, title: "Quick check",             caption: "Put a hand on your bottom — it should feel soft. If your legs or bottom went tight, that's the wrong muscles. Let go, use less effort, and try again.", say: "Put a hand on your bottom. It should feel soft. If your legs or bottom went tight, that's the wrong muscles. Let go, use less effort, and try again.", t: { a: 1, f: 1, l: 1 } },
      { key: "hold",      dur: 7, title: "4 · Hold it",             caption: "Now hold the squeeze. Keep it gentle and steady — don't let it fade away.", say: "Now hold the squeeze. Keep it gentle and steady.", t: { a: 1, f: 1, l: 1 } },
      { key: "release",   dur: 9, title: "5 · Let go slowly",       caption: "Now let go, nice and slowly. Feel the muscles drop back down and relax.", say: "Now let go, nice and slowly. Feel the muscles drop back down and relax.", t: { a: 0, f: 0, l: 0 } },
      { key: "rest",      dur: 7, title: "Rest",                    caption: "Letting go fully is just as important as the squeeze.", say: "Letting go fully is just as important as the squeeze.", t: { a: 0, f: 0, l: 0 } },
    ],
  },
  {
    id: "breathing", emoji: "🌬️", title: "Breathing With Your Pelvic Floor",
    desc: "How your breath helps your pelvic floor — made simple",
    breathGuide: true,
    phases: [
      { key: "why",    dur: 15, title: "They work as a team",        caption: "Your breathing and your pelvic floor work as a team. When you breathe in, the pelvic floor relaxes. When you breathe out, it finds it easier to lift.", say: "Your breathing and your pelvic floor work as a team. When you breathe in, the pelvic floor relaxes. When you breathe out, it finds it easier to lift.", t: { a: 0, f: 0, l: 0 } },
      { key: "inhale", dur: 14, title: "1 · Breathe in — relax",     caption: "Breathe in slowly through your nose. Let your tummy and lower ribs get bigger, like a balloon filling up. Your chest stays still. Down below, your pelvic floor softens and relaxes.", say: "Breathe in slowly through your nose. Let your tummy get bigger, like a balloon filling up. Your chest stays still. Down below, your pelvic floor relaxes.", t: { a: 0, f: 0, l: 0 }, breath: "in" },
      { key: "exhale", dur: 13, title: "2 · Breathe out — lift",     caption: "Breathe out slowly through your mouth. As the air goes out, squeeze and lift your pelvic floor. The out-breath makes the lift easier.", say: "Breathe out slowly through your mouth. As the air goes out, squeeze and lift your pelvic floor. The out breath makes the lift easier.", t: { a: 1, f: 1, l: 1 }, breath: "out" },
      { key: "hold",   dur: 10, title: "3 · Hold and keep breathing", caption: "Keep the lift, and keep breathing — in… and out… The squeeze stays still. The breath keeps moving.", say: "Keep the lift, and keep breathing. In... and out. The squeeze stays still. The breath keeps moving.", t: { a: 1, f: 1, l: 1 } },
      { key: "rel",    dur: 7, title: "4 · Breathe in and let go",  caption: "On your next breath in, slowly let all the muscles go. Breathing in helps the muscles relax — this is also great if your pelvic floor feels too tight.", say: "On your next breath in, slowly let all the muscles go.", t: { a: 0, f: 0, l: 0 }, breath: "in" },
      { key: "rest",   dur: 7, title: "Rest",                       caption: "Easy rule: breathe out to lift. Breathe in to let go.", say: "Easy rule. Breathe out to lift. Breathe in to let go.", t: { a: 0, f: 0, l: 0 } },
    ],
  },
];

function TechniqueVideoPlayer({ lesson, color, onBack }) {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [voiceOn, setVoiceOn] = useState(true);
  const [anim, setAnim] = useState({ a: 0, f: 0, l: 0 });
  const [view, setView] = useState("side"); // "side" (sagittal) or "below" (inferior)
  const lastPhaseRef = useRef(-1);
  const animRef = useRef({ a: 0, f: 0, l: 0 });

  const totalDur = lesson.phases.reduce((s, p) => s + p.dur, 0);

  // Work out current phase from elapsed time
  let acc = 0, phaseIndex = 0, phaseStart = 0;
  for (let i = 0; i < lesson.phases.length; i++) {
    if (elapsed < acc + lesson.phases[i].dur) { phaseIndex = i; phaseStart = acc; break; }
    acc += lesson.phases[i].dur;
    phaseIndex = i; phaseStart = acc - lesson.phases[i].dur;
  }
  const phase = lesson.phases[phaseIndex];
  const phaseProg = Math.min(1, Math.max(0, (elapsed - phaseStart) / phase.dur));

  // Timer loop
  useEffect(() => {
    if (!playing) return;
    const iv = setInterval(() => {
      setElapsed(e => {
        const next = e + 0.1;
        return next >= totalDur ? 0 : next; // loop
      });
    }, 100);
    return () => clearInterval(iv);
  }, [playing, totalDur, lesson]);

  // Smooth easing toward phase targets
  useEffect(() => {
    if (!playing) return;
    const iv = setInterval(() => {
      const t = phase.t;
      const cur = animRef.current;
      const nxt = {
        a: cur.a + (t.a - cur.a) * 0.12,
        f: cur.f + (t.f - cur.f) * 0.12,
        l: cur.l + (t.l - cur.l) * 0.10,
      };
      animRef.current = nxt;
      setAnim(nxt);
    }, 80);
    return () => clearInterval(iv);
  }, [playing, phase]);

  // Narration on phase change — speaks a concise line; fuller detail stays on screen as caption
  useEffect(() => {
    if (!playing) return;
    if (lastPhaseRef.current !== phaseIndex) {
      lastPhaseRef.current = phaseIndex;
      const toRead = phase.say || phase.caption;
      if (voiceOn && toRead) { try { speak(toRead, loadVoiceStyle()); } catch (e) {} }
    }
  }, [phaseIndex, playing, voiceOn, phase]);

  useEffect(() => () => { try { stopSpeech(); } catch (e) {} }, []);

  // Breathing: follows the script when a phase specifies it, otherwise a free 8s cycle
  let breathIn, breath;
  if (phase.breath === "in") { breathIn = true; breath = phaseProg; }
  else if (phase.breath === "out") { breathIn = false; breath = 1 - phaseProg; }
  else {
    const breathT = (elapsed % 8) / 8;
    breathIn = breathT < 0.5;
    breath = breathIn ? (breathT * 2) : (1 - (breathT - 0.5) * 2); // 0→1→0
  }
  const breathR = 16 + breath * 10;

  // Derived anatomy values for the sagittal (side) cross-section.
  // anim.a = back/anal contraction, anim.f = front lift, anim.l = overall lift, muscleGlow = activation
  const muscleGlow = Math.max(anim.a, anim.f, anim.l);
  const lift = anim.l;                    // 0 = relaxed, 1 = fully lifted
  const muscleColor = muscleGlow > 0.45 ? "#f472b6" : "#8b6db5";
  const mThick = 7 + 7 * muscleGlow;      // muscle thickens as it works
  // The pelvic floor hammock: as it lifts, the lowest point rises and the curve tightens
  const sag = 34 - 26 * lift;             // how far the hammock sags below its anchors
  const hammockMidY = 196 - 24 * lift;    // midpoint rises with the lift
  const organShift = -14 * lift;          // bladder/uterus/rectum drawn upward as floor lifts
  const perineumY = 210 - 20 * lift;      // perineal body rises
  const breathDrop = breathIn ? 10 * (1 - lift) : 0; // diaphragm/organ descent on in-breath when relaxed

  // Inferior (from-below) view values — which openings tighten
  const liftY = -8 * anim.l;
  const urethraR = 6 - 2 * anim.f;
  const vagRx = 15 - 6 * anim.f, vagRy = 21 - 7 * anim.f;
  const anusR = 11 - 4.5 * anim.a;
  const infMuscleColor = muscleGlow > 0.45 ? "#f472b6" : "#7c5ca8";

  function togglePlay() {
    if (!playing) { try { if (typeof primeSpeech === "function") primeSpeech(); } catch (e) {} }
    setPlaying(p => !p);
  }
  function restart() { try { stopSpeech(); } catch(e){} setElapsed(0); lastPhaseRef.current = -1; }

  // Jump to an absolute time (used by the draggable progress bar)
  function seekTo(t) {
    try { stopSpeech(); } catch(e){}
    const clamped = Math.max(0, Math.min(totalDur - 0.05, t));
    lastPhaseRef.current = -1;   // let narration re-trigger for the landed phase
    setElapsed(clamped);
  }
  // Jump to the start of a specific phase (chapter)
  function goToPhase(i) {
    let start = 0;
    for (let k = 0; k < i; k++) start += lesson.phases[k].dur;
    seekTo(start);
  }
  function prevStep() { goToPhase(Math.max(0, phaseIndex - 1)); }
  function nextStep() { goToPhase(Math.min(lesson.phases.length - 1, phaseIndex + 1)); }

  // Handle drag/tap on the progress bar
  const barRef = useRef(null);
  function seekFromClientX(clientX) {
    const el = barRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    seekTo(frac * totalDur);
  }
  function onBarDown(e) {
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    seekFromClientX(x);
    const move = (ev) => { const mx = ev.touches ? ev.touches[0].clientX : ev.clientX; seekFromClientX(mx); };
    const up = () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up);
    };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: true }); window.addEventListener("touchend", up);
  }

  return (
    <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column", padding: "8px 20px 40px" }}>
      <button onClick={() => { try { stopSpeech(); } catch (e) {} onBack(); }} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 14, cursor: "pointer", textAlign: "left", padding: "4px 0 10px" }}>‹ All videos</button>

      {/* Phase title */}
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 17, fontWeight: 900, color: "#f5f0ff" }}>{phase.title}</div>
      </div>

      {/* View toggle: side (sagittal) vs below (inferior) */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        {[{ id: "side", label: "◧ Side view" }, { id: "below", label: "◉ From below" }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={{
            flex: 1, background: view === v.id ? color + "22" : "transparent",
            border: `1px solid ${view === v.id ? color : "#3d2a5e"}`,
            borderRadius: 10, padding: "7px 0", color: view === v.id ? color : "#9ca3af",
            fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.2s ease"
          }}>{v.label}</button>
        ))}
      </div>

      {/* Animated anatomy — sagittal (side) cross-section */}
      <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", border: "1px solid #3d2a5e", borderRadius: 20, padding: "10px 6px 4px", boxShadow: "0 8px 30px #00000055" }}>
        {view === "side" ? (
        <svg viewBox="0 0 300 250" width="100%" height="230" style={{ display: "block" }}>
          <defs>
            <radialGradient id="bladderGrad" cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor="#fcd34d" /><stop offset="100%" stopColor="#d99e2b" />
            </radialGradient>
            <radialGradient id="uterusGrad" cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor="#f9a8d4" /><stop offset="100%" stopColor="#db2777" />
            </radialGradient>
            <linearGradient id="muscleGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={muscleColor} /><stop offset="50%" stopColor="#f472b6" /><stop offset="100%" stopColor={muscleColor} />
            </linearGradient>
          </defs>

          <text x="42" y="20" textAnchor="middle" fontSize="8" fill="#6b7280">FRONT</text>
          <text x="262" y="20" textAnchor="middle" fontSize="8" fill="#6b7280">BACK</text>

          {/* Pubic bone (front) */}
          <ellipse cx="52" cy="150" rx="13" ry="20" fill="#e5e0ea" opacity="0.85" />
          <text x="40" y="152" textAnchor="end" fontSize="8" fill="#9ca3af">Pubic</text>
          <text x="40" y="161" textAnchor="end" fontSize="8" fill="#9ca3af">bone</text>

          {/* Sacrum / tailbone (back) */}
          <path d="M 258 96 Q 276 150, 250 200 Q 244 176, 246 150 Q 246 120, 258 96 Z" fill="#e5e0ea" opacity="0.8" />
          <text x="270" y="150" textAnchor="start" fontSize="8" fill="#9ca3af">Spine</text>

          {/* Organs — shift upward as the floor lifts, gently descend on relaxed in-breath */}
          <g style={{ transition: "transform 0.25s ease" }} transform={`translate(0 ${organShift + breathDrop})`}>
            {/* Bladder */}
            <path d="M 78 150 Q 74 120, 104 116 Q 132 118, 128 150 Q 126 172, 100 172 Q 80 172, 78 150 Z" fill="url(#bladderGrad)" opacity="0.92" />
            <text x="103" y="146" textAnchor="middle" fontSize="8.5" fill="#3b2a00" fontWeight="700">Bladder</text>
            {/* Urethra from bladder down through the floor */}
            <path d={`M 88 172 Q 80 190, 74 ${perineumY - 4}`} stroke="#c084fc" strokeWidth="4" fill="none" strokeLinecap="round" />

            {/* Uterus */}
            <path d="M 150 132 Q 168 120, 178 138 Q 184 152, 168 160 Q 150 164, 146 150 Q 144 138, 150 132 Z" fill="url(#uterusGrad)" opacity="0.9" />
            <text x="164" y="142" textAnchor="middle" fontSize="8" fill="#4a0824" fontWeight="700">Uterus</text>
            {/* Vaginal canal */}
            <path d={`M 150 160 Q 140 184, 120 ${perineumY - 2}`} stroke="#f472b6" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.85" />

            {/* Rectum (back) */}
            <path d="M 214 130 Q 228 128, 226 152 Q 224 178, 210 186 Q 200 188, 202 168 Q 204 146, 214 130 Z" fill="#b98a6a" opacity="0.85" />
            <text x="216" y="150" textAnchor="middle" fontSize="8" fill="#3a2416" fontWeight="700">Rectum</text>
            {/* Anal canal */}
            <path d={`M 206 186 Q 202 198, 196 ${perineumY}`} stroke="#60a5fa" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.8" />
          </g>

          {/* ── PELVIC FLOOR MUSCLE HAMMOCK — the star of the animation ── */}
          {/* Anchored front (pubic bone) to back (tailbone); sags when relaxed, tightens & lifts when contracted */}
          <path
            d={`M 60 ${162} Q ${150} ${hammockMidY + sag}, 248 ${168}`}
            fill="none" stroke="url(#muscleGrad)" strokeWidth={mThick} strokeLinecap="round"
            opacity={0.7 + 0.3 * muscleGlow}
            style={{ transition: "d 0.08s linear, stroke-width 0.15s ease" }}
          />
          {/* Inner muscle highlight */}
          <path
            d={`M 66 ${162} Q ${150} ${hammockMidY + sag - 3}, 242 ${168}`}
            fill="none" stroke="#ffffff" strokeWidth={Math.max(1, mThick * 0.28)} strokeLinecap="round"
            opacity={0.25 + 0.35 * muscleGlow}
            style={{ transition: "d 0.08s linear" }}
          />

          {/* Contraction glow when active */}
          {muscleGlow > 0.4 && (
            <path d={`M 60 162 Q 150 ${hammockMidY + sag}, 248 168`} fill="none" stroke="#f472b6"
              strokeWidth={mThick + 8} strokeLinecap="round" opacity={(muscleGlow - 0.4) * 0.4}
              style={{ filter: "blur(4px)" }} />
          )}

          {/* Upward lift arrows appear as the muscle engages */}
          {lift > 0.35 && [90, 150, 205].map((x, i) => (
            <g key={i} opacity={(lift - 0.35) * 1.5} style={{ transition: "opacity 0.3s ease" }}>
              <line x1={x} y1={196 - 24 * lift + 14} x2={x} y2={196 - 24 * lift - 4} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
              <path d={`M ${x - 4} ${196 - 24 * lift + 2} L ${x} ${196 - 24 * lift - 6} L ${x + 4} ${196 - 24 * lift + 2}`} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          ))}

          {/* Muscle label */}
          <text x="150" y="240" textAnchor="middle" fontSize="9.5" fill={muscleColor} fontWeight="800">Pelvic floor muscles</text>

          {/* Lift gauge */}
          <rect x="284" y="150" width="6" height="60" rx="3" fill="#ffffff12" />
          <rect x="284" y={150 + 54 - 54 * lift} width="6" height={6 + 54 * lift} rx="3" fill={color} opacity="0.9" style={{ transition: "all 0.15s ease" }} />
        </svg>
        ) : (
        <svg viewBox="0 0 300 250" width="100%" height="230" style={{ display: "block" }}>
          {/* Body outline — pelvic opening seen from below */}
          <ellipse cx="150" cy="128" rx="118" ry="112" fill="#241640" stroke="#3d2a5e" strokeWidth="2" />
          <text x="150" y="26" textAnchor="middle" fontSize="9" fill="#6b7280">FRONT (pubic bone)</text>
          <text x="150" y="243" textAnchor="middle" fontSize="9" fill="#6b7280">BACK (tailbone)</text>

          {/* Pelvic floor muscle sling (levator ani) — tightens inward as it works */}
          <g style={{ transition: "transform 0.3s ease" }} transform={`translate(0 ${liftY})`}>
            <path d={`M 74 60 C 52 128, 78 196, 150 212 C 222 196, 248 128, 226 60`}
              fill="none" stroke={infMuscleColor} strokeWidth={13 + 4 * muscleGlow} strokeLinecap="round"
              opacity={0.55 + 0.45 * muscleGlow} />
            <path d={`M 96 74 C 82 128, 100 182, 150 194 C 200 182, 218 128, 204 74`}
              fill="none" stroke={infMuscleColor} strokeWidth={9 + 3 * muscleGlow} strokeLinecap="round"
              opacity={0.4 + 0.5 * muscleGlow} />

            {/* Urethra */}
            <circle cx="150" cy="76" r={urethraR} fill="#0d0820" stroke="#c084fc" strokeWidth="2.5" style={{ transition: "r 0.15s ease" }} />
            {/* Vagina */}
            <ellipse cx="150" cy="126" rx={vagRx} ry={vagRy} fill="#0d0820" stroke="#f472b6" strokeWidth="2.5" style={{ transition: "all 0.15s ease" }} />
            {/* Anus */}
            <circle cx="150" cy="186" r={anusR} fill="#0d0820" stroke="#60a5fa" strokeWidth="2.5" style={{ transition: "r 0.15s ease" }} />
          </g>

          {/* Labels */}
          <text x="52" y="72" textAnchor="end" fontSize="10" fill="#c084fc" fontWeight="700">Urethra</text>
          <line x1="56" y1="74" x2={140 - urethraR} y2={76 + liftY} stroke="#c084fc55" strokeWidth="1" />
          <text x="48" y="128" textAnchor="end" fontSize="10" fill="#f472b6" fontWeight="700">Vagina</text>
          <line x1="52" y1="126" x2={148 - vagRx - 2} y2={126 + liftY} stroke="#f472b655" strokeWidth="1" />
          <text x="56" y="192" textAnchor="end" fontSize="10" fill="#60a5fa" fontWeight="700">Anus</text>
          <line x1="60" y1="188" x2={148 - anusR - 2} y2={186 + liftY} stroke="#60a5fa55" strokeWidth="1" />
          <text x="252" y="120" textAnchor="start" fontSize="9" fill="#9ca3af">Pelvic floor</text>
          <text x="252" y="131" textAnchor="start" fontSize="9" fill="#9ca3af">muscles</text>

          {/* Lift/tighten indicator */}
          <rect x="272" y="150" width="6" height="60" rx="3" fill="#ffffff12" />
          <rect x="272" y={150 + 48 - 48 * anim.l} width="6" height={12 + 48 * anim.l} rx="3" fill={color} opacity="0.9" style={{ transition: "all 0.15s ease" }} />
          <text x="275" y="222" textAnchor="middle" fontSize="8" fill="#9ca3af">TONE</text>
        </svg>
        )}

        {/* Breathing guide — only on lessons that teach breathing */}
        {lesson.breathGuide && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "2px 14px 12px" }}>
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r={breathR} fill={color + "33"} stroke={color} strokeWidth="2" style={{ transition: "r 0.1s linear" }} />
            </svg>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#f5f0ff" }}>{breathIn ? "Breathe in…" : "Breathe out…"}</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>Keep the breath moving — never hold it</div>
            </div>
          </div>
        )}
      </div>

      {/* Caption */}
      <div style={{ background: color + "14", border: `1px solid ${color}33`, borderRadius: 14, padding: "12px 16px", marginTop: 14, minHeight: 64 }}>
        <p style={{ fontSize: 13.5, color: "#e9e2f7", lineHeight: 1.65, margin: 0 }}>{phase.caption}</p>
      </div>

      {/* Chapter dots — tap any step to jump there */}
      <div style={{ display: "flex", gap: 5, marginTop: 14, marginBottom: 8 }}>
        {lesson.phases.map((p, i) => (
          <button key={i} onClick={() => goToPhase(i)} title={p.title} style={{
            flex: 1, height: 8, borderRadius: 4, border: "none", cursor: "pointer", padding: 0,
            background: i === phaseIndex ? color : (i < phaseIndex ? color + "88" : "#ffffff1a"),
            transition: "background 0.2s ease"
          }} />
        ))}
      </div>

      {/* Draggable scrubber (YouTube-style) */}
      <div
        ref={barRef}
        onMouseDown={onBarDown}
        onTouchStart={onBarDown}
        style={{ height: 22, display: "flex", alignItems: "center", cursor: "pointer", touchAction: "none", marginBottom: 4 }}
      >
        <div style={{ position: "relative", height: 6, width: "100%", background: "#ffffff1a", borderRadius: 3 }}>
          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${(elapsed / totalDur) * 100}%`, background: color, borderRadius: 3, transition: "width 0.1s linear" }} />
          {/* Draggable knob */}
          <div style={{ position: "absolute", top: "50%", left: `${(elapsed / totalDur) * 100}%`, transform: "translate(-50%, -50%)", width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: `0 0 0 3px ${color}`, transition: "left 0.1s linear" }} />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9ca3af", marginBottom: 12 }}>
        <span>Step {phaseIndex + 1} of {lesson.phases.length}</span>
        <span>{Math.floor(elapsed)}s / {totalDur}s</span>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
        <button onClick={prevStep} title="Previous step" style={{ background: "transparent", border: "1px solid #3d2a5e", borderRadius: 14, padding: "14px 16px", color: "#c4b5d4", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>⏮</button>
        <button onClick={togglePlay} style={{
          flex: 1, background: `linear-gradient(135deg, ${color}cc, ${color})`, color: "#fff", border: "none",
          borderRadius: 14, padding: "14px", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: `0 6px 20px ${color}44`
        }}>{playing ? "⏸ Pause" : "▶ Play"}</button>
        <button onClick={nextStep} title="Next step" style={{ background: "transparent", border: "1px solid #3d2a5e", borderRadius: 14, padding: "14px 16px", color: "#c4b5d4", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>⏭</button>
        <button onClick={() => { setVoiceOn(v => !v); try { stopSpeech(); } catch (e) {} }} title="Voice on/off" style={{ background: voiceOn ? color + "22" : "transparent", border: `1px solid ${voiceOn ? color : "#3d2a5e"}`, borderRadius: 14, padding: "14px 16px", color: voiceOn ? color : "#6b7280", fontSize: 15, cursor: "pointer" }}>{voiceOn ? "🔊" : "🔇"}</button>
      </div>
      <button onClick={restart} style={{ width: "100%", marginTop: 8, background: "transparent", border: "none", color: "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 6 }}>↺ Start from beginning</button>
    </div>
  );
}

function EvidenceTab({ color }) {
  const cardStyle = {
    background: "linear-gradient(135deg, #1a1030, #110820)", border: "1px solid #3d2a5e",
    borderRadius: 16, padding: "16px", marginBottom: 12, boxShadow: "0 4px 16px #00000044"
  };
  const src = (title, body) => (
    <div style={cardStyle}>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: "#f5f0ff", marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "#c4b5d4", lineHeight: 1.6 }}>{body}</div>
    </div>
  );
  return (
    <div style={{ position: "relative", zIndex: 1, flex: 1, padding: "10px 22px 40px", overflowY: "auto" }}>
      <h2 style={{ fontSize: 20, fontWeight: 900, margin: "6px 0 4px" }}>UK guidance</h2>
      <p style={{ fontSize: 12.5, color: "#9ca3af", lineHeight: 1.6, margin: "0 0 16px" }}>
        For your reference, this is the published guidance available in the UK on pelvic floor exercises. It is provided for general information only. CoreDoc is a fitness and education app and does not promise any particular outcome; it is not a substitute for a supervised programme or professional assessment.
      </p>

      {src("NICE NG123", "Urinary incontinence and pelvic organ prolapse in women: management. Recommends pelvic floor muscle training programmes of at least 8 contractions, 3 times a day. For stress or mixed urinary incontinence, a programme of at least 3 months is advised.")}
      {src("NICE NG210", "Pelvic floor dysfunction: prevention and non-surgical management (2021). Covers prevention and non-surgical care, including pelvic floor muscle training for strength, endurance, power and relaxation, and training during and after pregnancy.")}
      {src("Prolapse guidance (NICE)", "For milder, symptomatic prolapse, a supervised programme of at least 4 months may be considered. More advanced symptoms should be assessed by a professional — which is why CoreDoc encourages a check-up when certain symptoms are reported.")}
      {src("POGP", "Pelvic, Obstetric & Gynaecological Physiotherapy — the UK professional network whose patient guidance informs best-practice pelvic floor exercise technique and progression.")}

      <div style={{ ...cardStyle, background: color + "12", border: `1px solid ${color}33` }}>
        <div style={{ fontSize: 12.5, color: "#e9e2f7", lineHeight: 1.6 }}>
          NICE describes <strong>supervised</strong> training, assessed by a physiotherapist. CoreDoc offers general self-guided fitness exercises, and encourages you to see a GP or pelvic health physiotherapist for assessment — especially if you have pain or aren't sure you're exercising the right muscles.
        </div>
      </div>

      <p style={{ fontSize: 10.5, color: "#6b7280", lineHeight: 1.6, marginTop: 6 }}>
        Guideline references are provided for general information. Always follow advice given to you personally by your own clinician. Guidance is periodically updated; see nice.org.uk and thepogp.co.uk for the latest versions.
      </p>
    </div>
  );
}

function VideosTab({ color }) {
  const [active, setActive] = useState(null);
  if (active) {
    return <TechniqueVideoPlayer lesson={active} color={color} onBack={() => setActive(null)} />;
  }
  return (
    <div style={{ position: "relative", zIndex: 1, flex: 1, padding: "10px 22px 40px" }}>
      <h2 style={{ fontSize: 21, fontWeight: 900, margin: "6px 0 4px" }}>Technique videos</h2>
      <p style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.6, margin: "0 0 18px" }}>Animated lessons showing exactly how to find and work your pelvic floor.</p>
      {VIDEO_LESSONS.map(lesson => (
        <button key={lesson.id} onClick={() => setActive(lesson)} className="card-hover" style={{
          display: "flex", alignItems: "center", gap: 14, width: "100%", textAlign: "left",
          background: "linear-gradient(135deg, #1a1030, #110820)", border: "1px solid #3d2a5e",
          borderRadius: 16, padding: "16px", marginBottom: 12, cursor: "pointer", boxShadow: "0 4px 16px #00000044"
        }}>
          <div style={{ fontSize: 30 }}>{lesson.emoji}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#f5f0ff", marginBottom: 3 }}>{lesson.title}</div>
            <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.5 }}>{lesson.desc}</div>
          </div>
          <div style={{ fontSize: 20, color: color }}>▶</div>
        </button>
      ))}
      <p style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.6, marginTop: 10 }}>Schematic diagrams for education only. If you feel pain, or can't feel a lift at all, see a pelvic health physiotherapist.</p>
    </div>
  );
}

function EducationModule({ onComplete, onSkip }) {
  const [tab, setTab] = useState("guide");
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const total = EDU_SLIDES.length;
  const slide = EDU_SLIDES[step];
  const colors = ["#c084fc","#f472b6","#60a5fa","#c084fc","#f472b6","#60a5fa","#c084fc","#f472b6","#60a5fa","#c084fc","#c084fc"];
  const color = colors[step];

  function goNext() {
    if (step + 1 >= total) { onComplete(); return; }
    setDir(1); setStep(s => s + 1);
  }
  function goPrev() { if (step > 0) { setDir(-1); setStep(s => s - 1); } }

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#15102a", color: "#f5f0ff", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", maxWidth: 420, margin: "0 auto", display: "flex", flexDirection: "column" }}>
      <OrbBackground color1={color} color2={colors[(step+2)%colors.length]} color3="#15102a"/>

      {/* Header */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "52px 24px 8px" }}>
        <button onClick={goPrev} style={{ background: "none", border: "none", color: step > 0 ? "#9ca3af" : "transparent", fontSize: 24, cursor: "pointer", padding: 4, lineHeight: 1 }}>‹</button>
        <div style={{ display: "flex", gap: 5 }}>
          {EDU_SLIDES.map((_,i) => (
            <div key={i} style={{ width: i===step?22:6, height: 6, borderRadius: 3, background: i===step?color:i<step?color+"66":"#ffffff22", transition: "all 0.3s ease", boxShadow: i===step?`0 0 6px ${color}`:"none" }}/>
          ))}
        </div>
        <button onClick={onSkip} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 13, cursor: "pointer" }}>Skip ✕</button>
      </div>

      {/* Guide / Videos tabs */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", gap: 8, padding: "10px 24px 2px" }}>
        {[{ id: "guide", label: "📖 Guide" }, { id: "videos", label: "🎬 Videos" }, { id: "evidence", label: "📚 Guidance" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, background: tab === t.id ? color + "22" : "transparent",
            border: `1px solid ${tab === t.id ? color : "#3d2a5e"}`,
            borderRadius: 12, padding: "9px 0", color: tab === t.id ? color : "#9ca3af",
            fontSize: 12.5, fontWeight: 700, cursor: "pointer", transition: "all 0.2s ease"
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "videos" ? (
        <VideosTab color={color} />
      ) : tab === "evidence" ? (
        <EvidenceTab color={color} />
      ) : (<>

      {/* Content */}
      <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "16px 28px 0" }}>
        <div style={{ background: `radial-gradient(circle, ${color}18 0%, transparent 70%)`, borderRadius: "50%", padding: 20, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <EduIllustration type={slide.illustration} color={color}/>
        </div>
        <div style={{ textAlign: "center", maxWidth: 340 }}>
          <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 12, lineHeight: 1.3 }}>{slide.title}</h2>
          <p style={{ fontSize: 14, color: "#c4b5d4", lineHeight: 1.7, marginBottom: slide.highlight ? 14 : 0 }}>{slide.body}</p>
          {slide.highlight && (
            <div style={{ background: color+"15", border: `1px solid ${color}44`, borderRadius: 14, padding: "14px 16px" }}>
              <p style={{ fontSize: 13, color: "#f5f0ff", lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>"{slide.highlight}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Next button */}
      <div style={{ position: "relative", zIndex: 1, padding: "24px 24px 48px" }}>
        <button onClick={goNext} className="btn-primary" style={{
          width: "100%", background: `linear-gradient(135deg, ${color}cc, ${color})`,
          color: "#fff", border: "none", borderRadius: 16, padding: "18px",
          fontSize: 16, fontWeight: 700, cursor: "pointer",
          boxShadow: `0 8px 24px ${color}44`
        }}>{step + 1 >= total ? "Start exercising →" : "Next →"}</button>
      </div>

      </>)}
    </div>
  );
}

function createSoundEngine() {
  let ctx = null;
  function getCtx() {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  }

  function playTone({ freq = 440, freq2 = null, type = "sine", gain = 0.18, attack = 0.01, decay = 0.1, sustain = 0.6, release = 0.3, duration = 0.8 }) {
    try {
      const c = getCtx();
      const osc = c.createOscillator();
      const gainNode = c.createGain();
      const now = c.currentTime;
      osc.connect(gainNode);
      gainNode.connect(c.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, now);
      if (freq2) osc.frequency.exponentialRampToValueAtTime(freq2, now + duration * 0.5);
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(gain, now + attack);
      gainNode.gain.linearRampToValueAtTime(gain * sustain, now + attack + decay);
      gainNode.gain.linearRampToValueAtTime(0, now + duration + release);
      osc.start(now);
      osc.stop(now + duration + release + 0.1);
    } catch {}
  }

  function playChord(freqs, opts = {}) {
    freqs.forEach((f, i) => setTimeout(() => playTone({ freq: f, ...opts }), i * 60));
  }

  function suspend() {
    try { if (ctx && ctx.state === "running") ctx.suspend(); } catch {}
  }

  function resume() {
    try { if (ctx && ctx.state === "suspended") ctx.resume(); } catch {}
  }

  return {
    squeeze: () => playTone({ freq: 523, freq2: 659, type: "sine", gain: 0.14, attack: 0.02, duration: 0.5, release: 0.2 }),
    hold: () => playTone({ freq: 392, type: "triangle", gain: 0.1, attack: 0.05, decay: 0.2, sustain: 0.4, duration: 0.6, release: 0.3 }),
    release: () => playTone({ freq: 659, freq2: 523, type: "sine", gain: 0.12, attack: 0.01, duration: 0.4, release: 0.3 }),
    rest: () => playTone({ freq: 330, type: "sine", gain: 0.08, attack: 0.05, duration: 0.3, release: 0.4 }),
    tick: () => playTone({ freq: 880, type: "sine", gain: 0.06, attack: 0.005, decay: 0.05, sustain: 0.1, duration: 0.08, release: 0.05 }),
    complete: () => { playChord([523, 659, 784], { type: "sine", gain: 0.16, attack: 0.02, duration: 0.6, release: 0.5 }); setTimeout(() => playChord([659, 784, 1047], { type: "sine", gain: 0.14, attack: 0.02, duration: 0.8, release: 0.6 }), 300); },
    suspend,
    resume,
  };
}

const soundEngine = createSoundEngine();

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #15102a; overflow-x: hidden; }

  /* Type system: Fraunces (expressive serif) for display, Plus Jakarta Sans for UI/body */
  .display-font, h1, h2, h3 { font-family: 'Fraunces', Georgia, serif !important; letter-spacing: -0.01em; }

  /* ── THEME PROPAGATION ──
     Some screens hardcode the default purple palette. These rules remap those
     hardcoded values to the active theme's colours (set as CSS variables on
     the app root), so switching theme recolours every page. */
  [style*="#15102a"], [style*="21, 16, 42"] { background: var(--bg, #15102a) !important; }
  [style*="linear-gradient"][style*="rgb(124, 58, 237)"],
  [style*="linear-gradient"][style*="#7c3aed,"],
  [style*="linear-gradient"][style*="#7c3aed)"] {
    background: linear-gradient(135deg, var(--orb1, #7c3aed), var(--accent, #c084fc)) !important;
  }
  [style*="color: #c084fc"], [style*="color: rgb(192, 132, 252)"] { color: var(--accent, #c084fc) !important; }
  [style*="border"][style*="rgb(124, 58, 237)"], [style*="border"][style*="rgb(192, 132, 252)"] { border-color: var(--accent, #c084fc) !important; }

  /* ── LIGHT THEME OVERRIDES ──
     Inline styles assume a dark background. When a light theme is active,
     .light-ui remaps the core text + card colours. Selectors match both hex
     and the browser's rgb() serialization of the style attribute. */
  .light-ui { color: #241540 !important; }
  .light-ui [style*="#f5f0ff"], .light-ui [style*="245, 240, 255"] { color: #241540 !important; }
  .light-ui [style*="#e9e2f7"], .light-ui [style*="233, 226, 247"] { color: #33235a !important; }
  .light-ui [style*="#c4b5d4"], .light-ui [style*="196, 181, 212"] { color: #4d3b6e !important; }
  .light-ui [style*="#9ca3af"], .light-ui [style*="156, 163, 175"] { color: #5c5470 !important; }
  .light-ui [style*="#8b7ba8"], .light-ui [style*="139, 123, 168"],
  .light-ui [style*="#8b7aaa"], .light-ui [style*="139, 122, 170"] { color: #6d5f88 !important; }
  .light-ui [style*="#6b7280"], .light-ui [style*="107, 114, 128"] { color: #6f6a80 !important; }
  .light-ui [style*="#4b5563"], .light-ui [style*="75, 85, 99"] { color: #837d92 !important; }
  .light-ui [style*="#1a1030"], .light-ui [style*="26, 16, 48"],
  .light-ui [style*="#110820"], .light-ui [style*="17, 8, 32"],
  .light-ui [style*="#271a4a"], .light-ui [style*="39, 26, 74"],
  .light-ui [style*="#1e1238"], .light-ui [style*="30, 18, 56"],
  .light-ui [style*="#2a1a3e"], .light-ui [style*="42, 26, 62"],
  .light-ui [style*="#0e0a1a"], .light-ui [style*="14, 10, 26"],
  .light-ui [style*="#0a0614"], .light-ui [style*="10, 6, 20"] {
    background: #ffffffea !important;
    border-color: #00000018 !important;
    box-shadow: 0 6px 24px #00000014 !important;
  }
  .light-ui [style*="#15102a"], .light-ui [style*="21, 16, 42"] { background: #f3f0fa !important; }
  .light-ui input, .light-ui select, .light-ui textarea { color: #241540 !important; }

  @keyframes floatA { 0%,100%{transform:translate3d(0,0,0) scale(1)} 33%{transform:translate3d(30px,-40px,0) scale(1.1)} 66%{transform:translate3d(-20px,20px,0) scale(0.95)} }
  @keyframes floatB { 0%,100%{transform:translate3d(0,0,0) scale(1)} 33%{transform:translate3d(-40px,30px,0) scale(1.05)} 66%{transform:translate3d(25px,-25px,0) scale(1.1)} }
  @keyframes floatC { 0%,100%{transform:translate3d(0,0,0) scale(1)} 50%{transform:translate3d(20px,40px,0) scale(0.9)} }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes popIn { 0%{opacity:0;transform:scale(0.8)} 70%{transform:scale(1.05)} 100%{opacity:1;transform:scale(1)} }
  @keyframes shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
  @keyframes pulse-ring { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(1.6);opacity:0} }
  @keyframes particle { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(var(--tx),var(--ty)) scale(0)} }
  @keyframes confetti-fall { 0%{transform:translateY(-20px) rotate(0deg);opacity:1} 100%{transform:translateY(120vh) rotate(720deg);opacity:0} }
  @keyframes slideUp { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
  @keyframes glow-pulse { 0%,100%{box-shadow:0 0 20px var(--glow-color, #c084fc44)} 50%{box-shadow:0 0 40px var(--glow-color, #c084fc88)} }

  .fade-up { animation: fadeUp 0.5s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up-delay-1 { animation: fadeUp 0.5s 0.08s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up-delay-2 { animation: fadeUp 0.5s 0.16s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up-delay-3 { animation: fadeUp 0.5s 0.24s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up-delay-4 { animation: fadeUp 0.5s 0.32s cubic-bezier(0.16,1,0.3,1) both; }
  .pop-in { animation: popIn 0.5s cubic-bezier(0.16,1,0.3,1) both; }
  .slide-up { animation: slideUp 0.4s cubic-bezier(0.16,1,0.3,1) both; }

  .card-hover {
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .card-hover:hover {
    transform: translateY(-3px);
  }
  .card-hover:active {
    transform: translateY(0px) scale(0.98);
  }

  .btn-primary {
    transition: transform 0.15s ease, filter 0.15s ease;
  }
  .btn-primary:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .btn-primary:active { transform: scale(0.97); }

  .shimmer-text {
    background: linear-gradient(90deg, #c084fc, #f472b6, #c084fc, #f472b6);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: shimmer 3s linear infinite;
  }
`;

// ─── FLOATING ORBS BACKGROUND ────────────────────────────────────────────────

function OrbBackground({ color1, color2, color3 }) {
  // Colours fall back to the active theme's CSS variables so every screen
  // recolours when the theme changes. Alpha is applied via opacity so var()
  // strings work in the gradients.
  const c1 = color1 || "var(--orb1, #7c3aed)";
  const c2 = color2 || "var(--orb2, #db2777)";
  const c3 = color3 || "var(--accent, #2563eb)";
  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      <div style={{
        position: "absolute", width: 320, height: 320, borderRadius: "50%",
        background: `radial-gradient(circle, ${c1} 0%, transparent 70%)`, opacity: 0.19,
        top: "-80px", left: "-80px", animation: "floatA 12s ease-in-out infinite",
        willChange: "transform", backfaceVisibility: "hidden"
      }} />
      <div style={{
        position: "absolute", width: 280, height: 280, borderRadius: "50%",
        background: `radial-gradient(circle, ${c2} 0%, transparent 70%)`, opacity: 0.15,
        top: "30%", right: "-60px", animation: "floatB 15s ease-in-out infinite",
        willChange: "transform", backfaceVisibility: "hidden"
      }} />
      <div style={{
        position: "absolute", width: 200, height: 200, borderRadius: "50%",
        background: `radial-gradient(circle, ${c3} 0%, transparent 70%)`, opacity: 0.13,
        bottom: "10%", left: "20%", animation: "floatC 10s ease-in-out infinite",
        willChange: "transform", backfaceVisibility: "hidden"
      }} />
    </div>
  );
}

// ─── CONFETTI ────────────────────────────────────────────────────────────────

function Confetti() {
  const pieces = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    color: ["#c084fc","#f472b6","#60a5fa","#fbbf24","#a78bfa"][Math.floor(Math.random() * 5)],
    delay: Math.random() * 1.5,
    duration: 2 + Math.random() * 2,
    size: 6 + Math.random() * 8,
    rotate: Math.random() * 360
  }));
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 50, overflow: "hidden" }}>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: "absolute", top: "-20px", left: `${p.left}%`,
          width: p.size, height: p.size, borderRadius: p.id % 3 === 0 ? "50%" : "2px",
          background: p.color,
          animation: `confetti-fall ${p.duration}s ${p.delay}s ease-in both`
        }} />
      ))}
    </div>
  );
}

// ─── ANIMATED RING ────────────────────────────────────────────────────────────

function BreathRing({ phase, progress, color }) {
  const size = 220;
  const cx = size / 2; const cy = size / 2; const r = 78;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - progress);
  const isActive = phase === "squeeze" || phase === "hold";

  return (
    <div style={{ position: "relative", width: size, height: size, margin: "0 auto" }}>
      {/* Outer pulse ring */}
      {isActive && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: `2px solid ${color}`,
          animation: "pulse-ring 1.5s ease-out infinite"
        }} />
      )}
      <svg width={size} height={size} style={{ position: "relative", zIndex: 1 }}>
        <defs>
          <radialGradient id={`glow-${color.replace("#","")}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </radialGradient>
          <filter id="blur-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>
        {/* Glow fill */}
        <circle cx={cx} cy={cy} r={r + 22} fill={`url(#glow-${color.replace("#","")})`} />
        {/* Track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ffffff08" strokeWidth="10" />
        {/* Progress arc */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dashoffset 0.15s linear", filter: `drop-shadow(0 0 8px ${color})` }}
        />
        {/* Inner circle */}
        <circle cx={cx} cy={cy} r={r - 24} fill={color + "15"}
          style={{ transition: "r 0.3s ease" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.15em", opacity: 0.9 }}>
          {phase === "squeeze" ? "Squeeze & Lift" : phase === "hold" ? "Hold" : phase === "release" ? "Release" : phase === "rest" ? "Rest" : ""}
        </span>
      </div>
    </div>
  );
}

// ─── EXERCISE TIMER ───────────────────────────────────────────────────────────

// ─── VOICE ENGINE ────────────────────────────────────────────────────────────

const VOICE_STYLES = [
  { id: "default", name: "Default", lang: "en-GB", pitch: 1.2, rate: 0.85, free: true },
  { id: "calm", name: "Calm", lang: "en-GB", pitch: 1.0, rate: 0.75, free: false },
  { id: "energetic", name: "Energetic", lang: "en-GB", pitch: 1.3, rate: 1.0, free: false },
  { id: "soft", name: "Soft US", lang: "en-US", pitch: 1.2, rate: 0.8, free: false },
];
const VOICE_STYLE_KEY = "coredoc-voice-style";
function loadVoiceStyle() { try { return localStorage.getItem(VOICE_STYLE_KEY) || "default"; } catch { return "default"; } }
function saveVoiceStyle(s) { try { localStorage.setItem(VOICE_STYLE_KEY, s); } catch {} }

// ─── QUEUED SPEECH SYSTEM ────────────────────────────────────────────────────
// The native @capacitor-community/text-to-speech plugin resolves its speak()
// promise only when the utterance FINISHES. We rely on that: each phrase is
// awaited to completion before the next starts. A token lets a new request
// cleanly supersede an in-progress one (e.g. changing exercise phase, or exiting).
let _speakToken = 0;

async function _nativeSpeak(tts, text, style, token) {
  try {
    // queueStrategy 1 = flush/interrupt any current speech, then speak this.
    // Because we await, this resolves only when the utterance has fully spoken.
    await tts.speak({
      text, lang: style.lang, rate: style.rate, pitch: style.pitch,
      volume: 1.0, category: "playback", queueStrategy: 1
    });
  } catch (e) {
    // If the plugin rejects (e.g. interrupted), just fall through
  }
}

function _browserSpeak(text, style, token) {
  return new Promise(resolve => {
    if (!("speechSynthesis" in window)) { setTimeout(resolve, 400); return; }
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = style.lang; utter.pitch = style.pitch; utter.rate = style.rate; utter.volume = 1.0;
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        const v = voices.find(v => v.lang.startsWith("en") && /samantha|karen|amy|emma|female/i.test(v.name)) ||
          voices.find(v => v.lang === "en-GB") || voices.find(v => v.lang.startsWith("en")) || voices[0];
        if (v) { try { utter.voice = v; } catch(e) {} }
      }
      let settled = false;
      const finish = () => { if (!settled) { settled = true; resolve(); } };
      utter.onend = finish;
      utter.onerror = finish;
      window.speechSynthesis.speak(utter);
      // Safety net in case onend never fires (some browsers)
      const words = Math.max(1, text.trim().split(/\s+/).length);
      setTimeout(finish, (words / 2.3) * 1000 + 4000);
    } catch (e) { setTimeout(resolve, 400); }
  });
}

async function speak(text, styleId = "default") {
  const style = VOICE_STYLES.find(v => v.id === styleId) || VOICE_STYLES[0];
  _speakToken++;
  const token = _speakToken;
  const tts = getTTS();
  if (tts) {
    await _nativeSpeak(tts, text, style, token);
  } else {
    await _browserSpeak(text, style, token);
  }
}

// Speak several phrases back-to-back, each finishing before the next starts.
async function speakSequence(items, styleId = "default") {
  const style = VOICE_STYLES.find(v => v.id === styleId) || VOICE_STYLES[0];
  _speakToken++;
  const token = _speakToken;
  const tts = getTTS();
  for (const item of items) {
    if (token !== _speakToken) return;               // superseded — stop
    if (item.delay) await new Promise(r => setTimeout(r, item.delay));
    if (token !== _speakToken) return;
    if (tts) await _nativeSpeak(tts, item.text, style, token);
    else await _browserSpeak(item.text, style, token);
  }
}

function stopSpeech() {
  _speakToken++;                                     // invalidate any running sequence
  const tts = getTTS();
  if (tts) { try { tts.stop(); } catch (e) {} }
  try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch (e) {}
}

// Prime speech on first tap — unlocks audio context on Android
let _speechPrimed = false;
function primeSpeech() {
  if (_speechPrimed) return;
  _speechPrimed = true;
  try {
    if ("speechSynthesis" in window) {
      const utter = new SpeechSynthesisUtterance(" ");
      utter.volume = 0;
      window.speechSynthesis.speak(utter);
    }
  } catch(e) {}
}

// ─── EXERCISE TIMER ───────────────────────────────────────────────────────────

function ExerciseTimer({ exercise, color, onComplete, soundEnabled, avatar, voiceEnabled, voiceStyle, pro, onNextExercise, canSkip, resumeRep, onRepComplete, onUpgrade }) {
  const [currentSet, setCurrentSet] = useState(1);
  const [currentRep, setCurrentRep] = useState(resumeRep || 1);
  const [phase, setPhase] = useState("squeeze");
  const [timeLeft, setTimeLeft] = useState(exercise.squeezeSecs || exercise.holdSecs);
  const [done, setDone] = useState(false);
  const [resting, setResting] = useState(false);
  const [paused, setPaused] = useState(false);
  const [started, setStarted] = useState(false);
  const stateRef = useRef({ phase: "squeeze", currentSet: 1, currentRep: 1, resting: false });
  const intervalRef = useRef(null);
  const soundRef = useRef(soundEnabled);
  const voiceRef = useRef(voiceEnabled);
  const voiceStyleRef = useRef(voiceStyle);
  const pausedRef = useRef(false);

  useEffect(() => { soundRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { voiceRef.current = voiceEnabled; voiceStyleRef.current = voiceStyle; }, [voiceEnabled, voiceStyle]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { stateRef.current = { phase, currentSet, currentRep, resting }; }, [phase, currentSet, currentRep, resting]);

  function announcePhase(p, rep, set, totalReps, totalSets) {
    if (!voiceRef.current) return;
    const s = voiceStyleRef.current || "default";
    if (p === "squeeze") speak("Squeeze and lift", s);
    else if (p === "hold") speak("Hold it", s);
    else if (p === "release") speak("And release", s);
    else if (p === "rest") speak("Rest now", s);
  }

  useEffect(() => {
    if (!done && !started) return;
    if (done) return;
    if (paused) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      if (pausedRef.current) return;
      setTimeLeft(t => {
        if (soundRef.current) { if (t===3||t===2||t===1) soundEngine.tick(); }
        if (t <= 1) { clearInterval(intervalRef.current); advance(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [phase, currentSet, currentRep, resting, done, paused, started]);

  useEffect(() => {
    if (!done && started) announcePhase(resting ? "rest" : phase, currentRep, currentSet, exercise.reps, exercise.sets);
  }, [phase, resting]);

  function playPhaseSound(p) {
    if (!soundRef.current) return;
    if (p==="squeeze") soundEngine.squeeze();
    else if (p==="hold") soundEngine.hold();
    else if (p==="release") soundEngine.release();
    else if (p==="rest") soundEngine.rest();
  }

  function advance() {
    const { phase, currentSet, currentRep, resting } = stateRef.current;
    if (resting) {
      stateRef.current = { ...stateRef.current, resting: false, currentRep: 1, phase: "squeeze" };
      setResting(false); setCurrentRep(1); setPhase("squeeze"); setTimeLeft(exercise.squeezeSecs || exercise.holdSecs); playPhaseSound("squeeze"); return;
    }
    if (phase === "squeeze") {
      stateRef.current.phase = "hold"; setPhase("hold"); setTimeLeft(exercise.holdSecs); playPhaseSound("hold");
    } else if (phase === "hold") {
      stateRef.current.phase = "release"; setPhase("release"); setTimeLeft(exercise.restSecs); playPhaseSound("release");
    } else {
      if (currentRep < exercise.reps) {
        const nr = currentRep+1;
        stateRef.current = { ...stateRef.current, currentRep: nr, phase: "squeeze" };
        setCurrentRep(nr);
        setPhase("squeeze");
        setTimeLeft(exercise.squeezeSecs || exercise.holdSecs);
        playPhaseSound("squeeze");
        // Save rep progress
        if (onRepComplete) onRepComplete(nr);
      } else if (currentSet < exercise.sets) {
        const ns = currentSet+1; stateRef.current = { ...stateRef.current, currentSet: ns, resting: true, phase: "rest" };
        setCurrentSet(ns); setResting(true); setPhase("rest"); setTimeLeft(exercise.setRestSecs); playPhaseSound("rest");
      } else {
        setDone(true); stopSpeech();
        if (soundRef.current) soundEngine.complete();
        if (voiceRef.current) setTimeout(() => speak("Well done! Exercise complete.", voiceStyleRef.current||"default"), 400);
      }
    }
  }

  function togglePause() {
    if (paused) {
      setPaused(false);
      if (voiceRef.current) speak("Resuming.", voiceStyleRef.current||"default");
    } else {
      setPaused(true); clearInterval(intervalRef.current); stopSpeech();
      if (voiceRef.current) speak("Paused.", voiceStyleRef.current||"default");
    }
  }

  const phaseDuration = resting ? exercise.setRestSecs : phase==="squeeze" ? (exercise.squeezeSecs || exercise.holdSecs) : phase==="hold" ? exercise.holdSecs : exercise.restSecs;
  const progress = phaseDuration > 0 ? timeLeft/phaseDuration : 0;
  const displayPhase = resting ? "rest" : phase;

  // Show start screen before exercise begins
  if (!started) return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{ marginBottom: 16 }}>
        <AvatarSVG avatar={avatar} size={80} animated={false} phase="rest"/>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#f5f0ff", marginBottom: 8 }}>{exercise.name}</div>
      <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 6 }}>{exercise.reps} reps · {exercise.holdSecs}s hold · Do 3× daily</div>
      <div style={{ background: color+"18", border: `1px solid ${color}44`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Get ready</div>
        <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.6 }}>{exercise.instruction}</p>
      </div>
      <button onClick={() => { try { primeSpeech(); } catch(e) {} setStarted(true); if(voiceEnabled) speak("Starting now.", voiceStyle); }} className="btn-primary" style={{
        background: `linear-gradient(135deg, ${color}cc, ${color})`,
        color: "#fff", border: "none", borderRadius: 14, padding: "16px 40px",
        fontSize: 16, fontWeight: 700, cursor: "pointer",
        boxShadow: `0 8px 24px ${color}44`
      }}>▶ Start exercise</button>
    </div>
  );

  if (done) return (
    <div style={{ textAlign:"center", padding:"24px 0", animation:"popIn 0.5s cubic-bezier(0.16,1,0.3,1) both" }}>
      <div style={{ marginBottom:10 }}><AvatarSVG avatar={avatar} size={72} animated={false} phase="rest"/></div>
      <div style={{ fontSize:18, fontWeight:700, color, marginBottom:16 }}>Exercise complete!</div>
      <button onClick={onComplete} className="btn-primary" style={{
        background:`linear-gradient(135deg, ${color}cc, ${color})`, color:"#fff", border:"none",
        borderRadius:14, padding:"14px 36px", fontSize:15, fontWeight:700, cursor:"pointer",
        boxShadow:`0 8px 24px ${color}44`
      }}>Next →</button>
    </div>
  );

  return (
    <div style={{ textAlign:"center" }}>
      <div style={{ marginBottom:10 }}>
        <span style={{ fontSize:12, color:"#6b7280", fontWeight:500 }}>
          Set {currentSet}/{exercise.sets} · {resting ? "Rest" : `Rep ${currentRep}/${exercise.reps}`}
        </span>
      </div>
      <div style={{ position:"relative", width:220, height:220, margin:"0 auto", opacity:paused?0.5:1, transition:"opacity 0.3s ease" }}>
        <BreathRing phase={paused?"rest":displayPhase} progress={progress} color={color}/>
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", paddingTop:20 }}>
          <AvatarSVG avatar={avatar} size={72} animated={!paused} phase={displayPhase}/>
        </div>
        {paused && <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ fontSize:40 }}>⏸</div>
        </div>}
      </div>
      <div style={{ fontSize:42, fontWeight:900, color, marginTop:10, fontVariantNumeric:"tabular-nums", filter:`drop-shadow(0 0 12px ${color}88)` }}>{timeLeft}s</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: color, marginTop: 6, textAlign: "center", letterSpacing: "0.05em" }}>
        {resting ? "Rest" :
         paused ? "Paused" :
         displayPhase === "squeeze" ? "Squeeze" :
         displayPhase === "hold" ? "Hold" :
         displayPhase === "release" ? "Release" : ""}
      </div>
      <div style={{ display:"flex", gap:10, justifyContent:"center", marginTop:16 }}>
        <button onClick={togglePause} style={{
          background:paused?`linear-gradient(135deg, ${color}cc, ${color})`:"#221545",
          border:`1px solid ${color}44`, borderRadius:12, padding:"10px 20px",
          color:paused?"#fff":color, fontSize:13, fontWeight:700, cursor:"pointer",
          boxShadow:paused?`0 4px 16px ${color}44`:"none"
        }}>{paused?"▶ Resume":"⏸ Pause"}</button>
        {canSkip && (
          <button onClick={() => {
            if (!pro) {
              if (onUpgrade) onUpgrade();
              return;
            }
            stopSpeech();
            clearInterval(intervalRef.current);
            onNextExercise();
          }} style={{
            background: pro ? "#221545" : "#1a1030",
            border: `1px solid ${pro ? "#a78bfa" : "#6b7280"}`,
            borderRadius: 12, padding: "10px 20px",
            color: pro ? "#e2d9ff" : "#9ca3af",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6
          }}>
            {pro ? "Skip ⏭" : "✨ Skip"}
          </button>
        )}
      </div>
    </div>
  );
}


// ─── PROGRESS SCREEN ─────────────────────────────────────────────────────────

const PRO_KEY = "coredoc-pro-unlocked";
function isPro() { try { return localStorage.getItem(PRO_KEY) === "1"; } catch { return false; } }
function unlockPro() { try { localStorage.setItem(PRO_KEY, "1"); } catch {} }

function LockedCard({ title, icon, preview, onUnlock }) {
  return (
    <div style={{ position: "relative", borderRadius: 20, overflow: "hidden", marginBottom: 14 }}>
      {/* Blurred preview content */}
      <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "18px 20px", border: "1px solid #3d2a5e55" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em" }}>{title}</span>
        </div>
        {/* Blurred fake content */}
        <div style={{ filter: "blur(6px)", opacity: 0.4, pointerEvents: "none", marginBottom: 14 }}>
          {preview}
        </div>
      </div>
      {/* Lock overlay */}
      <div style={{
        position: "absolute", inset: 0, borderRadius: 20,
        background: "linear-gradient(180deg, transparent 20%, #15102acc 60%, #15102a 100%)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
        padding: "0 20px 18px"
      }}>
        <button onClick={onUnlock} className="btn-primary" style={{
          width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)",
          color: "#fff", border: "none", borderRadius: 12, padding: "13px",
          fontSize: 14, fontWeight: 700, cursor: "pointer",
          boxShadow: "0 6px 20px #7c3aed55", display: "flex", alignItems: "center", justifyContent: "center", gap: 8
        }}>
          🔓 Unlock for £1.99
        </button>
      </div>
    </div>
  );
}

// ─── GOOGLE PLAY BILLING (via cordova-plugin-purchase) ───────────────────────
// This requires: npm install cordova-plugin-purchase
// And: npx cap sync
// Product must be created in Play Console > Monetise > Products > Subscriptions
// with Product ID exactly matching SUBSCRIPTION_ID below.

const SUBSCRIPTION_ID = "coredoc_pro";
let _store = null;
let _storeReady = false;

function initBilling() {
  if (_storeReady) return;
  // cordova-plugin-purchase registers window.CdvPurchase globally
  if (typeof window.CdvPurchase === "undefined") return;
  
  const { store, ProductType, Platform } = window.CdvPurchase;
  _store = store;

  store.register({
    id: SUBSCRIPTION_ID,
    type: ProductType.PAID_SUBSCRIPTION,
    platform: Platform.GOOGLE_PLAY,
  });

  store.when().approved(transaction => {
    transaction.verify();
  });
  store.when().verified(receipt => {
    receipt.finish();
    unlockPro();
  });
  store.when().productUpdated(() => {});

  store.initialize([Platform.GOOGLE_PLAY]);
  _storeReady = true;
}

async function launchBillingFlow() {
  return new Promise(async (resolve) => {
    try {
      if (!window.CdvPurchase) {
        // Running in browser preview — show helpful message
        if (!window.Capacitor || !window.Capacitor.isNativePlatform()) {
          resolve({ success: false, error: "Subscriptions are available in the CoreDoc app on Google Play Store. Download the app to subscribe." });
        } else {
          resolve({ success: false, error: "Please update CoreDoc to the latest version from the Play Store." });
        }
        return;
      }
      initBilling();
      // Store initialization is asynchronous — the product list may not be
      // loaded yet on the first attempt. Poll for up to ~8 seconds.
      let product = _store.get(SUBSCRIPTION_ID);
      let waited = 0;
      while ((!product || !product.getOffer()) && waited < 8000) {
        await new Promise(r => setTimeout(r, 500));
        waited += 500;
        product = _store.get(SUBSCRIPTION_ID);
      }
      if (!product) {
        resolve({ success: false, error: "Subscription not found. Please check your connection and try again." });
        return;
      }
      const offer = product.getOffer();
      if (!offer) {
        resolve({ success: false, error: "Pricing unavailable. Please try again shortly." });
        return;
      }

      const onApproved = window.CdvPurchase.store.when().approved(transaction => {
        transaction.verify();
      });
      const onVerified = window.CdvPurchase.store.when().verified(receipt => {
        receipt.finish();
        unlockPro();
        resolve({ success: true });
      });
      const onError = window.CdvPurchase.store.error(err => {
        resolve({ success: false, error: err.message || "Purchase failed. Please try again." });
      });

      offer.order().catch(err => {
        resolve({ success: false, error: err?.message || "Purchase cancelled." });
      });
    } catch(e) {
      resolve({ success: false, error: "Something went wrong. Please try again." });
    }
  });
}

async function restorePurchases() {
  return new Promise((resolve) => {
    try {
      if (!window.CdvPurchase) { resolve(false); return; }
      initBilling();
      window.CdvPurchase.store.restorePurchases().then(() => {
        const product = _store.get(SUBSCRIPTION_ID);
        if (product && product.owned) {
          unlockPro();
          resolve(true);
        } else {
          resolve(false);
        }
      }).catch(() => resolve(false));
    } catch(e) { resolve(false); }
  });
}

function PaywallModal({ onPurchase, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(false);

  async function handlePurchase() {
    setLoading(true);
    setError(null);
    try {
      const result = await launchBillingFlow();
      if (result.success) {
        onPurchase();
      } else {
        setError(result.error || "Purchase could not be completed.");
      }
    } catch(e) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  }

  async function handleRestore() {
    setRestoring(true);
    const restored = await restorePurchases();
    if (restored) { onPurchase(); }
    else { setError("No previous purchase found."); }
    setRestoring(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 400, padding: 16 }}>
      <div className="slide-up" style={{ background: "linear-gradient(180deg, #271a4a, #1e1238)", borderRadius: 24, padding: 28, maxWidth: 420, width: "100%", border: "1px solid #c084fc33", boxShadow: "0 -8px 60px #7c3aed33", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ width: 40, height: 4, background: "#3d2a5e", borderRadius: 2, margin: "0 auto 24px" }} />
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>✨</div>
          <h3 style={{ fontSize: 22, fontWeight: 900, color: "#f5f0ff", marginBottom: 8 }}>CoreDoc Pro</h3>
          <p style={{ fontSize: 14, color: "#9ca3af", lineHeight: 1.6 }}>Unlock everything for less than a cup of tea.</p>
        </div>
        {[
          { icon: "🔥", text: "Intermediate & Advanced levels for every focus" },
          { icon: "💧", text: "Bladder diary & Excel export" },
          { icon: "🎨", text: "Premium themes & music" },
          { icon: "🗣️", text: "Voice style customisation" },
          { icon: "🏆", text: "Achievements & weekly charts" },
          { icon: "📷", text: "Custom avatar photo" },
          { icon: "⏭", text: "Skip exercises" },
          { icon: "⏱", text: "Customise your own timings" },
        ].map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "#c084fc22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{f.icon}</div>
            <span style={{ fontSize: 14, color: "#f5f0ff", fontWeight: 500 }}>{f.text}</span>
          </div>
        ))}
        <div style={{ height: 1, background: "#3d2a5e44", margin: "20px 0" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 16, color: "#9ca3af" }}>Monthly subscription</span>
          <span style={{ fontSize: 28, fontWeight: 900, color: "#c084fc" }}>£1.99</span>
        </div>
        {error && <p style={{ color: "#ef4444", fontSize: 13, textAlign: "center", marginBottom: 12 }}>{error}</p>}
        <button onClick={handlePurchase} disabled={loading} className="btn-primary" style={{
          width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)",
          color: "#fff", border: "none", borderRadius: 14, padding: "16px",
          fontSize: 16, fontWeight: 700, cursor: loading ? "wait" : "pointer",
          boxShadow: "0 8px 32px #7c3aed55", marginBottom: 10, opacity: loading ? 0.8 : 1
        }}>{loading ? "Opening store..." : "Subscribe — £1.99/month"}</button>
        <button onClick={handleRestore} disabled={restoring} style={{
          width: "100%", background: "transparent", border: "1px solid #3d2a5e",
          borderRadius: 10, color: "#9ca3af", fontSize: 13, cursor: "pointer", padding: "10px", marginBottom: 8
        }}>{restoring ? "Checking..." : "Restore previous purchase"}</button>
        <button onClick={onClose} style={{ width: "100%", background: "transparent", border: "none", color: "#6b7280", fontSize: 14, cursor: "pointer", padding: "8px" }}>Maybe later</button>
        <div style={{ background: "#ffffff08", border: "1px solid #3d2a5e44", borderRadius: 10, padding: "12px 14px", marginTop: 12 }}>
          <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", margin: 0, lineHeight: 1.7 }}>
            <strong style={{ color: "#c4b5d4" }}>£1.99 / month</strong> · Subscription automatically renews unless cancelled at least 24 hours before the end of the current period. Cancel anytime in your <strong style={{ color: "#c4b5d4" }}>Google Play Store → Subscriptions</strong>. Price may vary by region.
          </p>
          <p style={{ fontSize: 10, color: "#4b5563", textAlign: "center", margin: "8px 0 0" }}>
            © 2026 Dr Abi Health. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

function ProgressScreen({ progress, onBack }) {
  const last7 = getLast7Days();
  const sessionDates = new Set(progress.sessions.map(s => s.date));
  const streak = getStreakDays(progress.sessions);
  const totalSessions = [...new Set(progress.sessions.map(s => s.date))].length;
  const programCounts = {};
  progress.sessions.forEach(s => { programCounts[s.programId] = (programCounts[s.programId] || 0) + 1; });
  const dayLabels = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const [pro, setPro] = useState(() => isPro());
  const [showPaywall, setShowPaywall] = useState(false);

  // Premium data calculations
  const last30 = Array.from({length:30},(_,i)=>{ const d=new Date(); d.setDate(d.getDate()-(29-i)); return d.toISOString().slice(0,10); });
  const workoutsPerDay = last7.map(d => progress.sessions.filter(s=>s.date===d).length);
  const weekTotal = last7.reduce((a,d)=>a+progress.sessions.filter(s=>s.date===d).length,0);
  const lastSession = progress.sessions.length ? progress.sessions[progress.sessions.length-1] : null;
  const lastSessionProg = lastSession ? PROGRAMS.find(p=>p.id===lastSession.programId) : null;

  // Achievements
  const totalDaysCount = sessionDates.size;
  const achievements = [
    { id:"first", icon:"⭐", label:"First day", desc:"Complete your first day of exercises", earned: totalDaysCount >= 1 },
    { id:"week", icon:"🔥", label:"7-day streak", desc:"Exercise 7 days in a row", earned: streak >= 7 },
    { id:"month", icon:"💜", label:"30 days", desc:"Exercise on 30 different days", earned: totalDaysCount >= 30 },
    { id:"streak30", icon:"🏆", label:"30-day streak", desc:"Exercise 30 days in a row", earned: streak >= 30 },
    { id:"three_months", icon:"💎", label:"90 days", desc:"Exercise on 90 different days", earned: totalDaysCount >= 90 },
    { id:"six_months", icon:"👑", label:"180 days", desc:"Exercise on 180 different days", earned: totalDaysCount >= 180 },
  ];

  return (
    <div style={{ position: "relative", zIndex: 1, padding: "32px 24px 120px", minHeight: "100vh", overflowY: "auto" }}>
      <OrbBackground />
      {showPaywall && <PaywallModal onPurchase={() => { setPro(true); setShowPaywall(false); }} onClose={() => setShowPaywall(false)} />}

      <button onClick={onBack} className="fade-up" style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 24, display: "flex", alignItems: "center", gap: 6, position: "relative", zIndex: 1 }}>← Back</button>

      <div className="fade-up-delay-1" style={{ marginBottom: 24, position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#c084fc", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>Your Journey</div>
          <h2 className="shimmer-text" style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.2 }}>Progress</h2>
        </div>
        {pro && <div style={{ background: "linear-gradient(135deg, #7c3aed, #c084fc)", borderRadius: 20, padding: "4px 12px", fontSize: 10, fontWeight: 700, color: "#fff", letterSpacing: "0.08em" }}>✨ PRO</div>}
      </div>

      {/* FREE: Stats row */}
      <div className="fade-up-delay-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16, position: "relative", zIndex: 1 }}>
        {[
          { label: "Streak", value: streak, icon: "🔥", color: "#f59e0b" },
          { label: "Total Days", value: totalSessions, icon: "✦", color: "#c084fc" },
          { label: "This Week", value: last7.filter(d => sessionDates.has(d)).length, icon: "📅", color: "#60a5fa" }
        ].map((stat, i) => (
          <div key={i} className="card-hover" style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 16, padding: "18px 10px", textAlign: "center", border: `1px solid ${stat.color}33`, boxShadow: `0 4px 24px ${stat.color}18` }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: stat.color, lineHeight: 1 }}>{stat.value}</div>
            <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4, fontWeight: 500 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* FREE: 7-day calendar */}
      <div className="fade-up-delay-3" style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "20px", marginBottom: 14, border: "1px solid #3d2a5e55", position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>Last 7 Days</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
          {last7.map((date, i) => {
            const done = sessionDates.has(date);
            const isToday = date === getTodayStr();
            const day = dayLabels[new Date(date + "T12:00:00").getDay()];
            return (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 9, color: isToday ? "#c084fc" : "#4b5563", marginBottom: 6, fontWeight: isToday ? 700 : 400 }}>{day}</div>
                <div style={{ width: 34, height: 34, borderRadius: "50%", margin: "0 auto", background: done ? "linear-gradient(135deg, #7c3aed, #c084fc)" : "#221545", border: isToday && !done ? "2px solid #c084fc55" : done ? "none" : "1px solid #3d2a5e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, boxShadow: done ? "0 4px 12px #7c3aed44" : "none" }}>
                  {done ? "✓" : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FREE: Programme bars */}
      {Object.keys(programCounts).length > 0 && (
        <div className="fade-up-delay-4" style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "20px", marginBottom: 14, border: "1px solid #3d2a5e55", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>By Programme</div>
          {PROGRAMS.filter(p => programCounts[p.id]).map(prog => (
            <div key={prog.id} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: "#f5f0ff", fontWeight: 500 }}>{prog.name}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: prog.color }}>{programCounts[prog.id]} sessions</span>
              </div>
              <div style={{ height: 6, background: "#2a1a3e", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 3, background: `linear-gradient(90deg, ${prog.glow}, ${prog.color})`, width: `${Math.min(100,(programCounts[prog.id]/prog.sessions)*100)}%`, boxShadow: `0 0 8px ${prog.color}88`, transition: "width 1s cubic-bezier(0.16,1,0.3,1)" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PRO divider */}
      {!pro && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 16px", position: "relative", zIndex: 1 }}>
          <div style={{ flex: 1, height: 1, background: "#3d2a5e44" }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em" }}>Pro features</span>
          <div style={{ flex: 1, height: 1, background: "#3d2a5e44" }} />
        </div>
      )}

      {/* PRO: Last Session */}
      {pro ? (
        <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "18px 20px", marginBottom: 14, border: "1px solid #c084fc33", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em" }}>Last Session</span>
          </div>
          {lastSessionProg ? (
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: lastSessionProg.color+"22", border: `1px solid ${lastSessionProg.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>💪</div>
              <div><div style={{ fontSize: 15, fontWeight: 700, color: "#f5f0ff" }}>{lastSessionProg.name}</div><div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{lastSession.date === getTodayStr() ? "Today" : lastSession.date}</div></div>
            </div>
          ) : <div style={{ fontSize: 13, color: "#6b7280" }}>No sessions yet</div>}
        </div>
      ) : (
        <div style={{ position: "relative", zIndex: 1 }}>
          <LockedCard title="Last Session" icon="✅" onUnlock={() => setShowPaywall(true)} preview={
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: "#c084fc22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>💪</div>
              <div><div style={{ fontSize: 15, fontWeight: 700, color: "#f5f0ff" }}>Core & Pelvic Tone</div><div style={{ fontSize: 12, color: "#6b7280" }}>Today</div></div>
            </div>
          }/>
        </div>
      )}

      {/* PRO: Weekly Chart */}
      {pro ? (
        <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "18px 20px", marginBottom: 14, border: "1px solid #c084fc33", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 18 }}>📊</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em" }}>Workouts This Week</span>
            <span style={{ marginLeft: "auto", fontSize: 20, fontWeight: 900, color: "#c084fc" }}>{weekTotal}</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 64 }}>
            {workoutsPerDay.map((count, i) => {
              const max = Math.max(...workoutsPerDay, 1);
              const barH = Math.max(4, (count / max) * 52);
              const day = dayLabels[new Date(last7[i] + "T12:00:00").getDay()];
              const isToday = last7[i] === getTodayStr();
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: "100%", height: barH, borderRadius: 4, background: count > 0 ? "linear-gradient(180deg, #c084fc, #7c3aed)" : "#2a1a3e", boxShadow: count > 0 ? "0 0 8px #c084fc66" : "none", transition: "height 0.6s cubic-bezier(0.16,1,0.3,1)" }} />
                  <span style={{ fontSize: 9, color: isToday ? "#c084fc" : "#4b5563", fontWeight: isToday ? 700 : 400 }}>{day}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ position: "relative", zIndex: 1 }}>
          <LockedCard title="Weekly Chart" icon="📊" onUnlock={() => setShowPaywall(true)} preview={
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 52 }}>
              {[2,1,3,0,2,1,3].map((h,i)=>(
                <div key={i} style={{ flex:1, height: h*16+4, borderRadius:4, background:"linear-gradient(180deg,#c084fc,#7c3aed)" }}/>
              ))}
            </div>
          }/>
        </div>
      )}

      {/* PRO: Achievements */}
      {pro ? (
        <div style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "18px 20px", marginBottom: 14, border: "1px solid #c084fc33", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 18 }}>🏆</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em" }}>Achievements</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {achievements.map(a => (
              <div key={a.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 64, position: "relative" }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, 
                  background: a.earned ? "linear-gradient(135deg, #c084fc22, #7c3aed22)" : "#271a4a", 
                  border: a.earned ? "1px solid #c084fc77" : "1px solid #3d2a5e55", 
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, 
                  boxShadow: a.earned ? "0 0 16px #c084fc55" : "none",
                  filter: a.earned ? "none" : "grayscale(1) brightness(0.4)"
                }}>{a.icon}</div>
                {/* Green tick for completed */}
                {a.earned && (
                  <div style={{ position: "absolute", top: -4, right: 4, width: 18, height: 18, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, border: "2px solid #15102a" }}>✓</div>
                )}
                <span style={{ fontSize: 9, color: a.earned ? "#e2d9ff" : "#6b7280", textAlign: "center", lineHeight: 1.2, fontWeight: a.earned ? 700 : 400 }}>{a.label}</span>
                <span style={{ fontSize: 8, color: a.earned ? "#9ca3af" : "#3d2a5e", textAlign: "center", lineHeight: 1.2 }}>{a.desc}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ position: "relative", zIndex: 1 }}>
          <LockedCard title="Achievements" icon="🏆" onUnlock={() => setShowPaywall(true)} preview={
            <div style={{ display: "flex", gap: 10 }}>
              {["🌱","🔥","💪","🏆","💎"].map((e,i)=>(
                <div key={i} style={{ width:44, height:44, borderRadius:12, background:"#c084fc22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{e}</div>
              ))}
            </div>
          }/>
        </div>
      )}

      {totalSessions === 0 && (
        <div className="fade-up" style={{ textAlign: "center", padding: "32px 0", color: "#4b5563", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🌱</div>
          <div style={{ fontSize: 14 }}>Complete your first session to start tracking.</div>
        </div>
      )}
    </div>
  );
}

// ─── REMINDER SYSTEM ─────────────────────────────────────────────────────────

const REMINDERS_KEY = "coredoc-reminders-v1";
const DEFAULT_REMINDERS = [
  { id: 1, label: "Morning session", time: "08:00", enabled: true },
  { id: 2, label: "Lunchtime session", time: "13:00", enabled: false },
  { id: 3, label: "Evening session", time: "20:00", enabled: true },
];

// Get the native LocalNotifications plugin if running in Capacitor on-device.
// This is what makes reminders fire even when the app is closed — the OS holds
// the schedule, not a JS timer.
function getLocalNotifications() {
  try {
    if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.LocalNotifications) {
      return window.Capacitor.Plugins.LocalNotifications;
    }
  } catch(e) {}
  return null;
}

function loadReminders() {
  try { const d = localStorage.getItem(REMINDERS_KEY); return d ? JSON.parse(d) : DEFAULT_REMINDERS; }
  catch { return DEFAULT_REMINDERS; }
}
function saveReminders(r) { try { localStorage.setItem(REMINDERS_KEY, JSON.stringify(r)); } catch {} }

// Reschedule ALL enabled reminders with the OS. Called whenever reminders change.
// Each fires daily at its set time and repeats — surviving app closure/restart.
async function syncNativeReminders(reminders) {
  const LN = getLocalNotifications();
  if (!LN) return; // browser preview — nothing to schedule natively
  try {
    // Clear existing CoreDoc reminders first (ids 1001+ reserved for reminders)
    const pending = await LN.getPending();
    if (pending && pending.notifications && pending.notifications.length) {
      await LN.cancel({ notifications: pending.notifications.filter(n => n.id >= 1001).map(n => ({ id: n.id })) });
    }
    const toSchedule = reminders.filter(r => r.enabled).map(r => {
      const [h, m] = r.time.split(":").map(Number);
      return {
        id: 1000 + r.id,
        title: "CoreDoc",
        body: `Time for your ${r.label}. A few minutes now makes a big difference.`,
        schedule: { on: { hour: h, minute: m }, allowWhileIdle: true }, // repeats daily
        smallIcon: "ic_stat_icon",
      };
    });
    if (toSchedule.length) await LN.schedule({ notifications: toSchedule });
  } catch(e) {}
}

function RemindersScreen({ onBack }) {
  const [reminders, setReminders] = useState(() => loadReminders());
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [permissionAsked, setPermissionAsked] = useState(false);

  async function requestPermission() {
    setPermissionAsked(true);
    const LN = getLocalNotifications();
    if (LN) {
      try {
        const res = await LN.requestPermissions();
        const granted = res && res.display === "granted";
        setPermissionGranted(granted);
        if (granted) syncNativeReminders(reminders);
        return;
      } catch(e) {}
    }
    // Browser fallback
    if ("Notification" in window) {
      const result = await Notification.requestPermission();
      setPermissionGranted(result === "granted");
    }
  }

  useEffect(() => {
    const LN = getLocalNotifications();
    if (LN) {
      LN.checkPermissions().then(res => {
        const granted = res && res.display === "granted";
        setPermissionGranted(granted);
        setPermissionAsked(res && res.display !== "prompt");
        if (granted) syncNativeReminders(loadReminders());
      }).catch(() => {});
      return;
    }
    if ("Notification" in window) {
      setPermissionGranted(Notification.permission === "granted");
      setPermissionAsked(Notification.permission !== "default");
    }
  }, []);

  function persist(updated) {
    setReminders(updated);
    saveReminders(updated);
    if (permissionGranted) syncNativeReminders(updated);
  }

  function toggleReminder(id) {
    persist(reminders.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
  }
  function updateTime(id, time) {
    persist(reminders.map(r => r.id === id ? { ...r, time } : r));
  }
  function updateLabel(id, label) {
    persist(reminders.map(r => r.id === id ? { ...r, label } : r));
  }

  const enabledCount = reminders.filter(r => r.enabled).length;

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#15102a", color: "#f5f0ff", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", maxWidth: 420, margin: "0 auto", padding: "32px 24px 120px", overflowY: "auto" }}>
      <OrbBackground />
      <div style={{ position: "relative", zIndex: 1 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 24, display: "flex", alignItems: "center", gap: 6 }}>← Back</button>

        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>Free Feature</div>
          <h2 style={{ fontSize: 24, fontWeight: 900, margin: "0 0 8px" }}>Daily Reminders</h2>
          <p style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.5 }}>Set up to 3 reminders to keep you consistent. Consistency is the most important factor in building pelvic floor strength.</p>
        </div>

        {/* Permission banner */}
        {!permissionGranted && (
          <div style={{ background: "#f59e0b11", border: "1px solid #f59e0b44", borderRadius: 16, padding: "16px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b", marginBottom: 6 }}>🔔 Enable notifications</div>
            <p style={{ fontSize: 13, color: "#c4b5d4", marginBottom: 14, lineHeight: 1.5 }}>
              {permissionAsked
                ? "Notifications are blocked. To enable reminders, go to your browser/device settings and allow notifications for this app."
                : "Allow notifications so CoreDoc can remind you to do your exercises at the times you choose."}
            </p>
            {!permissionAsked && (
              <button onClick={requestPermission} style={{
                background: "linear-gradient(135deg, #d97706, #f59e0b)", color: "#fff",
                border: "none", borderRadius: 10, padding: "10px 20px",
                fontSize: 13, fontWeight: 700, cursor: "pointer"
              }}>Allow notifications</button>
            )}
          </div>
        )}

        {permissionGranted && (
          <div style={{ background: "#c084fc18", border: "1px solid #c084fc55", borderRadius: 12, padding: "10px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14 }}>✅</span>
            <span style={{ fontSize: 13, color: "#c084fc", fontWeight: 600 }}>Notifications enabled — {enabledCount} reminder{enabledCount !== 1 ? "s" : ""} active</span>
          </div>
        )}

        {/* Reminder cards */}
        {reminders.map((reminder, i) => (
          <div key={reminder.id} className="fade-up" style={{
            background: reminder.enabled
              ? "linear-gradient(135deg, #1a1030ee, #110820ee)"
              : "linear-gradient(135deg, #0e0a1aee, #0a0614ee)",
            backdropFilter: "blur(12px)",
            borderRadius: 20, padding: "18px 20px", marginBottom: 12,
            border: `1px solid ${reminder.enabled ? "#f59e0b44" : "#3d2a5e33"}`,
            opacity: reminder.enabled ? 1 : 0.6,
            transition: "all 0.2s ease"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: reminder.enabled ? 14 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>🔔</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: reminder.enabled ? "#f5f0ff" : "#6b7280" }}>{reminder.label}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{reminder.time}</div>
                </div>
              </div>
              {/* Toggle */}
              <div onClick={() => toggleReminder(reminder.id)} style={{
                width: 48, height: 26, borderRadius: 13, cursor: "pointer", position: "relative",
                background: reminder.enabled ? "linear-gradient(135deg, #d97706, #f59e0b)" : "#2a1a3e",
                transition: "background 0.2s ease", border: `1px solid ${reminder.enabled ? "#f59e0b" : "#3d2a5e"}`
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                  position: "absolute", top: 2, left: reminder.enabled ? 24 : 2,
                  transition: "left 0.2s ease", boxShadow: "0 1px 4px #00000044"
                }} />
              </div>
            </div>

            {reminder.enabled && (
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Time</div>
                  <input type="time" value={reminder.time} onChange={e => updateTime(reminder.id, e.target.value)}
                    style={{ background: "#15102a", border: "1px solid #3d2a5e", borderRadius: 8, padding: "6px 10px", color: "#f5f0ff", fontSize: 13, width: "100%", outline: "none" }}
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Label</div>
                  <input type="text" value={reminder.label} onChange={e => updateLabel(reminder.id, e.target.value)}
                    style={{ background: "#15102a", border: "1px solid #3d2a5e", borderRadius: 8, padding: "6px 10px", color: "#f5f0ff", fontSize: 13, width: "100%", outline: "none" }}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Clinical note */}
        <div style={{ background: "#c084fc11", border: "1px solid #c084fc33", borderRadius: 14, padding: "14px 16px", marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#c084fc", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>💡 Helpful tip</div>
          <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.6 }}>
            Aim for 3 sessions daily — morning, afternoon, and evening. Research shows that spreading exercises throughout the day is more effective than one long session.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── TIMINGS EDITOR ──────────────────────────────────────────────────────────

function TimingsEditor({ program, customTimings, onSave, onSkip }) {
  const [timings, setTimings] = useState(() => {
    const t = {};
    program.exercises.forEach(ex => {
      t[ex.id] = {
        holdSecs: customTimings[ex.id]?.holdSecs ?? ex.holdSecs,
        restSecs: customTimings[ex.id]?.restSecs ?? ex.restSecs,
        reps: customTimings[ex.id]?.reps ?? ex.reps,
      };
    });
    return t;
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0d0820", overflowY: "auto", zIndex: 450, padding: "32px 24px 60px", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: "#f5f0ff" }}>
      <OrbBackground />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 420, margin: "0 auto" }}>
        <div style={{ fontSize: 36, textAlign: "center", marginBottom: 8 }}>🩺</div>
        <h2 style={{ fontSize: 20, fontWeight: 900, textAlign: "center", marginBottom: 6 }}>Clinician Timings</h2>
        <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", marginBottom: 24, lineHeight: 1.5 }}>
          Enter the hold times and reps your physiotherapist recommended. These will override the default settings for {program.name}.
        </p>

        {program.exercises.map(ex => (
          <div key={ex.id} style={{ background: "linear-gradient(135deg, #271a4aee, #1e1238ee)", borderRadius: 18, padding: "20px", marginBottom: 16, border: `1px solid ${program.color}33` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: program.color, marginBottom: 16 }}>{ex.name}</div>
            
            {/* Reps */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Number of reps</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[3, 5, 6, 8, 10, 12, 15].map(n => (
                  <button key={n} onClick={() => setTimings(t => ({ ...t, [ex.id]: { ...t[ex.id], reps: n } }))} style={{
                    background: timings[ex.id]?.reps === n ? `${program.color}33` : "#1a1030",
                    border: `1px solid ${timings[ex.id]?.reps === n ? program.color : "#3d2a5e"}`,
                    borderRadius: 8, padding: "6px 12px", color: timings[ex.id]?.reps === n ? program.color : "#6b7280",
                    fontSize: 13, fontWeight: 600, cursor: "pointer"
                  }}>{n}</button>
                ))}
              </div>
            </div>

            {/* Hold time */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Hold time (seconds)</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[1, 2, 3, 5, 6, 8, 10, 12, 15, 20].map(n => (
                  <button key={n} onClick={() => setTimings(t => ({ ...t, [ex.id]: { ...t[ex.id], holdSecs: n } }))} style={{
                    background: timings[ex.id]?.holdSecs === n ? `${program.color}33` : "#1a1030",
                    border: `1px solid ${timings[ex.id]?.holdSecs === n ? program.color : "#3d2a5e"}`,
                    borderRadius: 8, padding: "6px 12px", color: timings[ex.id]?.holdSecs === n ? program.color : "#6b7280",
                    fontSize: 13, fontWeight: 600, cursor: "pointer"
                  }}>{n}s</button>
                ))}
              </div>
            </div>

            {/* Rest time */}
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Rest time between reps (seconds)</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[1, 2, 3, 5, 6, 8, 10, 12, 15, 20].map(n => (
                  <button key={n} onClick={() => setTimings(t => ({ ...t, [ex.id]: { ...t[ex.id], restSecs: n } }))} style={{
                    background: timings[ex.id]?.restSecs === n ? `${program.color}33` : "#1a1030",
                    border: `1px solid ${timings[ex.id]?.restSecs === n ? program.color : "#3d2a5e"}`,
                    borderRadius: 8, padding: "6px 12px", color: timings[ex.id]?.restSecs === n ? program.color : "#6b7280",
                    fontSize: 13, fontWeight: 600, cursor: "pointer"
                  }}>{n}s</button>
                ))}
              </div>
            </div>
          </div>
        ))}

        <div style={{ background: "#c084fc11", border: "1px solid #c084fc33", borderRadius: 12, padding: "12px 16px", marginBottom: 20 }}>
          <p style={{ fontSize: 12, color: "#9ca3af", margin: 0, lineHeight: 1.5 }}>
            💜 Your clinician's timings will be saved and used every time you do this programme. You can change them anytime from the exercise screen.
          </p>
        </div>

        <button onClick={() => onSave(timings)} style={{
          width: "100%", background: `linear-gradient(135deg, #7c3aed, #c084fc)`,
          color: "#fff", border: "none", borderRadius: 14, padding: "16px",
          fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10,
          boxShadow: "0 6px 20px #7c3aed44"
        }}>Save & start exercising</button>
        <button onClick={onSkip} style={{
          width: "100%", background: "transparent", border: "1px solid #3d2a5e",
          borderRadius: 14, padding: "14px", color: "#6b7280",
          fontSize: 14, cursor: "pointer"
        }}>Use standard timings instead</button>
      </div>
    </div>
  );
}

// ─── FIRST-TIME GUIDED TOUR ──────────────────────────────────────────────────
const TOUR_KEY = "coredoc-tour-done";
const TOUR_STEPS = [
  { target: "nav-personalise", emoji: "🎨", title: "Make it yours",
    text: "Tap Style to change your avatar, add your photo, and pick themes, music and voice.", passthrough: false },
  { target: "nav-progress", emoji: "📊", title: "Your stats",
    text: "Tap Progress to see your streaks, weekly charts and achievements.", passthrough: false },
  { target: "nav-bladder", emoji: "💧", title: "Bladder tools",
    text: "Tap Bladder for timed voiding and your bladder diary.", passthrough: false },
  { target: "btn-learn", emoji: "🎬", title: "Guide & videos",
    text: "Tap Learn to open the guide — and watch the animated technique videos!", passthrough: true },
];

function FirstTimeTour({ onDone }) {
  const [step, setStep] = useState(-1); // -1 = welcome card
  const [rect, setRect] = useState(null);

  const current = step >= 0 ? TOUR_STEPS[step] : null;

  // Measure the target element (and re-measure on resize / late render)
  useEffect(() => {
    if (!current) { setRect(null); return; }
    let tries = 0;
    function measure() {
      const el = document.getElementById(current.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      } else if (tries++ < 20) {
        setTimeout(measure, 150);
      }
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [step]);

  function finish() {
    try { localStorage.setItem(TOUR_KEY, "1"); } catch(e) {}
    onDone();
  }
  function advance() {
    if (step + 1 >= TOUR_STEPS.length) finish();
    else setStep(s => s + 1);
  }

  // Final step: let the real button receive the tap, and end the tour when it does
  useEffect(() => {
    if (!current || !current.passthrough) return;
    const el = document.getElementById(current.target);
    if (!el) return;
    const handler = () => setTimeout(finish, 50);
    el.addEventListener("click", handler, { once: true });
    return () => el.removeEventListener("click", handler);
  }, [step]);

  // Welcome card
  if (step === -1) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#000000cc", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 28 }}>
        <div className="slide-up" style={{ background: "linear-gradient(135deg, #271a4a, #1e1238)", borderRadius: 24, padding: 28, maxWidth: 360, width: "100%", border: "1px solid #c084fc44", textAlign: "center", boxShadow: "0 8px 40px #7c3aed44" }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>👋</div>
          <h3 style={{ fontSize: 20, fontWeight: 900, color: "#f5f0ff", margin: "0 0 10px" }}>Welcome to CoreDoc!</h3>
          <p style={{ fontSize: 14, color: "#c4b5d4", lineHeight: 1.6, margin: "0 0 22px" }}>Let us show you around — it takes 30 seconds.</p>
          <button onClick={() => setStep(0)} style={{
            width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)", color: "#fff",
            border: "none", borderRadius: 14, padding: "15px", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 10,
            boxShadow: "0 6px 20px #7c3aed55"
          }}>Show me around →</button>
          <button onClick={finish} style={{ width: "100%", background: "transparent", border: "none", color: "#6b7280", fontSize: 13, cursor: "pointer", padding: 8 }}>Skip for now</button>
        </div>
      </div>
    );
  }

  if (!rect) return null;

  // Spotlight geometry
  const pad = 10;
  const holeX = rect.x - pad, holeY = rect.y - pad, holeW = rect.w + pad * 2, holeH = rect.h + pad * 2;
  const holeCx = holeX + holeW / 2;
  // Card above or below the hole, wherever there's more room
  const below = holeY < window.innerHeight / 2;
  const cardTop = below ? holeY + holeH + 18 : undefined;
  const cardBottom = below ? undefined : (window.innerHeight - holeY + 18);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 600, pointerEvents: "none" }}>
      <style>{`@keyframes tourPulse { 0%,100% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.78), 0 0 0 0 rgba(192,132,252,0.7); } 50% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.78), 0 0 0 12px rgba(192,132,252,0); } }`}</style>

      {/* Spotlight hole — the huge box-shadow dims everything around it */}
      <div style={{
        position: "absolute", left: holeX, top: holeY, width: holeW, height: holeH,
        borderRadius: 16, border: "2px solid #c084fc",
        animation: "tourPulse 1.6s ease-in-out infinite",
        pointerEvents: "none"
      }} />

      {/* Tap catcher over the hole: advances the tour (invisible). On the final
          passthrough step there is no catcher, so the real button gets the tap. */}
      {!current.passthrough && (
        <button onClick={advance} style={{
          position: "absolute", left: holeX, top: holeY, width: holeW, height: holeH,
          background: "transparent", border: "none", cursor: "pointer", pointerEvents: "auto"
        }} aria-label="Continue tour" />
      )}

      {/* Blockers around the hole so stray taps elsewhere don't dismiss things */}
      <div style={{ position: "absolute", left: 0, top: 0, right: 0, height: Math.max(0, holeY), pointerEvents: "auto" }} onClick={() => {}} />
      <div style={{ position: "absolute", left: 0, top: holeY + holeH, right: 0, bottom: 0, pointerEvents: "auto" }} onClick={() => {}} />
      <div style={{ position: "absolute", left: 0, top: holeY, width: Math.max(0, holeX), height: holeH, pointerEvents: "auto" }} onClick={() => {}} />
      <div style={{ position: "absolute", left: holeX + holeW, top: holeY, right: 0, height: holeH, pointerEvents: "auto" }} onClick={() => {}} />

      {/* Instruction card */}
      <div style={{
        position: "absolute", left: 20, right: 20, top: cardTop, bottom: cardBottom,
        maxWidth: 380, margin: "0 auto", pointerEvents: "auto"
      }}>
        <div className="slide-up" style={{ background: "linear-gradient(135deg, #271a4a, #1e1238)", borderRadius: 20, padding: "18px 20px", border: "1px solid #c084fc44", boxShadow: "0 8px 32px #00000088" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 24 }}>{current.emoji}</span>
            <span style={{ fontSize: 16, fontWeight: 900, color: "#f5f0ff" }}>{current.title}</span>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280", fontWeight: 700 }}>{step + 1}/{TOUR_STEPS.length}</span>
          </div>
          <p style={{ fontSize: 13.5, color: "#c4b5d4", lineHeight: 1.6, margin: "0 0 10px" }}>{current.text}</p>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={finish} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 12, cursor: "pointer", padding: 4 }}>Skip tour</button>
            <span style={{ fontSize: 12, color: "#c084fc", fontWeight: 800 }}>👆 Tap the highlighted button</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── LEVEL SELECTOR ──────────────────────────────────────────────────────────
function LevelSelector({ program, pro, onPick, onBack, onUpgrade }) {
  return (
    <div style={{ position: "relative", zIndex: 1, minHeight: "100vh", padding: "20px 22px 60px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 14, cursor: "pointer", padding: "4px 0 14px" }}>‹ Back</button>
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: program.color, textTransform: "uppercase", letterSpacing: "0.12em" }}>{program.name}</div>
        <h2 style={{ fontSize: 24, fontWeight: 900, margin: "4px 0" }}>Choose your level</h2>
        <p style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.5, margin: "0 auto", maxWidth: 300 }}>Levels build up gradually by increasing how long you hold each squeeze.</p>
      </div>

      <div style={{ marginTop: 20 }}>
        {LEVELS.map(level => {
          const locked = !level.free && !pro;
          return (
            <button key={level.id}
              onClick={() => locked ? onUpgrade() : onPick(level.id)}
              className="card-hover"
              style={{
                display: "block", width: "100%", textAlign: "left", position: "relative",
                background: locked ? "#1a1030" : `linear-gradient(135deg, ${program.color}18, #1a1030)`,
                border: `1px solid ${locked ? "#3d2a5e" : program.color + "55"}`,
                borderRadius: 18, padding: "18px 18px", marginBottom: 14, cursor: "pointer",
                opacity: locked ? 0.85 : 1,
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <span style={{ fontSize: 26 }}>{level.emoji}</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#f5f0ff" }}>{level.name}</span>
                {level.free
                  ? <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: "#4ade80", background: "#22c55e22", borderRadius: 20, padding: "3px 10px" }}>Free</span>
                  : (pro
                      ? <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: "#c084fc", background: "#c084fc22", borderRadius: 20, padding: "3px 10px" }}>✨ Pro</span>
                      : <span style={{ marginLeft: "auto", fontSize: 13 }}>🔒</span>)
                }
              </div>
              <p style={{ fontSize: 13, color: "#c4b5d4", lineHeight: 1.5, margin: "0 0 6px" }}>{level.blurb}</p>
              <p style={{ fontSize: 11.5, color: "#8b7ba8", lineHeight: 1.5, margin: 0 }}>{level.note}</p>
              {locked && (
                <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: "#c084fc" }}>Tap to unlock with Pro →</div>
              )}
            </button>
          );
        })}
      </div>

      <p style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.6, textAlign: "center", marginTop: 12 }}>
        Levels progress by increasing hold time as your strength builds, following recognised pelvic floor training principles. This app is for general fitness and education; please consult a healthcare professional if you experience symptoms.
      </p>
    </div>
  );
}

// ─── SPLASH SCREEN ───────────────────────────────────────────────────────────

// Official CoreDoc logo (extracted from brand assets) — keeps splash identical to the Play Store icon
const SPLASH_LOGO = "data:image/png;base64,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";


function SplashScreen({ onDone }) {
  const [opacity, setOpacity] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    setTimeout(() => setOpacity(1), 100);
    setTimeout(() => setFadeOut(true), 2200);
    setTimeout(() => onDone(), 2800);
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "#0d0820",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      opacity: fadeOut ? 0 : opacity,
      transition: fadeOut ? "opacity 0.6s ease" : "opacity 0.4s ease"
    }}>
      <OrbBackground />
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>

        {/* Icon + wordmark row */}
        <div style={{ display: "flex", alignItems: "center", gap: 28, marginBottom: 16 }}>

          {/* Ring icon matching logo */}
          <div style={{ position: "relative", width: 130, height: 130, flexShrink: 0 }}>
            <img src={SPLASH_LOGO} alt="CoreDoc" width="130" height="130"
              style={{ display: "block", width: 130, height: 130 }} />
          </div>

          {/* Wordmark */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 52, fontWeight: 900, letterSpacing: "-1px", lineHeight: 1 }}>
              <span style={{ color: "#a855f7" }}>Core</span><span style={{ color: "#f472b6" }}>Doc</span>
            </div>
            <div style={{ fontSize: 12, color: "#8b7aaa", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              Pelvic Floor Exercises
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── GATEKEEPER SCREEN ───────────────────────────────────────────────────────

function GatekeeperScreen({ onAgree }) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0d0820", overflowY: "auto", zIndex: 300, padding: "24px 20px 40px" }}> 
      <OrbBackground />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 420, width: "100%", margin: "0 auto" }}>

        {/* Logo/icon */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src={SPLASH_LOGO} alt="CoreDoc" width="72" height="72"
            style={{ display: "block", width: 72, height: 72, margin: "0 auto 10px" }} />
          <div style={{ fontSize: 22, fontWeight: 900, color: "#f5f0ff" }}>CoreDoc</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>Pelvic Floor Health App</div>
        </div>

        {/* Safety & Privacy Notice */}
        <div style={{ background: "linear-gradient(135deg, #271a4aee, #1e1238ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "24px", border: "1px solid #c084fc33", marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#c084fc", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
            ⚠️ Important Safety & Privacy Notice
          </div>

          {[
            { icon: "📚", title: "Educational & Wellbeing Only", text: "This app is for educational purposes for healthy individuals seeking general pelvic floor strengthening. It is not intended for the diagnosis or treatment of any medical condition. It does not provide medical diagnoses, treatment, or advice." },
            { icon: "🩺", title: "Not a Doctor", text: "The symptom screenings and exercise suggestions are automated templates. They are not a replacement for a medical evaluation by a licensed healthcare professional or pelvic floor physical therapist." },
            { icon: "⚕️", title: "Consult Your Provider", text: "If you are experiencing severe pelvic pain, pain during intercourse, or sudden changes in bowel/bladder habits, please consult a medical doctor before starting any physical exercises." },
            { icon: "🔒", title: "100% Private & Local", text: "Your privacy is fully protected. All data, diary entries, and questionnaire answers are stored strictly on your physical device. We do not collect, see, or transmit your data to any external server." },
            { icon: "💳", title: "Premium Content Notice", text: "Payment for premium content or advanced local tracking features represents a fee for access to educational, fitness, and wellness materials only. The guided tracking, pelvic conditioning exercises, and automated timing routines are designed for general strength and wellness support. They do not constitute physical therapy, medical treatment, or a provider-patient relationship, nor do they guarantee specific physical health outcomes." },
          ].map((item, i) => (
            <div key={i} style={{ marginBottom: i < 4 ? 16 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#f5f0ff", marginBottom: 4 }}>{item.icon} {item.title}</div>
              <p style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.6, margin: 0 }}>{item.text}</p>
            </div>
          ))}
        </div>

        {/* Checkbox agreement */}
        <div onClick={() => setAgreed(a => !a)} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20, cursor: "pointer", padding: "14px 16px", background: agreed ? "#c084fc18" : "#ffffff08", borderRadius: 14, border: `1px solid ${agreed ? "#c084fc55" : "#3d2a5e44"}`, transition: "all 0.2s ease" }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${agreed ? "#c084fc" : "#6b7280"}`, background: agreed ? "#c084fc" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all 0.2s ease" }}>
            {agreed && <span style={{ color: "#fff", fontSize: 14, fontWeight: 900 }}>✓</span>}
          </div>
          <p style={{ fontSize: 12, color: agreed ? "#e2d9ff" : "#9ca3af", lineHeight: 1.6, margin: 0 }}>
            I have read and accept the Terms & Conditions and Medical Disclaimer. I understand this app is for educational and general wellbeing purposes only, is intended for healthy individuals seeking general pelvic floor strengthening, is not intended for the diagnosis or treatment of any medical condition, and stores my data entirely locally on my device.
          </p>
        </div>

        {/* Get Started button */}
        <button
          onClick={agreed ? onAgree : undefined}
          disabled={!agreed}
          style={{
            width: "100%",
            background: agreed ? "linear-gradient(135deg, #7c3aed, #c084fc)" : "#2a1a3e",
            color: agreed ? "#fff" : "#4b5563",
            border: `1px solid ${agreed ? "transparent" : "#3d2a5e"}`,
            borderRadius: 14, padding: "16px",
            fontSize: 16, fontWeight: 700,
            cursor: agreed ? "pointer" : "not-allowed",
            boxShadow: agreed ? "0 8px 32px #7c3aed55" : "none",
            transition: "all 0.3s ease"
          }}>
          Get Started →
        </button>

        <p style={{ fontSize: 11, color: "#4b5563", textAlign: "center", marginTop: 12 }}>
          By Dr Abi Health
        </p>
        <p style={{ fontSize: 11, color: "#4b5563", textAlign: "center", marginTop: 6 }}>
          © 2026 Dr Abi Health. All rights reserved.
        </p>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

export default function PelvicFloorApp() {
  // Initialise Google Play Billing early so the product catalogue is already
  // loaded by the time the user opens the paywall (init is asynchronous)
  useEffect(() => {
    const t = setTimeout(() => { try { initBilling(); } catch(e) {} }, 1500);
    return () => clearTimeout(t);
  }, []);

  // Stop voice and music when app goes to background
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden) {
        stopSpeech();
        musicEngine.stop();
        soundEngine.suspend();
      } else {
        soundEngine.resume();
      }
    }
    function handleBlur() {
      stopSpeech();
      musicEngine.stop();
      soundEngine.suspend();
    }
    function handleFocus() {
      soundEngine.resume();
    }
    function handleNativePause() {
      // Fired by Capacitor/Android when the app goes to background or is closed —
      // more reliable on-device than visibilitychange, which the WebView may
      // freeze before delivering
      stopSpeech();
      musicEngine.stop();
      soundEngine.suspend();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("pause", handleNativePause);
    window.addEventListener("pagehide", handleNativePause);
    // Capacitor App plugin state listener, if available on this device
    let appStateHandle = null;
    try {
      const AppPlugin = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;
      if (AppPlugin && AppPlugin.addListener) {
        appStateHandle = AppPlugin.addListener("appStateChange", (state) => {
          if (state && state.isActive === false) handleNativePause();
          else soundEngine.resume();
        });
      }
    } catch(e) {}
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("pause", handleNativePause);
      window.removeEventListener("pagehide", handleNativePause);
      try { if (appStateHandle && appStateHandle.remove) appStateHandle.remove(); } catch(e) {}
    };
  }, []);
  const [screen, setScreen] = useState("home");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [pendingProgram, setPendingProgram] = useState(null); // program awaiting level choice
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [incompleteSession, setIncompleteSession] = useState(null);
  const [customTimings, setCustomTimings] = useState(() => loadCustomTimings());
  const [showTimingsEditor, setShowTimingsEditor] = useState(false);
  const [showTimingsPrompt, setShowTimingsPrompt] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [tourDone, setTourDone] = useState(() => { try { return localStorage.getItem("coredoc-tour-done") === "1"; } catch { return true; } });
  const [progress, setProgress] = useState(() => loadProgress());
  const [showConfetti, setShowConfetti] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [avatar, setAvatar] = useState(() => loadAvatar());
  const [showAvatarCustomiser, setShowAvatarCustomiser] = useState(false);
  const [screenerDone, setScreenerDone] = useState(() => !!loadScreener());
  const [screenerAnswers, setScreenerAnswers] = useState(() => loadScreener()?.answers || {});
  const [recommendedId, setRecommendedId] = useState(() => loadScreener()?.recommended || null);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [eduDone, setEduDone] = useState(() => !!localStorage.getItem("coredoc-edu-done"));
  const [showBladderPaywall, setShowBladderPaywall] = useState(false);
  const [pro, setPro] = useState(() => isPro());
  const [currentTheme, setCurrentTheme] = useState(() => loadTheme());
  const [currentMusic, setCurrentMusic] = useState(() => loadMusic());
  const [customBg, setCustomBg] = useState(() => loadCustomBg());
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceStyle, setVoiceStyle] = useState(() => loadVoiceStyle());

  function handleScreenerComplete(answers, recommended) {
    setScreenerAnswers(answers);
    setRecommendedId(recommended);
    setScreenerDone(true);
    if (Object.keys(answers).length > 0) setShowRecommendation(true);
    else setScreen("home");
  }

  function recordSession(programId) {
    const updated = { ...progress, sessions: [...progress.sessions, { date: getTodayStr(), programId, ts: Date.now() }] };
    setProgress(updated); saveProgress(updated);
  }
  function startProgram(prog) {
    // Show the level selector first; the actual program (with level applied)
    // starts once a level is chosen.
    setPendingProgram(prog);
    setScreen("levelselect");
  }

  function startProgramAtLevel(levelId) {
    const prog = applyLevel(pendingProgram, levelId);
    const incomplete = loadIncompleteSession();
    if (incomplete && incomplete.programId === prog.id && (incomplete.exerciseIndex > 0 || incomplete.currentRep > 0)) {
      setSelectedProgram(prog);
      setIncompleteSession(incomplete);
      setShowResumePrompt(true);
    } else {
      clearIncompleteSession();
      setSelectedProgram(prog);
      setExerciseIndex(0);
      const hasCustom = customTimings[prog.id];
      setShowTimingsPrompt(!hasCustom);
      if (hasCustom) {
        setScreen("exercise");
        saveIncompleteSession(prog.id, 0, 0);
      }
    }
  }

  function beginExercise() {
    setShowTimingsPrompt(false);
    setScreen("exercise");
    saveIncompleteSession(selectedProgram.id, 0, 0);
  }
  function nextExercise() {
    const nextIndex = exerciseIndex + 1;
    // Record progress after each exercise completed
    const updatedProgress = {
      ...progress,
      sessions: [...progress.sessions, { 
        date: getTodayStr(), 
        programId: selectedProgram.id,
        exerciseIndex,
        ts: Date.now()
      }]
    };
    setProgress(updatedProgress);
    saveProgress(updatedProgress);

    if (nextIndex < selectedProgram.exercises.length) {
      // Save incomplete session so user can resume if they leave
      saveIncompleteSession(selectedProgram.id, nextIndex);
      setExerciseIndex(nextIndex);
    } else {
      // All exercises done — clear incomplete session
      clearIncompleteSession();
      setShowConfetti(true); 
      setTimeout(() => setShowConfetti(false), 3500); 
      setScreen("complete");
    }
  }

  const currentExercise = selectedProgram?.exercises[exerciseIndex];
  const streak = getStreakDays(progress.sessions);
  const totalDays = [...new Set(progress.sessions.map(s => s.date))].length;
  const todayDone = progress.sessions.some(s => s.date === getTodayStr());

  return (
    <div className={currentTheme.light ? "light-ui" : ""} style={{ "--bg": currentTheme.bg, "--accent": currentTheme.accent, "--orb1": currentTheme.orb1, "--orb2": currentTheme.orb2, minHeight: "100vh", background: customBg ? `linear-gradient(#000000bb, #000000bb), url(${customBg}) center/cover fixed` : currentTheme.bg, color: "#f5f0ff", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", maxWidth: 420, margin: "0 auto", position: "relative", overflow: "hidden" }}>
      <style>{globalCSS}</style>

      {showConfetti && <Confetti />}

      {/* EDUCATION MODULE — runs once after disclaimer */}
      {!showSplash && !showDisclaimer && !eduDone && (
        <EducationModule
          onComplete={() => { localStorage.setItem("coredoc-edu-done","1"); setEduDone(true); }}
          onSkip={() => { localStorage.setItem("coredoc-edu-done","1"); setEduDone(true); }}
        />
      )}

      {/* SYMPTOM SCREENER — first launch only */}
      {!showSplash && !screenerDone && !showDisclaimer && eduDone && (
        <SymptomScreener onComplete={handleScreenerComplete} />
      )}

      {/* RECOMMENDATION SCREEN */}
      {screenerDone && showRecommendation && (
        <RecommendationScreen
          answers={screenerAnswers}
          recommendedId={recommendedId}
          onStart={prog => { setShowRecommendation(false); startProgram(prog); }}
          onBrowse={() => { setShowRecommendation(false); setScreen("home"); }}
        />
      )}

      {/* AVATAR CUSTOMISER */}
      {showAvatarCustomiser && (
        <AvatarCustomiser
          avatar={avatar}
          pro={pro}
          onUpgrade={() => setShowBladderPaywall(true)}
          onSave={a => { setAvatar(a); setShowAvatarCustomiser(false); }}
          onClose={() => setShowAvatarCustomiser(false)}
        />
      )}

      {/* DISCLAIMER */}
      {/* SPLASH SCREEN */}
      {showSplash && (
        <SplashScreen onDone={() => setShowSplash(false)} />
      )}

      {/* GATEKEEPER */}
      {!showSplash && showDisclaimer && (
        <GatekeeperScreen onAgree={() => { try { primeSpeech(); } catch(e) {} setShowDisclaimer(false); }} />
      )}

      {/* MAIN APP — only shown after splash + education + screener */}
      {!showSplash && screenerDone && eduDone && !showRecommendation && <>

      {/* CLINICIAN TIMINGS PROMPT */}
      {showTimingsPrompt && selectedProgram && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: 24 }}>
          <div style={{ background: "linear-gradient(135deg, #271a4a, #1e1238)", borderRadius: 24, padding: 28, maxWidth: 380, width: "100%", border: "1px solid #c084fc33" }}>
            <div style={{ fontSize: 36, textAlign: "center", marginBottom: 12 }}>🩺</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#f5f0ff", textAlign: "center", marginBottom: 8 }}>Have clinician timings?</h3>
            <p style={{ fontSize: 14, color: "#9ca3af", textAlign: "center", lineHeight: 1.6, marginBottom: 24 }}>
              Have your physiotherapist or clinician recommended specific hold times or reps for your exercises? You can customise the timings to match their advice.
            </p>
            <button onClick={() => {
              if (!pro) { setShowTimingsPrompt(false); setShowBladderPaywall(true); }
              else { setShowTimingsPrompt(false); setShowTimingsEditor(true); }
            }} style={{
              width: "100%", background: "linear-gradient(135deg, #7c3aed, #c084fc)",
              color: "#fff", border: "none", borderRadius: 14, padding: "14px",
              fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 10,
              boxShadow: "0 6px 20px #7c3aed44"
            }}>{pro ? "✏️ Input my clinician's timings" : "🔓 Input clinician timings — Pro"}</button>
            <button onClick={beginExercise} style={{
              width: "100%", background: "transparent", border: "1px solid #3d2a5e",
              borderRadius: 14, padding: "14px", color: "#9ca3af",
              fontSize: 14, fontWeight: 600, cursor: "pointer"
            }}>Continue with standard timings</button>
          </div>
        </div>
      )}

      {/* TIMINGS EDITOR — Pro only */}
      {showTimingsEditor && selectedProgram && (
        <TimingsEditor
          program={selectedProgram}
          customTimings={customTimings[selectedProgram.id] || {}}
          onSave={(timings) => {
            const updated = { ...customTimings, [selectedProgram.id]: timings };
            setCustomTimings(updated);
            saveCustomTimings(updated);
            setShowTimingsEditor(false);
            beginExercise();
          }}
          onSkip={() => { setShowTimingsEditor(false); beginExercise(); }}
        />
      )}

      {/* RESUME SESSION PROMPT */}
      {showResumePrompt && selectedProgram && incompleteSession && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: 24 }}>
          <div style={{ background: "linear-gradient(135deg, #271a4a, #1e1238)", borderRadius: 24, padding: 28, maxWidth: 380, width: "100%", border: "1px solid #c084fc33", boxShadow: "0 8px 40px #7c3aed44" }}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 16 }}>👋</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#f5f0ff", textAlign: "center", marginBottom: 8 }}>Welcome back!</h3>
            <p style={{ fontSize: 14, color: "#9ca3af", textAlign: "center", lineHeight: 1.6, marginBottom: 24 }}>
              {incompleteSession.currentRep > 0
                ? <>You were on <strong style={{ color: "#f5f0ff" }}>rep {incompleteSession.currentRep}</strong> of {selectedProgram.exercises[incompleteSession.exerciseIndex]?.reps} in exercise {incompleteSession.exerciseIndex + 1} of {selectedProgram.exercises.length} today.</>
                : <>You completed <strong style={{ color: "#f5f0ff" }}>{incompleteSession.exerciseIndex} of {selectedProgram.exercises.length} exercises</strong> in your {selectedProgram.name} session today.</>
              } Would you like to continue where you left off?
            </p>
            <div style={{ background: `${selectedProgram.color}18`, border: `1px solid ${selectedProgram.color}33`, borderRadius: 12, padding: "12px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: selectedProgram.color, fontWeight: 700, marginBottom: 4 }}>Next up</div>
              <div style={{ fontSize: 15, color: "#f5f0ff", fontWeight: 700 }}>
                {selectedProgram.exercises[incompleteSession.exerciseIndex]?.name}
              </div>
            </div>
            <button onClick={() => {
              setExerciseIndex(incompleteSession.exerciseIndex);
              setShowResumePrompt(false);
              // Keep incompleteSession in state — the exercise timer reads
              // resumeRep from it to continue at the correct rep
              setScreen("exercise");
            }} style={{
              width: "100%", background: `linear-gradient(135deg, ${selectedProgram.glow}, ${selectedProgram.color})`,
              color: "#fff", border: "none", borderRadius: 14, padding: "14px",
              fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10,
              boxShadow: `0 6px 20px ${selectedProgram.color}44`
            }}>▶ Continue where I left off</button>
            <button onClick={() => {
              clearIncompleteSession();
              setExerciseIndex(0);
              setShowResumePrompt(false);
              setIncompleteSession(null);
              setScreen("exercise");
            }} style={{
              width: "100%", background: "transparent", border: "1px solid #3d2a5e",
              borderRadius: 14, padding: "14px", color: "#6b7280",
              fontSize: 14, fontWeight: 600, cursor: "pointer"
            }}>Start from beginning</button>
          </div>
        </div>
      )}

      {/* PROGRESS SCREEN */}
      {screen === "progress" && <ProgressScreen progress={progress} onBack={() => setScreen("home")} />}

      {/* BLADDER SCREEN */}
      {screen === "bladder" && (
        <TimedVoidingScreen
          onBack={() => setScreen("home")}
          pro={pro}
          onUpgrade={() => { setShowBladderPaywall(true); }}
        />
      )}

      {/* REMINDERS SCREEN */}
      {screen === "reminders" && (
        <RemindersScreen onBack={() => setScreen("home")} />
      )}

      {/* PERSONALISE SCREEN */}
      {screen === "personalise" && (
        <PersonaliseScreen
          onBack={() => setScreen("home")}
          pro={pro}
          onUpgrade={() => setShowBladderPaywall(true)}
          currentTheme={currentTheme}
          onThemeChange={t => setCurrentTheme(t)}
          currentMusic={currentMusic}
          onMusicChange={m => setCurrentMusic(m)}
          customBg={customBg}
          onCustomBg={url => setCustomBg(url)}
          voiceEnabled={voiceEnabled}
          onVoiceToggle={() => setVoiceEnabled(v => !v)}
          voiceStyle={voiceStyle}
          onVoiceStyleChange={s => setVoiceStyle(s)}
        />
      )}

      {/* First-time guided tour */}
      {!tourDone && screen === "home" && screenerDone && eduDone && !showSplash && !showDisclaimer && (
        <FirstTimeTour onDone={() => setTourDone(true)} />
      )}

      {/* Bladder paywall */}
      {showBladderPaywall && (
        <PaywallModal
          onPurchase={() => { unlockPro(); setPro(true); setShowBladderPaywall(false); }}
          onClose={() => setShowBladderPaywall(false)}
        />
      )}

      {/* HOME SCREEN */}
      {screen === "home" && (
        <div style={{ position: "relative", minHeight: "100vh", paddingBottom: 40 }}>
          <OrbBackground color1={currentTheme.orb1} color2={currentTheme.orb2} color3={currentTheme.orb3} />
          <div style={{ position: "relative", zIndex: 1 }}>
            {/* Avatar + greeting */}
            <div className="fade-up" style={{ padding: "40px 24px 16px", display: "flex", alignItems: "center", gap: 16 }}>
              <button onClick={() => setShowAvatarCustomiser(true)} style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, position: "relative" }}>
                <div style={{ background: "linear-gradient(135deg, #1a1030, #110820)", borderRadius: "50%", padding: 8, border: "2px solid #c084fc44", boxShadow: "0 0 20px #c084fc22", transition: "box-shadow 0.2s ease" }}>
                  <AvatarSVG avatar={avatar} size={60} />
                </div>
                <div style={{ position: "absolute", bottom: 2, right: 2, background: "#c084fc", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, border: "2px solid #15102a" }}>✏️</div>
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#c084fc18", border: "1px solid #c084fc33", borderRadius: 20, padding: "4px 12px", marginBottom: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#c084fc", boxShadow: "0 0 6px #c084fc" }} />
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>Hi <strong style={{ color: "#f5f0ff" }}>{avatar.name}</strong> 👋</span>
                </div>
                
              </div>
              <button onClick={() => setScreen("progress")} className="card-hover" style={{
                background: "linear-gradient(135deg, #1a1030, #110820)", border: "1px solid #3d2a5e",
                borderRadius: 14, padding: "10px 12px", cursor: "pointer", textAlign: "center",
                boxShadow: "0 4px 16px #00000044", flexShrink: 0
              }}>
                <div style={{ fontSize: 18 }}>📊</div>
                <div style={{ fontSize: 9, color: "#9ca3af", marginTop: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Stats</div>
              </button>
              <button id="btn-learn" onClick={() => { localStorage.removeItem("coredoc-edu-done"); setEduDone(false); }} className="card-hover" style={{
                background: "linear-gradient(135deg, #1a1030, #110820)", border: "1px solid #3d2a5e",
                borderRadius: 14, padding: "10px 12px", cursor: "pointer", textAlign: "center",
                boxShadow: "0 4px 16px #00000044", flexShrink: 0
              }}>
                <div style={{ fontSize: 18 }}>📖</div>
                <div style={{ fontSize: 9, color: "#9ca3af", marginTop: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Learn</div>
              </button>
            </div>

            <div className="fade-up-delay-1" style={{ padding: "0 24px 20px" }}>
              <h1 style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.3, margin: 0 }}>
                CoreDoc<br />
                <span className="shimmer-text" style={{ fontSize: 20 }}>Pelvic Floor Exercises</span>
              </h1>
            </div>

            {/* Streak bar */}
            <div className="fade-up-delay-1" style={{ margin: "0 24px 24px" }}>
              <div style={{
                background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)",
                borderRadius: 18, padding: "16px 20px", border: "1px solid #3d2a5e55",
                display: "flex", gap: 0, alignItems: "center",
                boxShadow: "0 4px 24px #00000044"
              }}>
                {[
                  { val: streak, label: "day streak", icon: "🔥", color: "#f59e0b" },
                  null,
                  { val: totalDays, label: "total days", icon: "✦", color: "#c084fc" },
                  null,
                  { val: null, label: todayDone ? "Done today ✓" : "Not yet today", icon: null, color: todayDone ? "#c084fc" : "#6b7280" }
                ].map((item, i) => item === null
                  ? <div key={i} style={{ width: 1, height: 32, background: "#3d2a5e44", margin: "0 16px" }} />
                  : (
                    <div key={i} style={{ flex: 1, textAlign: "center" }}>
                      {item.icon && <span style={{ fontSize: 18, fontWeight: 900, color: item.color }}>{item.icon} {item.val}</span>}
                      {!item.icon && <span style={{ fontSize: 13, fontWeight: 700, color: item.color }}>{item.label}</span>}
                      {item.icon && <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>{item.label}</div>}
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Programme cards */}
            <div style={{ padding: "0 24px" }}>
              <div className="fade-up-delay-2" style={{ fontSize: 11, fontWeight: 700, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16 }}>Choose your programme</div>
              {PROGRAMS.map((prog, idx) => {
                const sessionsCount = progress.sessions.filter(s => s.programId === prog.id).length;
                return (
                  <button key={prog.id}
                    className={`card-hover fade-up-delay-${Math.min(idx + 2, 4)}`}
                    onMouseEnter={() => setHoveredCard(prog.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => startProgram(prog)}
                    style={{
                      width: "100%", display: "block", textAlign: "left", cursor: "pointer",
                      background: hoveredCard === prog.id
                        ? `linear-gradient(135deg, ${prog.glow}22, ${prog.color}15)`
                        : "linear-gradient(135deg, #1a1030ee, #110820ee)",
                      backdropFilter: "blur(12px)",
                      border: `1px solid ${hoveredCard === prog.id ? prog.color + "55" : prog.color + "22"}`,
                      borderRadius: 20, padding: "20px", marginBottom: 12,
                      boxShadow: hoveredCard === prog.id ? `0 8px 32px ${prog.color}22` : "0 4px 16px #00000033",
                      transition: "all 0.2s ease"
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: 17, fontWeight: 800, color: "#f5f0ff" }}>{prog.name}</span>
                      <span style={{ background: prog.color + "22", color: prog.color, borderRadius: 20, padding: "3px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em" }}>{prog.tag}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 12, lineHeight: 1.4 }}>{prog.description}</div>
                    <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: prog.color, fontWeight: 600 }}>✦ {prog.exercises.length} exercises</span>
                      <span style={{ fontSize: 12, color: "#4b5563" }}>3× daily</span>
                      {sessionsCount > 0 && (
                        <span style={{ fontSize: 11, color: "#c084fc", marginLeft: "auto", fontWeight: 700 }}>{sessionsCount} done</span>
                      )}
                    </div>
                    {/* Mini progress bar */}
                    {sessionsCount > 0 && (
                      <div style={{ marginTop: 10, height: 3, background: "#2a1a3e", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{
                          height: "100%", borderRadius: 2,
                          background: `linear-gradient(90deg, ${prog.glow}, ${prog.color})`,
                          width: `${Math.min(100, (sessionsCount / prog.sessions) * 100)}%`,
                          boxShadow: `0 0 6px ${prog.color}88`
                        }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Redo questionnaire */}
            <div style={{ padding: "8px 24px 100px" }}>
              <button onClick={() => { saveScreener(null); localStorage.removeItem(SCREENER_KEY); setScreenerDone(false); setShowRecommendation(false); }} style={{
                width: "100%", background: "transparent", border: "1px solid #3d2a5e44",
                borderRadius: 14, padding: "12px", color: "#6b7280", fontSize: 13,
                fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8
              }}>
                🔄 Redo questionnaire
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BOTTOM NAV BAR */}
      {(screen === "home" || screen === "bladder" || screen === "progress" || screen === "reminders" || screen === "personalise") && screenerDone && eduDone && (
        <div style={{
          position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
          width: "100%", maxWidth: 420, zIndex: 50,
          background: "linear-gradient(180deg, transparent, #15102a 30%)",
          paddingTop: 20
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${currentTheme.bg}f0, ${currentTheme.bg}f8)`,
            backdropFilter: "blur(20px)",
            borderTop: "1px solid #3d2a5e55",
            display: "flex", padding: "8px 0 20px"
          }}>
            {[
              { id: "home", icon: "🏠", label: "Exercises" },
              { id: "bladder", icon: "💧", label: "Bladder" },
              { id: "reminders", icon: "🔔", label: "Reminders" },
              { id: "personalise", icon: "🎨", label: "Style" },
              { id: "progress", icon: "📊", label: "Progress" },
            ].map(tab => (
              <button key={tab.id} id={`nav-${tab.id}`} onClick={() => setScreen(tab.id)} style={{
                flex: 1, background: "none", border: "none", cursor: "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "6px 0"
              }}>
                <div style={{ fontSize: 18 }}>{tab.icon}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: screen === tab.id ? currentTheme.accent : "#4b5563", letterSpacing: "0.04em" }}>{tab.label}</div>
                {screen === tab.id && <div style={{ width: 4, height: 4, borderRadius: "50%", background: currentTheme.accent, boxShadow: `0 0 6px ${currentTheme.accent}` }} />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LEVEL SELECT SCREEN */}
      {screen === "levelselect" && pendingProgram && (
        <LevelSelector
          program={pendingProgram}
          pro={pro}
          onPick={(levelId) => startProgramAtLevel(levelId)}
          onBack={() => { setPendingProgram(null); setScreen("home"); }}
          onUpgrade={() => setShowBladderPaywall(true)}
        />
      )}

      {/* EXERCISE SCREEN */}
      {screen === "exercise" && selectedProgram && currentExercise && (
        <div style={{ position: "relative", minHeight: "100vh", padding: "32px 24px 40px" }}>
          <OrbBackground color1={selectedProgram.glow} color2={selectedProgram.color} color3="#1a1030" />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button onClick={() => setScreen("home")} className="fade-up" style={{
              background: "none", border: "none", color: "#9ca3af", fontSize: 13,
              cursor: "pointer", padding: 0, marginBottom: 24, display: "flex", alignItems: "center", gap: 6
            }}>← {selectedProgram.name}</button>
            <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              {pro && (
                <button onClick={() => setShowTimingsEditor(true)} style={{
                  background: "#c084fc18", border: "1px solid #c084fc44",
                  borderRadius: 20, padding: "6px 12px", cursor: "pointer",
                  fontSize: 11, fontWeight: 700, color: "#c084fc"
                }}>🩺 Edit timings</button>
              )}
              <button onClick={() => setVoiceEnabled(v => !v)} style={{
                background: voiceEnabled ? "#c084fc22" : "#ffffff11",
                border: `1px solid ${voiceEnabled ? "#c084fc55" : "#ffffff22"}`,
                borderRadius: 20, padding: "6px 12px", cursor: "pointer",
                fontSize: 12, fontWeight: 700, color: voiceEnabled ? "#c084fc" : "#6b7280",
                transition: "all 0.2s ease"
              }}>{voiceEnabled ? "🗣️ Voice" : "🔇 Voice"}</button>
              <button onClick={() => setSoundEnabled(s => !s)} style={{
                background: soundEnabled ? "#c084fc22" : "#ffffff11",
                border: `1px solid ${soundEnabled ? "#c084fc55" : "#ffffff22"}`,
                borderRadius: 20, padding: "6px 12px", cursor: "pointer",
                fontSize: 12, fontWeight: 700, color: soundEnabled ? "#c084fc" : "#6b7280",
                transition: "all 0.2s ease"
              }}>{soundEnabled ? "🔊 Sound" : "🔇 Sound"}</button>
            </div>
            </div>

            <div className="fade-up-delay-1" style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: selectedProgram.color, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>
                Exercise {exerciseIndex + 1} of {selectedProgram.exercises.length}
              </div>
              <h2 style={{ fontSize: 24, fontWeight: 900, margin: "0 0 4px" }}>{currentExercise.name}</h2>
              <div style={{ display: "flex", gap: 12 }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>{currentExercise.sets} sets · {currentExercise.reps} reps</span>
                <span style={{ fontSize: 12, color: "#6b7280" }}>{currentExercise.holdSecs}s hold</span>
              </div>
            </div>

            <div className="fade-up-delay-2">
              <ExerciseTimer 
                key={`${selectedProgram.id}-${exerciseIndex}`} 
                exercise={{
                  ...currentExercise,
                  ...(customTimings[selectedProgram.id]?.[currentExercise.id] || {})
                }}
                color={selectedProgram.color} 
                onComplete={nextExercise} 
                soundEnabled={soundEnabled} 
                avatar={avatar} 
                voiceEnabled={voiceEnabled} 
                voiceStyle={voiceStyle} 
                pro={pro} 
                onNextExercise={nextExercise} 
                canSkip={exerciseIndex + 1 < selectedProgram.exercises.length}
                resumeRep={incompleteSession?.exerciseIndex === exerciseIndex ? incompleteSession.currentRep : undefined}
                onRepComplete={(rep) => saveIncompleteSession(selectedProgram.id, exerciseIndex, rep)}
                onUpgrade={() => setShowBladderPaywall(true)}
              />
            </div>

            <div className="fade-up-delay-3" style={{ marginTop: 28, background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 16, padding: "18px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: selectedProgram.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>How to do this</div>
              <p style={{ fontSize: 13, color: "#c4b5d4", margin: "0 0 14px", lineHeight: 1.7 }}>{currentExercise.instruction}</p>
              <div style={{ borderTop: "1px solid #3d2a5e44", paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#9ca3af", flex: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#4b5563" }}>TIP · </span>
                  {currentExercise.tip}
                </span>
              </div>
              {voiceEnabled && (
                <button onClick={() => { primeSpeech(); speak("Voice is working. Squeeze and lift, hold, and release.", voiceStyle); }} style={{
                  marginTop: 12, width: "100%", background: "#c084fc18", border: "1px solid #c084fc44",
                  borderRadius: 10, padding: "8px", color: "#c084fc", fontSize: 12,
                  fontWeight: 600, cursor: "pointer"
                }}>🗣️ Test voice</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* COMPLETE SCREEN */}
      {screen === "complete" && selectedProgram && (
        <div style={{ position: "relative", minHeight: "100vh", padding: "60px 24px 40px", textAlign: "center" }}>
          <OrbBackground color1={selectedProgram.glow} color2="#f59e0b" color3={selectedProgram.color} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="pop-in" style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
              <div style={{ background: `linear-gradient(135deg, ${selectedProgram.glow}33, ${selectedProgram.color}22)`, borderRadius: "50%", padding: 16, border: `2px solid ${selectedProgram.color}44`, boxShadow: `0 0 40px ${selectedProgram.color}44` }}>
                <AvatarSVG avatar={avatar} size={90} />
              </div>
            </div>
            <h2 className="fade-up" style={{ fontSize: 28, fontWeight: 900, margin: "0 0 8px" }}>Well done, {avatar.name}!</h2>
            <p className="fade-up-delay-1" style={{ fontSize: 14, color: "#9ca3af", margin: "0 0 28px", lineHeight: 1.6 }}>
              You've completed <strong style={{ color: selectedProgram.color }}>{selectedProgram.name}</strong>.
            </p>

            <div className="fade-up-delay-2" style={{
              background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)",
              borderRadius: 20, padding: "24px", marginBottom: 16,
              border: "1px solid #f59e0b33", boxShadow: "0 8px 32px #f59e0b18"
            }}>
              <div style={{ fontSize: 44, fontWeight: 900, color: "#f59e0b", filter: "drop-shadow(0 0 12px #f59e0b88)" }}>
                {getStreakDays(progress.sessions)} 🔥
              </div>
              <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 6 }}>day streak — keep it going!</div>
            </div>

            <div className="fade-up-delay-3" style={{ background: "linear-gradient(135deg, #1a1030ee, #110820ee)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "20px", marginBottom: 16, textAlign: "left", border: `1px solid ${selectedProgram.color}22` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: selectedProgram.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Remember</div>
              {["Always fully release between contractions", "Don't hold your breath during exercises", "Stop if you feel pain or increased heaviness", "See a pelvic health physio if symptoms persist"].map((tip, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ color: selectedProgram.color, fontSize: 12, marginTop: 2 }}>✦</span>
                  <span style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.5 }}>{tip}</span>
                </div>
              ))}
            </div>

            {/* 3-month reminder for incontinence and prolapse */}
            {(selectedProgram.id === "incontinence" || selectedProgram.id === "prolapse") && (
              <div className="fade-up-delay-3" style={{
                background: selectedProgram.id === "incontinence" ? "#60a5fa11" : "#f472b611",
                border: `1px solid ${selectedProgram.id === "incontinence" ? "#60a5fa44" : "#f472b644"}`,
                borderRadius: 16, padding: "16px 18px", marginBottom: 16, textAlign: "left"
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: selectedProgram.color, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>⏱ Keep going for 3 months</div>
                <p style={{ fontSize: 13, color: "#c4b5d4", margin: 0, lineHeight: 1.6 }}>
                  {selectedProgram.id === "incontinence"
                    ? "Published guidance suggests consistent pelvic floor exercises over at least 3 months to build strength. You're building something — don't stop now."
                    : "Guidance suggests regular pelvic floor exercises over several months to build strength. Every session counts — keep going."
                  }
                </p>
              </div>
            )}

            {/* Daily dose reminder for prolapse */}
            {selectedProgram.id === "prolapse" && (
              <div className="fade-up-delay-3" style={{
                background: "#f472b608", border: "1px solid #f472b633",
                borderRadius: 16, padding: "16px 18px", marginBottom: 16, textAlign: "left"
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#f472b6", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>📋 Your daily target</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { label: "3 sessions per day", sub: "morning, afternoon, evening" },
                    { label: "~10 slow holds per session", sub: "plus a set of quick contractions" },
                    { label: "~40–48 contractions total", sub: "the minimum effective dose per POGP guidance" },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <span style={{ color: "#f472b6", fontSize: 12, marginTop: 2 }}>✦</span>
                      <div>
                        <span style={{ fontSize: 13, color: "#f5f0ff", fontWeight: 600 }}>{item.label}</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}> — {item.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: "#6b7280", margin: "10px 0 0", fontStyle: "italic" }}>
                  Quality over quantity — 30 good contractions beat 100 rushed ones.
                </p>
              </div>
            )}

            {/* Daily dose reminder for incontinence */}
            {selectedProgram.id === "incontinence" && (
              <div className="fade-up-delay-3" style={{
                background: "#60a5fa08", border: "1px solid #60a5fa33",
                borderRadius: 16, padding: "16px 18px", marginBottom: 16, textAlign: "left"
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>📋 Your daily target</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { label: "3 sessions per day", sub: "morning, afternoon, evening" },
                    { label: "8–10 slow holds × 10 sec", sub: "per session" },
                    { label: "8–10 quick flicks", sub: "per session — a key part of everyday fitness" },
                    { label: "30–40 contractions total/day", sub: "NICE NG123 minimum effective dose" },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <span style={{ color: "#60a5fa", fontSize: 12, marginTop: 2 }}>✦</span>
                      <div>
                        <span style={{ fontSize: 13, color: "#f5f0ff", fontWeight: 600 }}>{item.label}</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}> — {item.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: "1px solid #60a5fa22", marginTop: 12, paddingTop: 10 }}>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0, lineHeight: 1.5 }}>
                    <strong style={{ color: "#60a5fa" }}>The Knack</strong> — the single most important skill. Contract <em>before</em> every cough, sneeze, laugh, or lift. NICE recommends minimum 3 months, ideally 6 months for full effect.
                  </p>
                </div>
              </div>
            )}

            <div className="fade-up-delay-4" style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setScreen("progress")} className="btn-primary card-hover" style={{
                flex: 1, background: "linear-gradient(135deg, #1a1030, #110820)", color: "#c084fc",
                border: "1px solid #c084fc33", borderRadius: 14, padding: "14px",
                fontSize: 13, fontWeight: 700, cursor: "pointer"
              }}>📊 Progress</button>
              <button onClick={() => { setScreen("home"); setSelectedProgram(null); }} className="btn-primary" style={{
                flex: 2, background: `linear-gradient(135deg, ${selectedProgram.glow}, ${selectedProgram.color})`,
                color: "#fff", border: "none", borderRadius: 14, padding: "14px",
                fontSize: 14, fontWeight: 700, cursor: "pointer",
                boxShadow: `0 8px 24px ${selectedProgram.color}44`
              }}>Back to programmes</button>
            </div>
          </div>
        </div>
      )}

      </>} {/* end screenerDone */}
    </div>
  );
}
